/**
 * Fake backend for preview/demo mode.
 *
 * Activated when ?preview=true is in the URL (or sessionStorage has preview=true).
 * Intercepts window.fetch for all /api/* calls and returns realistic fake data.
 * Real fetch is used unchanged for everything else (assets, wasm, images, etc.).
 *
 * Usage: open the app with ?preview=true appended to any URL.
 */

const UID        = 'preview-user-001';
const UID2       = 'preview-user-002';
const UID3       = 'preview-user-003';
const BOT1       = 'preview-bot-001';
const BOT2       = 'preview-bot-002';
const BOT3       = 'preview-bot-003';
const TENANT_ID  = 'preview-tenant-001';
const MEM_ID     = 'preview-mem-001';
const MEM_ID2    = 'preview-mem-002';
const STORAGE1   = 'preview-storage-001';
const STORAGE2   = 'preview-storage-002';
const STORAGE3   = 'preview-storage-003';
const SELLBOT_STORAGE_ID = 'preview-sellchamber-001';
const CHEST1     = 'preview-chest-001';
const CHEST2     = 'preview-chest-002';
const CHEST3     = 'preview-chest-003';
const GRANT1     = 'preview-grant-001';
const GRANT2     = 'preview-grant-002';
const CMD1       = 'preview-cmd-001';
const CMD2       = 'preview-cmd-002';
const ITEM1      = 'preview-item-diamond';
const ITEM2      = 'preview-item-iron';
const ITEM3      = 'preview-item-gold';
const ITEM4      = 'preview-item-emerald';
const DC1        = 'preview-dc-001';
const DC2        = 'preview-dc-002';
const PROXY1     = 'preview-proxy-001';
const SESSION1   = 'preview-session-001';
const NOW        = new Date().toISOString();
const YESTERDAY  = new Date(Date.now() - 86400000).toISOString();

// The real mode is carried by a cookie the backend reads per request. Entering it reloads the page,
// so the preview stand-in has to survive a reload too, or the mode would appear to do nothing.
const MEMBER_VIEW_KEY = 'vektor.preview.memberView';

function readMemberView(): boolean {
  try { return sessionStorage.getItem(MEMBER_VIEW_KEY) === '1'; } catch { return false; }
}

function writeMemberView(on: boolean): void {
  try {
    if (on) sessionStorage.setItem(MEMBER_VIEW_KEY, '1');
    else sessionStorage.removeItem(MEMBER_VIEW_KEY);
  } catch { /* preview only, a storage failure just means the toggle does not stick */ }
}

const FAKE_USER = {
  username: 'PreviewUser',
  id: UID,
  language: null,
  loggedin: true,
  minecraftUsernameVerified: true,
  permissions: 255,
  minecraftUUID: '069a79f4-44e9-4726-a5be-fca90e38aaf5',
  versionNumber: 42,
  fifteenMinutesInAddonCount: 7,
};

const FAKE_USER2 = { username: 'BotOwner', id: UID2, language: null, loggedin: true, minecraftUsernameVerified: true, permissions: 3, versionNumber: 41 };
const FAKE_USER3 = { username: 'TenantMember', id: UID3, language: null, loggedin: true, minecraftUsernameVerified: true, permissions: 1, versionNumber: 40 };

const FAKE_TENANT = {
  id: TENANT_ID,
  name: 'Preview Tenant',
  slug: 'preview',
  isActive: true,
  settings: {
    id: 'preview-settings-001',
    tenantId: TENANT_ID,
    defaultProfitMargin: 15,
    description: 'Demo tenant for visual QA',
    maxMembers: 20,
    allowMemberInvites: true,
  },
};

const FAKE_TENANT_MEMBERSHIP = {
  id: MEM_ID,
  tenantId: TENANT_ID,
  userId: UID,
  role: 2, // Owner
  isActive: true,
  tenant: FAKE_TENANT,
  user: { username: 'PreviewUser', id: UID },
};

const FAKE_TENANT_MEMBERSHIP2 = {
  id: MEM_ID2,
  tenantId: TENANT_ID,
  userId: UID3,
  role: 0, // Member
  isActive: true,
  tenant: FAKE_TENANT,
  user: { username: 'TenantMember', id: UID3 },
};

const BOT_OPERATOR_ACTIONS = ['bot.read', 'bot.tasks.read', 'bot.tasks.run', 'bot.control',
  'bot.world.write', 'bot.config.read', 'bot.config.write', 'bot.possess.view'];

const BOT_OWNER_ACTIONS = [...BOT_OPERATOR_ACTIONS, 'bot.money.read', 'bot.money.write',
  'bot.msgs.read', 'bot.msgs.send', 'bot.possess.control', 'bot.audit.read',
  'bot.credentials.manage', 'bot.proxy.assign', 'bot.surveillance.read',
  'bot.surveillance.delete', 'bot.access.read', 'bot.access.grant', 'bot.delete'];

const FAKE_BOTS = [
  {
    id: BOT1,
    minecraftUsername: 'FarmerBot42',
    minecraftUuid: '069a79f4-44e9-4726-a5be-fca90e38aaf5',
    ownerUsername: 'PreviewUser',
    state: 'Playing',
    isOwner: true,
    myActions: BOT_OWNER_ACTIONS,
    userGrants: 1,
    tenantGrants: 1,
  },
  {
    id: BOT2,
    minecraftUsername: 'MinerBot7',
    minecraftUuid: '853c80ef-3c37-49fd-aa49-938b674adae6',
    ownerUsername: 'PreviewUser',
    isOwner: true,
    myActions: BOT_OWNER_ACTIONS,
    userGrants: 0,
    tenantGrants: 0,
  },
  {
    id: BOT3,
    minecraftUsername: 'CityMule',
    minecraftUuid: '61699b2e-d327-4a01-9f1e-0ea8c3f06bc6',
    ownerUsername: 'BotOwner',
    isOwner: false,
    myActions: BOT_OPERATOR_ACTIONS,
    userGrants: 2,
    tenantGrants: 0,
  },
];

const FAKE_GRANTED_BOTS = [
  { id: BOT1, username: 'FarmerBot42' },
  { id: BOT2, username: 'MinerBot7' },
];

const FAKE_MY_GRANTS = [
  { id: GRANT1, userId: UID, botId: BOT1, createdAt: YESTERDAY, level: 3 },
  { id: GRANT2, userId: UID, botId: BOT2, createdAt: YESTERDAY, level: 1 },
];

const VOTE_SITE_1 = 'minecraft-mp';
const VOTE_SITE_2 = 'planet-minecraft';
const VOTE_BOT_ID_1 = '00000000-0000-0000-0000-000000000101';
const VOTE_BOT_ID_2 = '00000000-0000-0000-0000-000000000102';
const VOTE_ADHOC_ID_1 = '00000000-0000-0000-0000-000000000201';
const VOTE_ADHOC_ID_2 = '00000000-0000-0000-0000-000000000202';

const minutesAgo = (n: number) => new Date(Date.now() - n * 60_000).toISOString();

// Covers every branch the citybuild status table can render: healthy with a good tick rate, healthy
// but degraded, bad, whitelisted, and a reading too old to still mean anything.
const FAKE_CITYBUILD_STATUSES = [
    { citybuild: 1, state: 1, rawStatusText: null, expiresAtUtc: null, updatedAtUtc: minutesAgo(2), lastUpdatedByBotId: null, serverTps: 18.4, tpsUpdatedAtUtc: minutesAgo(1) },
    { citybuild: 2, state: 1, rawStatusText: null, expiresAtUtc: null, updatedAtUtc: minutesAgo(3), lastUpdatedByBotId: null, serverTps: 12.6, tpsUpdatedAtUtc: minutesAgo(1) },
    { citybuild: 3, state: 1, rawStatusText: null, expiresAtUtc: null, updatedAtUtc: minutesAgo(4), lastUpdatedByBotId: null, serverTps: 7.9, tpsUpdatedAtUtc: minutesAgo(2) },
    { citybuild: 4, state: 2, rawStatusText: 'Der Server ist voll', expiresAtUtc: minutesAgo(-2), updatedAtUtc: minutesAgo(1), lastUpdatedByBotId: null, serverTps: null, tpsUpdatedAtUtc: null },
    { citybuild: 5, state: 3, rawStatusText: 'Whitelist aktiv', expiresAtUtc: minutesAgo(-1), updatedAtUtc: minutesAgo(6), lastUpdatedByBotId: null, serverTps: null, tpsUpdatedAtUtc: null },
    { citybuild: 6, state: 1, rawStatusText: null, expiresAtUtc: null, updatedAtUtc: minutesAgo(40), lastUpdatedByBotId: null, serverTps: 15.2, tpsUpdatedAtUtc: minutesAgo(40) },
    { citybuild: 7, state: 0, rawStatusText: null, expiresAtUtc: null, updatedAtUtc: minutesAgo(90), lastUpdatedByBotId: null, serverTps: null, tpsUpdatedAtUtc: null },
];

const FAKE_VOTE_SITES = [
  { id: 'preview-vote-site-001', key: VOTE_SITE_1, displayName: 'Minecraft-MP', isEnabled: true },
  { id: 'preview-vote-site-002', key: VOTE_SITE_2, displayName: 'PlanetMinecraft', isEnabled: true },
];

const FAKE_VOTE_SCHEDULER_STATUS = {
  enabled: true,
  windowStartHourLocal: 8,
  windowEndHourLocal: 22,
  lastTickStartUtc: NOW,
  lastTickFinishUtc: NOW,
  lastTickInWindow: true,
  currentBotId: null,
  currentSiteKey: null,
};

const FAKE_VOTE_DAILY = Array.from({ length: 7 }, (_, i) => {
  const d = new Date(Date.now() - (6 - i) * 86_400_000);
  const attempts = 4 + (i % 3);
  const failed = i === 2 ? 2 : (i % 4 === 0 ? 1 : 0);
  return { date: d.toISOString().slice(0, 10), attempts, success: attempts - failed, failed };
});

function fakeVoteRangeSummary(daily: typeof FAKE_VOTE_DAILY) {
  const attempts = daily.reduce((sum, d) => sum + d.attempts, 0);
  const success = daily.reduce((sum, d) => sum + d.success, 0);
  return { attempts, success, failed: attempts - success, successRatePercent: attempts === 0 ? 0 : Math.round((success / attempts) * 1000) / 10 };
}

const FAKE_VOTE_TODAY_STAT = { attempts: 2, success: 1, failed: 1, successRatePercent: 50 };

const FAKE_VOTE_BY_BOT = [
  { botRegistryId: VOTE_BOT_ID_1, minecraftUsername: 'FarmerBot42', ownerId: UID, ownerUsername: 'PreviewUser', attempts: 9, success: 8, failed: 1, successRatePercent: 88.9, lastVoteUtc: NOW },
  { botRegistryId: VOTE_BOT_ID_2, minecraftUsername: 'CityMule', ownerId: UID2, ownerUsername: 'BotOwner', attempts: 5, success: 3, failed: 2, successRatePercent: 60, lastVoteUtc: YESTERDAY },
];

const FAKE_VOTE_BY_ADHOC = [
  { targetId: VOTE_ADHOC_ID_1, minecraftUsername: 'Aktenkoffer_', ownerUserId: UID3, ownerUsername: 'TenantMember', isEnabled: true, attempts: 6, success: 6, failed: 0, successRatePercent: 100, lastVoteUtc: NOW },
  { targetId: VOTE_ADHOC_ID_2, minecraftUsername: 'RetiredVoter_', ownerUserId: UID2, ownerUsername: 'BotOwner', isEnabled: false, attempts: 3, success: 1, failed: 2, successRatePercent: 33.3, lastVoteUtc: YESTERDAY },
];

const FAKE_VOTE_BY_SITE = [
  { siteKey: VOTE_SITE_1, siteDisplayName: 'Minecraft-MP', attempts: 12, success: 10, failed: 2, successRatePercent: 83.3 },
  { siteKey: VOTE_SITE_2, siteDisplayName: 'PlanetMinecraft', attempts: 8, success: 6, failed: 2, successRatePercent: 75 },
];

const FAKE_VOTE_TODAY_ROWS = [
  { id: `${VOTE_BOT_ID_1.replace(/-/g, '')}|${VOTE_SITE_1}`, minecraftUsernameRedacted: 'FarmerBot42', siteKey: VOTE_SITE_1, status: 'Voted', outcome: 0, lastVoteUtc: NOW, redeemed: true, lastRedeemedUtc: NOW, proxyHealthy: true, isStale: false, isEnabled: true, isAdHoc: false, ownerUserId: UID, ownerUsername: 'PreviewUser' },
  { id: `${VOTE_BOT_ID_2.replace(/-/g, '')}|${VOTE_SITE_2}`, minecraftUsernameRedacted: 'CityMule', siteKey: VOTE_SITE_2, status: 'Failed', outcome: 3, lastVoteUtc: YESTERDAY, redeemed: false, lastRedeemedUtc: null, proxyHealthy: true, isStale: false, isEnabled: true, isAdHoc: false, ownerUserId: UID2, ownerUsername: 'BotOwner' },
  { id: `adhoc:${VOTE_ADHOC_ID_1.replace(/-/g, '')}|${VOTE_SITE_1}`, minecraftUsernameRedacted: 'Aktenkoffer_', siteKey: VOTE_SITE_1, status: 'Pending', outcome: null, lastVoteUtc: null, redeemed: false, lastRedeemedUtc: null, proxyHealthy: true, isStale: false, isEnabled: true, isAdHoc: true, ownerUserId: UID3, ownerUsername: 'TenantMember' },
];

const FAKE_VOTE_LOG = [
  { id: 'preview-vote-log-001', minecraftUsernameRedacted: 'F********2', siteKey: VOTE_SITE_1, startedUtc: NOW, finishedUtc: NOW, outcome: 0, captchaPath: 2, solverProvider: '2captcha', solverCostUsd: 0.0021, durationMs: 4200, errorClass: null, rawNotes: null },
  { id: 'preview-vote-log-002', minecraftUsernameRedacted: 'C******e', siteKey: VOTE_SITE_2, startedUtc: YESTERDAY, finishedUtc: YESTERDAY, outcome: 3, captchaPath: 3, solverProvider: '2captcha', solverCostUsd: 0.0018, durationMs: 8900, errorClass: 'CaptchaFailed', rawNotes: 'Solver returned invalid token' },
];

const FAKE_GRANTED_ACCOUNTS = [
  { id: GRANT1, userId: UID, botId: BOT1, createdAt: YESTERDAY, level: 3 },
];


const FAKE_COMMANDS: any[] = [
  { id: CMD1, ownerId: UID, name: 'Sell All', template: '/sell all', onlyPrefix: false, createdAt: YESTERDAY },
  { id: CMD2, ownerId: UID, name: 'Home', template: '/home', onlyPrefix: false, createdAt: YESTERDAY },
];

const FAKE_CHESTS: any[] = [
  { id: CHEST1, x: 100, y: 64, z: 200, x2: 101, y2: 64, z2: 200, type: 1, typeName: 'Double', filterMode: 0, filterModeName: 'Disabled', filterItems: [], lastScannedAt: NOW },
  { id: CHEST2, x: 110, y: 64, z: 200, type: 0, typeName: 'Single', filterMode: 1, filterModeName: 'Whitelist', filterItems: [{ itemId: 264, metadata: 0 }], lastScannedAt: YESTERDAY },
  { id: CHEST3, x: 120, y: 64, z: 200, type: 2, typeName: 'Infinite', filterMode: 0, filterModeName: 'Disabled', filterItems: [], lastScannedAt: null , suctionEnabled: true, suctionCheckedAt: NOW },
];

// A real sell chamber runs a few hundred chests. The Truhen table pages, sorts and bulk-edits server
// side, so preview needs that order of magnitude to exercise any of it.
const CHAMBER_ROWS = 12;
const CHAMBER_COLUMNS = 28;
const FILTER_ITEM_IDS = [264, 265, 266, 388, 296];

for (let row = 0; row < CHAMBER_ROWS; row++) {
  for (let column = 0; column < CHAMBER_COLUMNS; column++) {
    const index = row * CHAMBER_COLUMNS + column;
    const isDouble = index % 5 === 0;
    const isInfinite = index % 23 === 0;
    const type = isInfinite ? 2 : isDouble ? 1 : 0;
    const filterMode = isInfinite ? 1 : index % 4 === 0 ? 1 : index % 7 === 0 ? 2 : 0;
    const chestX = 140 + column * 2;
    const chestZ = 200 + row * 3;
    FAKE_CHESTS.push({
      id: `preview-chamber-${index.toString().padStart(3, '0')}`,
      x: chestX,
      y: 64,
      z: chestZ,
      x2: type === 1 ? chestX + 1 : null,
      y2: type === 1 ? 64 : null,
      z2: type === 1 ? chestZ : null,
      type,
      typeName: type === 2 ? 'InfiniteStorage' : type === 1 ? 'DoubleChest' : 'SingleChest',
      filterMode,
      filterModeName: filterMode === 2 ? 'Blacklist' : filterMode === 1 ? 'Whitelist' : 'Disabled',
      filterItems: filterMode === 0 ? [] : isInfinite
        ? [{ itemId: FILTER_ITEM_IDS[index % FILTER_ITEM_IDS.length], metadata: 0 }]
        : [{ itemId: FILTER_ITEM_IDS[index % FILTER_ITEM_IDS.length], metadata: 0 }, { itemId: 1, metadata: 0 }],
      lastScannedAt: index % 6 === 0 ? null : index % 2 === 0 ? NOW : YESTERDAY,
      // All three suction states, so every Truhen badge is reachable in preview.
      suctionEnabled: isInfinite && index % 46 === 0,
      suctionCheckedAt: isInfinite && index % 69 !== 0 ? NOW : null,
    });
  }
}

function previewChestsFor(storageId: string): any[] {
  if (storageId === STORAGE2) return FAKE_CHESTS.filter(c => c.id === CHEST2);
  if (storageId === STORAGE3 || storageId === SELLBOT_STORAGE_ID) return [];
  return FAKE_CHESTS;
}

function previewChestFilterFromQuery(query: URLSearchParams) {
  const type = query.get('type');
  const filterMode = query.get('filterMode');
  return {
    search: query.get('search') ?? undefined,
    type: type == null ? undefined : parseInt(type),
    filterMode: filterMode == null ? undefined : parseInt(filterMode),
    neverScanned: query.get('neverScanned') === 'true',
  };
}

// Mirrors BuildChestQuery in InventoryController so preview paging and bulk selection behave like
// the real backend rather than agreeing with themselves.
function previewMatchingChests(storageId: string, selection: { chestIds?: string[] | null; match?: any }): any[] {
  let chests = previewChestsFor(storageId);
  const match = selection.match;
  if (match) {
    if (match.type != null) chests = chests.filter(c => c.type === match.type);
    if (match.filterMode != null) chests = chests.filter(c => c.filterMode === match.filterMode);
    if (match.neverScanned) chests = chests.filter(c => !c.lastScannedAt);
    const search = (match.search ?? '').trim();
    if (search) {
      for (const term of search.split(/[\s,]+/).filter(Boolean)) {
        chests = chests.filter(c => `${c.x}`.includes(term) || `${c.y}`.includes(term) || `${c.z}`.includes(term));
      }
    }
  }
  if (selection.chestIds && selection.chestIds.length > 0) {
    const ids = new Set(selection.chestIds);
    chests = chests.filter(c => ids.has(c.id));
  }
  return chests;
}

function previewSortChests(chests: any[], sort: string | null, desc: boolean): any[] {
  const direction = desc ? -1 : 1;
  const sorted = [...chests];
  if (sort === 'type') sorted.sort((a, b) => (a.type - b.type || a.x - b.x) * direction);
  else if (sort === 'scanned') {
    sorted.sort((a, b) => ((Date.parse(a.lastScannedAt ?? '') || 0) - (Date.parse(b.lastScannedAt ?? '') || 0)) * direction);
  } else sorted.sort((a, b) => ((a.x - b.x) || (a.y - b.y) || (a.z - b.z)) * direction);
  return sorted;
}

function previewSyncChestCounts() {
  for (const storage of FAKE_STORAGES) {
    const chests = previewChestsFor(storage.id);
    storage.chestCount = chests.length;
    storage.scannedChestCount = chests.filter(c => c.lastScannedAt).length;
  }
}

const FAKE_STORAGES = [
  {
    id: STORAGE1,
    name: 'Main Storage',
    location: '/p h Hauptlager',
    citybuild: 1,
    citybuildName: 'CB2',
    chestCount: FAKE_CHESTS.length,
    scannedChestCount: FAKE_CHESTS.filter(c => c.lastScannedAt).length,
    createdAt: YESTERDAY,
    ownerId: UID,
    ownerName: 'PreviewUser',
    isOwner: true,
    reachThroughChests: false,
    ignoreLoreCodes: true,
    sellChamberBotId: null,
    sellChamberBotName: '',
    myActions: ['storage.read', 'storage.items.write', 'storage.chests.write', 'storage.config.write',
      'storage.tasks.run', 'storage.bots.assign', 'storage.access.read', 'storage.access.grant',
      'storage.audit.read', 'storage.delete'],
  },
  {
    id: STORAGE2,
    name: 'Mining Cache',
    location: '/warp mine',
    citybuild: 22,
    citybuildName: 'Nature',
    chestCount: 1,
    scannedChestCount: 1,
    createdAt: YESTERDAY,
    ownerId: UID,
    ownerName: 'PreviewUser',
    isOwner: true,
    reachThroughChests: false,
    ignoreLoreCodes: true,
    sellChamberBotId: null,
    sellChamberBotName: '',
    myActions: ['storage.read', 'storage.items.write', 'storage.chests.write', 'storage.config.write',
      'storage.tasks.run', 'storage.bots.assign', 'storage.access.read', 'storage.access.grant',
      'storage.audit.read', 'storage.delete'],
  },
  {
    id: STORAGE3,
    name: 'Clan Bank',
    location: '',
    citybuild: 25,
    citybuildName: 'Wasser',
    chestCount: 0,
    scannedChestCount: 0,
    createdAt: YESTERDAY,
    ownerId: UID3,
    ownerName: 'TenantMember',
    isOwner: false,
    sellChamberBotId: null,
    reachThroughChests: false,
    ignoreLoreCodes: true,
    myActions: ['storage.read'],
  },
  {
    id: SELLBOT_STORAGE_ID,
    name: 'FarmerBot42 Botkammer',
    location: '/p h botkammer',
    citybuild: 1,
    citybuildName: 'CB2',
    chestCount: 0,
    scannedChestCount: 0,
    createdAt: YESTERDAY,
    ownerId: UID,
    ownerName: 'PreviewUser',
    isOwner: true,
    sellChamberBotId: BOT1,
    reachThroughChests: false,
    ignoreLoreCodes: true,
    myActions: ['storage.read', 'storage.items.write', 'storage.chests.write', 'storage.config.write',
      'storage.tasks.run', 'storage.bots.assign', 'storage.access.read', 'storage.access.grant',
      'storage.audit.read', 'storage.delete'],
  },
];

const ITEM_QUANTITIES: Record<string, number> = {
  '264:0': 512,   // diamond
  '265:0': 4096,  // iron_ingot
  '266:0': 256,   // gold_ingot
  '388:0': 128,   // emerald
  '296:0': 8192,  // wheat
  '5:0':   2048,  // wood
  '1:0':   16384, // stone
  '49:0':  320,   // obsidian
  // Potion and spawn egg draw as tinted layers rather than a plain sprite, so the grid needs one of each.
  '373:0': 5,
  '383:0': 2,
  // items.json has no variations for the potion, so a brewed one (water breathing, extended) is the
  // stocked item the vanilla catalog cannot name.
  '373:8269': 12,
  '373:0:cmdpotion': 3,
  '89:0':  64,    // glowstone
  '1:0:cmpstone2': 96,  // Stein II
  '1:0:cmpstone3': 12,  // Stein III
  '278:0:enchpick': 1,
};

const FAKE_RESERVATIONS: Record<string, number> = {
  '265:0': 1536,
  '296:0': 8192,
};

const FAKE_ALL_ITEMS = {
  // Paper is stocked in another storage only, which is the case the panel's "elsewhere" marker covers.
  quantities: { ...ITEM_QUANTITIES, '339:0': 2048 },
  infiniteItems: ['1:0', '89:0'],
};

const FAKE_STORAGE_ITEMS = {
  quantities: ITEM_QUANTITIES,
  infiniteItems: FAKE_ALL_ITEMS.infiniteItems,
};

type FakeStockPolicy = { minQuantity: number; targetQuantity: number; sourceStorageId: string | null; status: number };
type FakeStockRow = { itemKey: string; itemId: number; metadata: number; snbt: string | null; onHand: number; openSupply: number; policy: FakeStockPolicy | null };

const stockRow = (itemKey: string, onHand: number, policy: FakeStockPolicy | null, openSupply = 0): FakeStockRow => {
  const [itemId, metadata] = itemKey.split(':').map(Number);
  return { itemKey, itemId, metadata, snbt: null, onHand, openSupply, policy };
};

const FAKE_STOCK_BY_STORAGE: Record<string, FakeStockRow[]> = {};

// Stone carries the Stein II and Stein III stacks of ITEM_QUANTITIES folded in, 81 and 729 raw each.
// The Clan Bank has no chests, so it shows the empty state.
function previewStockRows(storageId: string): FakeStockRow[] {
  if (storageId === STORAGE3) return [];
  return FAKE_STOCK_BY_STORAGE[storageId] ??= [
    stockRow('266:0', 0, { minQuantity: 64, targetQuantity: 576, sourceStorageId: null, status: 2 }, 192),
    stockRow('27:0', 0, { minQuantity: 32, targetQuantity: 128, sourceStorageId: STORAGE2, status: 2 }),
    stockRow('265:0', 4096, { minQuantity: 6912, targetQuantity: 13824, sourceStorageId: STORAGE2, status: 1 }, 1536),
    stockRow('50:0', 40, { minQuantity: 64, targetQuantity: 256, sourceStorageId: null, status: 1 }),
    stockRow('1:0', 16384 + 96 * 81 + 12 * 729, { minQuantity: 13824, targetQuantity: 41472, sourceStorageId: null, status: 0 }),
    stockRow('264:0', 512, { minQuantity: 128, targetQuantity: 640, sourceStorageId: null, status: 0 }),
    stockRow('296:0', 8192, null),
    stockRow('5:0', 2048, null),
    stockRow('388:0', 128, null),
    stockRow('49:0', 320, null),
  ];
}

// Stands in for the backend's evaluation so a preview save shows the status the real PUT returns.
function previewSaveStock(storageId: string, body: { upserts?: { itemKey: string; minQuantity: number; targetQuantity: number; sourceStorageId?: string | null }[]; removedItemKeys?: string[] } | null): Response {
  const rows = previewStockRows(storageId);
  const upserts = body?.upserts ?? [];
  for (let upsertIndex = 0; upsertIndex < upserts.length; upsertIndex++) {
    const upsert = upserts[upsertIndex];
    if (upsert.targetQuantity < upsert.minQuantity)
      return apiError(`Upserts[${upsertIndex}]`, `${upsert.itemKey}: the target (${upsert.targetQuantity}) is below the minimum (${upsert.minQuantity}).`);
  }
  for (const upsert of upserts) {
    const row = rows.find(candidate => candidate.itemKey === upsert.itemKey);
    if (!row) continue;
    const status = row.onHand <= 0 ? 2 : row.onHand < upsert.minQuantity ? 1 : 0;
    row.policy = { minQuantity: upsert.minQuantity, targetQuantity: upsert.targetQuantity, sourceStorageId: upsert.sourceStorageId ?? null, status };
  }
  for (const itemKey of body?.removedItemKeys ?? []) {
    const row = rows.find(candidate => candidate.itemKey === itemKey);
    if (row) row.policy = null;
  }
  return apiOk({ rows });
}

type FakeReplenishRecipe = {
  displayName: string;
  resultCount: number;
  ingredients: { itemKey: string; displayName: string; perCraft: number; available: number }[];
};

// Powered rails need more gold than the Mining Cache holds, so that card shows a blocked line beside a craftable one.
const FAKE_REPLENISH_RECIPES: Record<string, FakeReplenishRecipe> = {
  '266:0': { displayName: 'Gold Ingot', resultCount: 9, ingredients: [{ itemKey: '41:0', displayName: 'Block of Gold', perCraft: 1, available: 512 }] },
  '50:0': {
    displayName: 'Torch', resultCount: 4, ingredients: [
      { itemKey: '263:0', displayName: 'Coal', perCraft: 1, available: 4096 },
      { itemKey: '280:0', displayName: 'Stick', perCraft: 1, available: 2048 },
    ],
  },
  '265:0': { displayName: 'Iron Ingot', resultCount: 9, ingredients: [{ itemKey: '42:0', displayName: 'Block of Iron', perCraft: 1, available: 2048 }] },
  '27:0': {
    displayName: 'Powered Rail', resultCount: 6, ingredients: [
      { itemKey: '266:0', displayName: 'Gold Ingot', perCraft: 6, available: 40 },
      { itemKey: '280:0', displayName: 'Stick', perCraft: 1, available: 2048 },
      { itemKey: '331:0', displayName: 'Redstone', perCraft: 1, available: 512 },
    ],
  },
};

function previewReplenishLine(itemKey: string, quantity: number, lineIndex: number) {
  const recipe = FAKE_REPLENISH_RECIPES[itemKey];
  if (!recipe)
    return { lineIndex, itemKey, displayName: itemKey, quantity, steps: [], shortfalls: [], totalConsumed: [], estimatedSeconds: 0, planError: 'No recipe found for this item.', planFailure: 1 };
  const crafts = Math.ceil(quantity / recipe.resultCount);
  const consumed = recipe.ingredients.map(ingredient => ({ itemKey: ingredient.itemKey, displayName: ingredient.displayName, quantity: ingredient.perCraft * crafts }));
  const estimatedSeconds = 20 + crafts * 0.4;
  return {
    lineIndex, itemKey, displayName: recipe.displayName, quantity,
    steps: [{
      itemKey, displayName: recipe.displayName, times: crafts, resultCount: recipe.resultCount,
      producedQuantity: crafts * recipe.resultCount, disposition: 'Final', estimatedSeconds, ingredients: consumed,
    }],
    shortfalls: recipe.ingredients
      .filter(ingredient => ingredient.perCraft * crafts > ingredient.available)
      .map(ingredient => ({ itemKey: ingredient.itemKey, displayName: ingredient.displayName, needed: ingredient.perCraft * crafts, available: ingredient.available })),
    totalConsumed: consumed,
    estimatedSeconds,
    planError: null,
  };
}

// Stands in for the backend's sizing and per-source grouping, so a preview submit raises open supply and the line leaves.
function previewPlannedOrders(storageId: string) {
  const linesBySource = new Map<string, { itemKey: string; quantity: number }[]>();
  for (const row of previewStockRows(storageId)) {
    if (!row.policy || row.policy.status === 0) continue;
    const quantity = row.policy.targetQuantity - row.onHand - row.openSupply;
    if (quantity <= 0) continue;
    const sourceStorageId = row.policy.sourceStorageId ?? storageId;
    linesBySource.set(sourceStorageId, [...(linesBySource.get(sourceStorageId) ?? []), { itemKey: row.itemKey, quantity }]);
  }
  const orders = [...linesBySource.entries()].map(([sourceStorageId, requestedLines]) => {
    const lines = requestedLines.map((line, lineIndex) => previewReplenishLine(line.itemKey, line.quantity, lineIndex));
    const craftableLines = lines.filter(line => !line.planError && line.shortfalls.length === 0);
    return {
      sourceStorageId,
      sourceStorageName: FAKE_STORAGES.find(storage => storage.id === sourceStorageId)?.name ?? null,
      plan: {
        lines,
        totalConsumed: craftableLines.flatMap(line => line.totalConsumed),
        estimatedSeconds: craftableLines.reduce((total, line) => total + line.estimatedSeconds, 0),
        eligibleBotIds: FAKE_BOTS.slice(0, 2).map((b: any) => b.id),
        botError: null,
      },
      sourceError: null,
    };
  });
  return { orders: orders.sort((a, b) => Number(b.sourceStorageId === storageId) - Number(a.sourceStorageId === storageId)) };
}

// Main Storage shows both rows a shared line produces plus an uncraftable line; every other storage previews the empty state.
function previewExternalDemand(storageId: string) {
  if (storageId !== STORAGE1) return { groups: [] };
  const piston = { itemKey: '33:0', displayName: 'Piston' };
  return {
    groups: [
      {
        sourceStorageId: STORAGE1,
        sourceStorageName: 'Main Storage',
        sourceError: null,
        demand: [
          { itemKey: '4:0', displayName: 'Cobblestone', needed: 8398848, available: 3932160, missing: 4466688, neededFor: [piston, { itemKey: '61:0', displayName: 'Furnace' }, { itemKey: '67:0', displayName: 'Stone Stairs' }, { itemKey: '139:0', displayName: 'Cobblestone Wall' }, { itemKey: '23:0', displayName: 'Dispenser' }, { itemKey: '158:0', displayName: 'Dropper' }, { itemKey: '29:0', displayName: 'Sticky Piston' }] },
          { itemKey: '42:0', displayName: 'Block of Iron', needed: 40, available: 12, missing: 28, neededFor: [piston, { itemKey: '154:0', displayName: 'Hopper' }] },
        ],
        uncraftable: [
          { itemKey: '138:0', displayName: 'Beacon', quantity: 2, error: "A single craft of Beacon and what it is made from does not fit the bot's inventory." },
        ],
      },
      {
        sourceStorageId: STORAGE2,
        sourceStorageName: 'Mining Cache',
        sourceError: null,
        demand: [
          { itemKey: '264:0', displayName: 'Diamond', needed: 24, available: 0, missing: 24, neededFor: [{ itemKey: '264:0', displayName: 'Diamond' }] },
        ],
        uncraftable: [],
      },
    ],
  };
}

function previewRecordOpenSupply(resultStorageId: string, lines: { itemKey: string; quantity: number }[]) {
  const rows = FAKE_STOCK_BY_STORAGE[resultStorageId];
  if (!rows) return;
  for (const line of lines) {
    const row = rows.find(candidate => candidate.itemKey === line.itemKey);
    if (row) row.openSupply += line.quantity;
  }
}

const FAKE_PRICING: Record<string, { buy?: number; sell?: number }> = {
  '264:0': { buy: 1800, sell: 2100 },
  '265:0': { buy: 55,   sell: 70   },
  '266:0': { buy: 400,  sell: 500  },
  '388:0': { buy: 150,  sell: 200  },
  '296:0': { buy: 3,    sell: 5    },
  '5:0':   { buy: 8,    sell: 12   },
  '1:0':   { buy: 1,    sell: 2    },
};

const FAKE_ITEM_ENTRIES = [
  { id: ITEM1, minecraftId: 'minecraft:diamond',    singlePrice: 2100, quaterStackPrice: 128000, halfStackPrice: 250000, stackPrice: 500000, threeStacksPrice: 1450000, nineStacksPrice: 4300000, doubleChestPrice: 8500000, purchasePriceSingle: 1800 },
  { id: ITEM2, minecraftId: 'minecraft:iron_ingot', singlePrice: 70,   quaterStackPrice: 3800,  halfStackPrice: 7200,   stackPrice: 13500,  threeStacksPrice: 40000,   nineStacksPrice: 120000,  doubleChestPrice: 230000,  purchasePriceSingle: 55   },
  { id: ITEM3, minecraftId: 'minecraft:gold_ingot', singlePrice: 500,  quaterStackPrice: 29000, halfStackPrice: 57000,  stackPrice: 112000, threeStacksPrice: 330000,  nineStacksPrice: 990000,  doubleChestPrice: 1950000, purchasePriceSingle: 400  },
  { id: ITEM4, minecraftId: 'minecraft:emerald',    singlePrice: 200,  quaterStackPrice: 11500, halfStackPrice: 22500,  stackPrice: 44000,  threeStacksPrice: 130000,  nineStacksPrice: 390000,  doubleChestPrice: 760000,  purchasePriceSingle: 150  },
];

const FAKE_DROP_CONFIGS = [
  {
    id: DC1,
    name: 'Farm Loot',
    description: 'Standard farming drop table',
    location: 'FarmDistrict',
    type: 0,
    citybuild: 1,
    itemsToDrop: [{ itemId: 296, metadata: 0, chance: 0.8, minCount: 1, maxCount: 4 }],
    everythingExceptRepairable: false,
    createdAt: YESTERDAY,
    updatedAt: NOW,
    versionNumber: 3,
  },
  {
    id: DC2,
    name: 'Mine Loot',
    description: 'Ore and stone drops',
    location: 'MineShaft3',
    type: 1,
    citybuild: 2,
    itemsToDrop: [
      { itemId: 264, metadata: 0, chance: 0.05, minCount: 1, maxCount: 1 },
      { itemId: 265, metadata: 0, chance: 0.6,  minCount: 1, maxCount: 4 },
    ],
    everythingExceptRepairable: true,
    createdAt: YESTERDAY,
    updatedAt: NOW,
    versionNumber: 7,
  },
];

const FAKE_JOB_ENTRIES = [
  { createdAt: YESTERDAY, sequenceNumber: 1, itemIdentifier: 'minecraft:wheat', metadata: 0, isCustomBlock: false, name: 'Wheat', pricesToStacks: { 5: 160, 16: 480, 64: 1800 } },
  { createdAt: YESTERDAY, sequenceNumber: 2, itemIdentifier: 'minecraft:iron_ingot', metadata: 0, isCustomBlock: false, name: 'Iron Ingot', pricesToStacks: { 16: 880, 64: 3200 } },
  { createdAt: YESTERDAY, sequenceNumber: 3, itemIdentifier: 'minecraft:diamond', metadata: 0, isCustomBlock: false, name: 'Diamond', pricesToStacks: { 16: 30000, 64: 115000 } },
  { createdAt: YESTERDAY, sequenceNumber: 4, itemIdentifier: 'minecraft:wood', metadata: 0, isCustomBlock: false, name: 'Wood', pricesToStacks: { 64: 600, 192: 1700 } },
  { createdAt: YESTERDAY, sequenceNumber: 5, itemIdentifier: 'minecraft:stone', metadata: 0, isCustomBlock: false, name: 'Stone', pricesToStacks: { 64: 100, 192: 280 } },
];

const FAKE_BOT_STATUS = {
  running: true,
  state: 'Playing',
  username: 'FarmerBot42',
  server: 'play.example-citybuild.de',
  connectedSince: YESTERDAY,
  ping: 42,
  packetsInPerSecond: 18,
  packetsOutPerSecond: 6,
  chunksLoaded: 441,
  entitiesTracked: 12,
};

const FAKE_SESSION_INFO = {
  linked: true,
  minecraftUsername: 'FarmerBot42',
  minecraftUuid: '069a79f4-44e9-4726-a5be-fca90e38aaf5',
  tokenExpiry: new Date(Date.now() + 2 * 3600000).toISOString(),
  lastRefreshed: NOW,
  keepSessionAlive: true,
  isExpired: false,
};

const FAKE_PLAYER_STATE = {
  running: true,
  state: {
    health: 20,
    maxHealth: 20,
    food: 18,
    experience: 0.62,
    level: 7,
    position: { x: 104, y: 64, z: 218 },
    gameMode: 'SURVIVAL',
    server: 'play.example-citybuild.de',
  },
};

const FAKE_INVENTORY_STATE = {
  running: true,
  activeHotbar: 0,
  slots: [
    { slotIndex: 0,  itemId: 276, metadata: 0, damage: 0.1, count: 1 }, // diamond sword
    { slotIndex: 1,  itemId: 278, metadata: 0, damage: 0.0, count: 1 }, // diamond pickaxe
    { slotIndex: 2,  itemId: 264, metadata: 0, damage: 0,   count: 32 },// diamond
    { slotIndex: 3,  itemId: 265, metadata: 0, damage: 0,   count: 64 },// iron ingot
    { slotIndex: 4,  itemId: -1,  metadata: 0, damage: 0,   count: 0  },
    { slotIndex: 5,  itemId: 296, metadata: 0, damage: 0,   count: 64 },// wheat
    { slotIndex: 36, itemId: 310, metadata: 0, damage: 0.2, count: 1  },// diamond helmet
    { slotIndex: 37, itemId: 311, metadata: 0, damage: 0.1, count: 1  },// diamond chest
    { slotIndex: 38, itemId: 312, metadata: 0, damage: 0.0, count: 1  },// diamond legs
    { slotIndex: 39, itemId: 313, metadata: 0, damage: 0.0, count: 1  },// diamond boots
  ],
  cursor: { itemId: -1, count: 0, damage: 0 },
  window: null,
};

const FAKE_AVAILABLE_TASKS = {
  available: [
    {
      plugin: 'FarmPlugin',
      tasks: [
        { name: 'HarvestWheat', description: 'Harvest all wheat in range', parameters: [{ name: 'radius', type: 'number', required: false, defaultValue: '16', description: 'Harvest radius in blocks' }] },
        { name: 'PlantWheat', description: 'Plant wheat seeds in tilled soil', parameters: [] },
        { name: 'WaterCrops', description: 'Use water bucket on crops', parameters: [] },
      ],
    },
    {
      plugin: 'TradingPlugin',
      tasks: [
        { name: 'SellInventory', description: 'Sell all sellable items', parameters: [{ name: 'shop', type: 'string', required: true, description: 'Shop name to sell at' }] },
        { name: 'BuyItems', description: 'Buy items from shop', parameters: [{ name: 'item', type: 'string', required: true }, { name: 'amount', type: 'number', required: true }] },
      ],
    },
    {
      plugin: 'UnloadArea',
      tasks: [
        { name: 'UnloadArea', description: 'Collect drops at a spawner area and deposit them into the configured storage.', parameters: [{ name: 'configId', type: 'string', required: true, description: 'ID of the UnloadAreaConfig that specifies the spawner area and deposit storage.', optionSource: 'UnloadAreaConfigs' }] },
      ],
    },
    {
      plugin: 'Storage',
      tasks: [
        { name: 'StorageDeposit', description: 'Deposit items into a storage.', parameters: [{ name: 'storageId', type: 'string', required: true, description: 'Storage to deposit into.', optionSource: 'Storages' }, { name: 'itemKey', type: 'string', required: false }] },
      ],
    },
    {
      plugin: 'NavigationPlugin',
      tasks: [
        { name: 'WalkTo', description: 'Walk to coordinates', parameters: [{ name: 'x', type: 'number', required: true }, { name: 'y', type: 'number', required: true }, { name: 'z', type: 'number', required: true }] },
        { name: 'UseWarp', description: 'Use a warp point', parameters: [{ name: 'warp', type: 'string', required: true }] },
      ],
    },
  ],
};

const FAKE_TASK_PARAMETER_OPTIONS: Record<string, { id: string; name: string }[]> = {
  UnloadAreaConfigs: [
    { id: 'f6000000-0000-0000-0000-000000000001', name: 'Blaze-Spawner Nord' },
    { id: 'f6000000-0000-0000-0000-000000000002', name: 'Eisengolem-Farm' },
  ],
};

const FAKE_TASK_STATUS = {
  active: true,
  tree: {
    id: 'task-tree-001',
    pluginName: 'FarmPlugin',
    taskName: 'HarvestWheat',
    status: 'Running',
    progress: 0.42,
    statusMessage: 'Harvesting row 7 of 16...',
    error: null,
    startedAt: NOW,
    completedAt: null,
    subtasks: [
      { id: 'sub-001', pluginName: 'NavigationPlugin', taskName: 'WalkTo', status: 'Completed', progress: 1.0, statusMessage: 'Arrived', error: null, startedAt: YESTERDAY, completedAt: NOW, subtasks: [] },
      { id: 'sub-002', pluginName: 'FarmPlugin', taskName: 'PlantWheat', status: 'Running', progress: 0.1, statusMessage: 'Planting...', error: null, startedAt: NOW, completedAt: null, subtasks: [] },
    ],
    isPaused: false,
    interceptDepth: 0,
  },
  queue: [
    { id: 'queue-preview-001', pluginName: 'TradingPlugin', taskName: 'SellInventory' },
    { id: 'queue-preview-002', pluginName: 'Storage', taskName: 'ReadChestContents' },
  ],
};

const fakeHistoryRun = (id: string, pluginName: string, taskName: string, status: string, hoursAgo: number, error: string | null = null) => {
  const completedAt = new Date(Date.now() - hoursAgo * 3600000).toISOString();
  const startedAt = new Date(Date.now() - hoursAgo * 3600000 - 240000).toISOString();
  return {
    id, pluginName, taskName, status, progress: status === 'Completed' ? 100 : 40, statusMessage: null, error,
    startedAt, completedAt, isPaused: false, interceptDepth: 0, isIntercept: false, isChatCommand: false, interceptTask: null,
    subtasks: [
      { id: `${id}-walk`, pluginName: 'Pathfinding', taskName: 'GoTo', status: 'Completed', progress: 100, statusMessage: 'Arrived', error: null, startedAt, completedAt: startedAt, subtasks: [], isPaused: false, interceptDepth: 0, isIntercept: false, isChatCommand: false, interceptTask: null },
    ],
  };
};

const FAKE_TASK_HISTORY_NEWEST = [
  fakeHistoryRun('hist-001', 'Storage', 'ScanStorageForChests', 'Completed', 1),
  fakeHistoryRun('hist-002', 'Jobs', 'ScanJobsManual', 'Failed', 3, 'Inventory window closed unexpectedly'),
  fakeHistoryRun('hist-003', 'Storage', 'ReadChestContents', 'Cancelled', 26),
];

const FAKE_TASK_HISTORY_OLDER = [
  fakeHistoryRun('hist-004', 'Farm', 'FarmCrops', 'Completed', 24 * 6),
  fakeHistoryRun('hist-005', 'Storage', 'MoveItemsBetweenStorages', 'Failed', 24 * 30, 'Chest at 120 64 -310 could not be opened'),
];

const fakeConnectionMarker = (id: string, actionType: string, online: boolean, hoursAgo: number, actorUsername: string | null = null, reason: string | null = null) => ({
  id, actionType, online, actorUsername, reason, at: new Date(Date.now() - hoursAgo * 3600000).toISOString(),
});

const FAKE_CONNECTION_MARKERS_NEWEST = [
  fakeConnectionMarker('conn-001', 'Reconnected', true, 2),
  fakeConnectionMarker('conn-002', 'Disconnected', false, 2.5, null, 'Connection timed out'),
  fakeConnectionMarker('conn-003', 'Reconnecting', false, 2.5),
  fakeConnectionMarker('conn-004', 'Started', true, 25, 'PreviewUser'),
];

const FAKE_CONNECTION_MARKERS_OLDER = [
  fakeConnectionMarker('conn-005', 'Stopped', false, 24 * 2, 'PreviewUser'),
  fakeConnectionMarker('conn-006', 'AutoResumed', true, 24 * 10),
];

const FAKE_PLUGIN_SETTINGS = {
  plugins: [
    {
      plugin: 'FarmPlugin',
      definitions: [
        { name: 'defaultRadius', label: 'Default Harvest Radius', type: 'number', default: 16, description: 'Default radius for harvest tasks' },
        { name: 'autoReplant', label: 'Auto Replant', type: 'boolean', default: true, description: 'Automatically replant crops after harvest' },
        { name: 'farmMode', label: 'Farm Mode', type: 'enum', default: 'wheat', options: ['wheat', 'potato', 'carrot', 'beet'], description: 'Primary crop type' },
      ],
      values: { defaultRadius: 16, autoReplant: true, farmMode: 'wheat' },
    },
    {
      plugin: 'TradingPlugin',
      definitions: [
        { name: 'defaultShop', label: 'Default Shop', type: 'string', default: '/warp shop', description: 'Warp command for the trading shop' },
        { name: 'sellDelay', label: 'Sell Delay (ms)', type: 'number', default: 500 },
        { name: 'whitelist', label: 'Item Whitelist', type: 'list', elementSchema: { name: 'item', label: 'Item Key', type: 'string', default: '' }, default: [] },
      ],
      values: { defaultShop: '/warp shop', sellDelay: 500, whitelist: ['264:0', '388:0'] },
    },
    {
      plugin: 'Armoury',
      definitions: [
        { name: 'StorageId', label: 'Storage', type: 'string', optionSource: 'Storages', default: null, description: 'Storage the bot deposits worn gear into and pulls fresh tools and armor from.' },
      ],
      values: { StorageId: '00000000-0000-0000-0000-000000000000' },
    },
    {
      plugin: 'Protection',
      definitions: [
        { name: 'DetectionRadius', label: 'Detection Radius', type: 'number', default: 12, description: 'Engage hostile mobs that come within this many blocks of the bot.' },
        { name: 'SafeRadius', label: 'Safe Radius', type: 'number', default: 18, description: 'Combat stays active until no hostile mobs are within this many blocks.' },
        { name: 'JumpCrit', label: 'Jump Crit', type: 'boolean', default: true, description: 'Jump before each swing and hit while falling for extra damage.' },
        { name: 'AttackCooldownMs', label: 'Attack Cooldown (ms)', type: 'number', default: 400 },
        { name: 'MobUnreachableTimeoutMs', label: 'Mob Unreachable Timeout (ms)', type: 'number', default: 8000 },
        { name: 'ExcludedTasks', label: 'Excluded Tasks', type: 'list', optionSource: 'AvailableTasks', elementSchema: { name: 'task', label: 'Task', type: 'string', default: '' }, default: ['Storage/StorageRetrieval', 'Storage/StorageDeposit', 'Storage/MoveItemsBetweenStorages'], description: 'Tasks the plugin will NOT interrupt. Anything not listed here is protected automatically.' },
        { name: 'HealthThreshold', label: 'Health Threshold', type: 'number', default: 6.0, description: 'Trigger the safeguard when the bot\'s health drops below this value (0-20).' },
        { name: 'SafeLocation', label: 'Safe Location', type: 'location', default: null, description: 'Where the bot flees when the safeguard fires. When unset, the bot disconnects instead.' },
        { name: 'MaxHealWaitMs', label: 'Max Heal Wait (ms)', type: 'number', default: 60000 },
      ],
      values: { DetectionRadius: 12, SafeRadius: 18, JumpCrit: true, AttackCooldownMs: 400, MobUnreachableTimeoutMs: 8000, ExcludedTasks: ['FarmPlugin/PlantWheat', 'Storage/StorageRetrieval'], HealthThreshold: 6.0, SafeLocation: '/home schutz', MaxHealWaitMs: 60000 },
    },
    {
      plugin: 'AutoJoin',
      definitions: [
        { name: 'TargetCitybuild', label: 'Target Citybuild', type: 'enum', default: 'CB1', options: ['CB1', 'CB2', 'CB3', 'CBE', 'Nature'], description: 'The citybuild to join automatically.' },
        { name: 'Target', label: 'Join Target', type: 'location', default: '/p h 1', description: 'Where to go after switching citybuild. Leave unset to stay on the citybuild spawn.' },
        { name: 'DisconnectOnFailure', label: 'Disconnect on join failure', type: 'boolean', default: true, description: 'If joining the citybuild or travelling to the target fails, force-disconnect so the bot\'s auto-reconnect loop can retry from scratch.' },
        {
          name: 'Pause', label: 'Rejoin pause', type: 'object',
          description: 'A daily window during which the bot does not bring itself back online. A bot that drops at 05:00 with a 04:00 to 08:00 pause stays offline until 08:00 and joins then.',
          default: { From: null, Until: null, DisconnectOnStart: false, ReleaseJitterMinutes: 5 },
          fields: [
            { name: 'From', label: 'Pause from', type: 'time', default: null, description: 'German local time. Leave both times empty to rejoin around the clock.' },
            { name: 'Until', label: 'Pause until', type: 'time', default: null, description: 'German local time. The window may cross midnight, so 22:00 until 06:00 is one night.' },
            { name: 'DisconnectOnStart', label: 'Go offline when the window opens', type: 'boolean', default: false, description: 'Disconnect a bot that is still playing when the window starts, instead of only holding back one that drops during it. Whatever it was working on is cancelled.' },
            { name: 'ReleaseJitterMinutes', label: 'Spread the rejoin by up to (minutes)', type: 'number', default: 5, description: 'Each bot picks a random delay up to this many minutes after the window closes. Bots sharing a pause would otherwise all come back in the same second. 0 rejoins the moment the window ends.' },
          ],
        },
      ],
      values: { TargetCitybuild: 'CB1', Target: '/p h 1', DisconnectOnFailure: true, Pause: { From: '04:00:00', Until: '08:00:00', DisconnectOnStart: true, ReleaseJitterMinutes: 5 } },
    },
    {
      plugin: 'Freebie',
      definitions: [
        { name: 'IsEnabled', label: 'Active', type: 'boolean', default: false, description: 'Whether the lamp hands anything out.' },
        { name: 'LampX', label: 'Lamp X', type: 'number', default: 0, description: 'Block coordinate of the redstone lamp that starts a giveaway.' },
        { name: 'LampY', label: 'Lamp Y', type: 'number', default: 0, description: 'Block coordinate of the redstone lamp that starts a giveaway.' },
        { name: 'LampZ', label: 'Lamp Z', type: 'number', default: 0, description: 'Block coordinate of the redstone lamp that starts a giveaway.' },
        { name: 'LampDebounceSeconds', label: 'Lamp debounce (seconds)', type: 'number', default: 2 },
      ],
      values: { IsEnabled: true, LampX: -248814, LampY: 64, LampZ: -1201, LampDebounceSeconds: 2 },
    },
  ],
};

const FAKE_BOT_PLUGINS = {
  plugins: [
    { name: 'FarmPlugin', isUserFacing: true, requiredFeatureFlag: 'FARM', requiredLicense: null, customPagePath: null, hasSettings: true, enabled: true, statusCard: null },
    { name: 'TradingPlugin', isUserFacing: true, requiredFeatureFlag: null, requiredLicense: null, customPagePath: null, hasSettings: true, enabled: false, statusCard: null },
    { name: 'Protection', isUserFacing: true, requiredFeatureFlag: null, requiredLicense: null, customPagePath: null, hasSettings: true, enabled: true, statusCard: null },
    { name: 'Armoury', isUserFacing: true, requiredFeatureFlag: null, requiredLicense: null, customPagePath: null, hasSettings: true, enabled: true, statusCard: null },
    { name: 'AutoJoin', isUserFacing: true, requiredFeatureFlag: null, requiredLicense: null, customPagePath: null, hasSettings: true, enabled: true, statusCard: null },
    { name: 'Freebie', isUserFacing: true, requiredFeatureFlag: null, requiredLicense: null, customPagePath: null, hasSettings: true, enabled: true, statusCard: null },
  ],
  botRunning: true,
};

const FAKE_SESSIONS: any[] = [
  { id: SESSION1, info: 'Chrome on Windows (Preview)', isVerifiedAndValid: true, createdAt: YESTERDAY },
];

const FAKE_AUDIT_LOG = [
  { id: 1, action: 'Login', description: 'Logged in from Chrome on Windows', createdAt: NOW, executorUserId: UID },
  { id: 2, action: 'GrantCreated', description: 'Granted access to FarmerBot42', createdAt: YESTERDAY, executorUserId: UID },
  { id: 3, action: 'CommandCreated', description: 'Created command: Sell All', createdAt: YESTERDAY, executorUserId: UID },
];

const FAKE_INVENTORY_AUDIT_LOG = {
  items: [
    { id: 'ial-001', storageId: STORAGE1, storageName: 'Main Storage', actionType: 'input', userId: UID, username: 'PreviewUser', items: { '264:0': 64 }, note: 'Batch scan', createdAt: NOW },
    { id: 'ial-002', storageId: STORAGE1, storageName: 'Main Storage', actionType: 'extraction', userId: UID, username: 'PreviewUser', items: { '265:0': 128 }, note: 'Sell run', createdAt: YESTERDAY },
  ],
  total: 2,
};

const FAKE_RESOURCE_ASSIGNMENTS = (resourceId: string, family: string) => ({
  resourceId,
  resourceFamily: family,
  ownerId: UID,
  ownerName: 'PreviewUser',
  myActions: [`${family}.read`, `${family}.access.read`, `${family}.access.grant`, `${family}.delete`],
  assignments: [
    { id: 'ra-000', principalType: 0, principalId: UID, principalName: 'PreviewUser', roleId: '11111111-0000-4000-8000-000000000003', roleName: 'Storage owner', expiresAt: null, scope: 2, scopeName: null, isOwner: true, isExpired: false, createdByUserId: UID, createdByName: 'PreviewUser', createdAt: NOW },
    { id: 'ra-001', principalType: 0, principalId: UID3, principalName: 'TenantMember', roleId: '11111111-0000-4000-8000-000000000001', roleName: 'Storage reader', expiresAt: null, scope: 2, scopeName: null, isOwner: false, isExpired: false, createdByUserId: UID, createdByName: 'PreviewUser', createdAt: NOW },
    { id: 'ra-002', principalType: 1, principalId: TENANT_ID, principalName: 'Preview Tenant', roleId: '11111111-0000-4000-8000-000000000002', roleName: 'Storage contributor', expiresAt: null, scope: 2, scopeName: null, isOwner: false, isExpired: false, createdByUserId: UID, createdByName: 'PreviewUser', createdAt: NOW },
    { id: 'ra-003', principalType: 0, principalId: UID2, principalName: 'PlatformAdmin', roleId: '11111111-0000-4000-8000-0000000000ff', roleName: 'Platform administrator', expiresAt: null, scope: 0, scopeName: null, isOwner: false, isExpired: false, createdByUserId: UID, createdByName: 'PreviewUser', createdAt: NOW },
  ],
});

const FAKE_PROXIES = [
  { id: PROXY1, name: 'EU-West-1', host: '10.0.1.5', port: 3128, description: 'Primary EU proxy', isEnabled: true, lastPingMs: 18, lastPingAt: NOW, botsUsingCount: 2, userGrantsCount: 1, tenantGrantsCount: 1 },
];

const FAKE_ADMIN_TENANTS = [
  FAKE_TENANT,
  { id: 'tenant-002', name: 'Builder Guild', slug: 'builders', isActive: true, settings: null },
];

const FAKE_VERSIONS = ['4.2.1-beta1', '4.2.0', '4.1.5', '4.1.4', '4.1.3', '4.1.2', '4.1.1', '4.1.0', '4.0.9', '4.0.8'];

const FAKE_CHANGELOG = {
  '4.2.1-beta1': '- New: FarmPlugin auto-water support\n- Fix: task queue ordering on restart\n- Fix: mobile overflow on BotEngine page',
  '4.2.0': '- New: Inventory audit log with filtering\n- New: Per-chest slot viewer\n- Improved: BotEngine task tree visualization\n- Fix: tenant switching race condition',
  '4.1.5': '- New: Bot card header wrapping on mobile\n- Fix: JobDetails stats-grid layout on small screens\n- Fix: PlayerState grid collapse at 375px',
};

const FAKE_APP_EVENTS = [
  { id: 'evt-001', type: 'BotConnected', location: 'play.example-citybuild.de', data: { botId: BOT1, username: 'FarmerBot42' }, createdAt: NOW },
  { id: 'evt-002', type: 'TaskCompleted', location: 'play.example-citybuild.de', data: { plugin: 'FarmPlugin', task: 'HarvestWheat', duration: 340 }, createdAt: YESTERDAY },
];

const FAKE_CHAT_MESSAGES = [
  '[Server] Welcome to CitybuildA!',
  '<FarmerBot42> /sell all',
  '[Shop] Sold 64x wheat for $320.',
  '<FarmerBot42> /home',
  '[Server] You have been teleported.',
];

const FAKE_CHATLOG_LINES = [
  '§e[Server] §fWillkommen auf CitybuildA!',
  '§7[Shop] §fVerkauft 64x Weizen für §a$320§f.',
  '§b<Steve> §fhat jemand diamanten?',
  '§b<FarmerBot42> §f/sell all',
  '§7[Shop] §fVerkauft 128x Karotten für §a$512§f.',
  '§b<Alex> §fgg',
  '§e[Server] §fSpieler Notch ist dem Spiel beigetreten.',
  '§b<FarmerBot42> §f/home farm',
  '§e[Server] §fDu wurdest teleportiert.',
  '§b<Notch> §fwo ist der spawn',
];

const FAKE_CHATLOG_COUNT = 450;
const FAKE_CHATLOG_ENTRIES = Array.from({ length: FAKE_CHATLOG_COUNT }, (_, i) => ({
  id: `preview-chat-${i}`,
  createdAt: new Date(Date.now() - (FAKE_CHATLOG_COUNT - 1 - i) * 3600000).toISOString(),
  message: FAKE_CHATLOG_LINES[i % FAKE_CHATLOG_LINES.length],
}));

const FAKE_MSG_CONTACTS = [
  { name: 'Steve', display: '§bSteve', lastBody: '2 stacks wären super', lastAtUtc: NOW, lastIsOutgoing: false, count: 22, unreadCount: 2 },
  { name: 'Alex', display: '§aAlex', lastBody: 'gerne, bis später', lastAtUtc: YESTERDAY, lastIsOutgoing: true, count: 9, unreadCount: 0 },
  { name: 'Notch', display: '§6Notch', lastBody: 'wo ist der spawn', lastAtUtc: YESTERDAY, lastIsOutgoing: false, count: 4, unreadCount: 0 },
];

let fakeMutedContacts: string[] = ['notch'];

const FAKE_MSG_BODIES = [
  'hey, hast du noch sand?', 'ja, wie viel brauchst du?', '2 stacks wären super',
  'kostet 500 pro stack', 'passt, ich zahl gleich', 'danke dir!', 'gerne, bis später',
  'warte, noch eine frage', 'klar, schieß los', 'verkaufst du auch weizen?',
];

const FAKE_MSG_CONVERSATION = Array.from({ length: 22 }, (_, i) => ({
  id: `preview-msg-${i}`,
  createdAtUtc: new Date(Date.now() - (22 - i) * 90000).toISOString(),
  isOutgoing: i % 2 === 1,
  body: FAKE_MSG_BODIES[i % FAKE_MSG_BODIES.length],
  display: i % 2 === 1 ? '§bFarmerBot42' : '§bSteve',
}));

const FAKE_SYSTEM_QUEUE_HISTORY = Array.from({ length: 24 }, (_, i) => ({
  timestamp: new Date(Date.now() - (23 - i) * 3600000).toISOString(),
  queueLength: Math.floor(Math.random() * 5),
  processed: Math.floor(20 + Math.random() * 40),
  errors: Math.random() > 0.9 ? 1 : 0,
}));

const FAKE_FEATURE_FLAGS_CATALOG = [
  { key: 'DEMO_FEATURE', defaultEnabled: false, description: 'Placeholder feature used to verify the feature-flag system end-to-end. Safe to disable in production.' },
];

// A real shop carries a few hundred listings and a panel user watches a dozen shops, so the fixture
// is generated at that size: a three-item preview hides every cost the page pays at its real scale.
const SHOP_STOCK_MATERIALS = [
  'diamond', 'diamond_block', 'diamond_ore', 'diamond_pickaxe', 'diamond_sword', 'diamond_axe',
  'diamond_shovel', 'diamond_hoe', 'diamond_helmet', 'diamond_chestplate', 'diamond_leggings',
  'diamond_boots', 'gold_ingot', 'gold_block', 'gold_ore', 'iron_ingot', 'iron_block', 'iron_ore',
  'coal', 'coal_block', 'coal_ore', 'emerald', 'emerald_block', 'emerald_ore', 'redstone',
  'redstone_block', 'redstone_ore', 'lapis_block', 'lapis_ore', 'quartz', 'quartz_block',
  'cobblestone', 'stone', 'dirt', 'grass', 'sand', 'gravel', 'glass', 'glowstone', 'obsidian',
  'netherrack', 'soul_sand', 'end_stone', 'brick', 'brick_block', 'clay', 'clay_ball', 'bookshelf',
  'chest', 'crafting_table', 'furnace', 'anvil', 'beacon', 'cauldron', 'brewing_stand', 'hopper',
  'dispenser', 'dropper', 'piston', 'sticky_piston', 'rail', 'detector_rail', 'activator_rail',
  'bucket', 'bow', 'arrow', 'bread', 'apple', 'cake', 'cookie', 'egg', 'bone', 'bone_block',
  'book', 'compass', 'clock', 'blaze_rod', 'blaze_powder', 'ender_pearl', 'ghast_tear', 'feather',
  'flint', 'leather', 'paper', 'string', 'stick', 'wheat', 'carrot', 'potato', 'beetroot',
  'melon', 'pumpkin', 'cactus', 'sugar', 'slime_ball', 'magma_cream', 'spider_eye', 'rotten_flesh',
  'beef', 'cooked_beef', 'chicken', 'cooked_chicken', 'porkchop', 'cooked_porkchop', 'mutton',
  'cooked_mutton', 'rabbit', 'cooked_rabbit', 'fish', 'cooked_fish', 'melon_seeds', 'pumpkin_seeds',
  'wheat_seeds', 'nether_star', 'dragon_egg', 'dragon_breath', 'chorus_fruit', 'elytra', 'shield',
  'saddle', 'name_tag', 'lead', 'painting', 'item_frame', 'sign', 'ladder', 'torch', 'tnt',
  'chainmail_helmet', 'chainmail_chestplate', 'chainmail_leggings', 'chainmail_boots',
  // Some shop sources send the 1.8 wire id instead of a name.
  '175', '383', '397', '38',
];

// Enchanted variants of one material are separate catalogue entries, exactly as the hash-keyed
// ShopStockItem table stores them, so the fixture exercises the deduplicated payload too.
const SHOP_STOCK_VARIANTS: { suffix: string; ench: string; lore: string; repairCost: number }[] = [
  { suffix: '', ench: '[]', lore: '[]', repairCost: 0 },
  {
    suffix: ' §7(Effizienz V)', ench: '[{"id":32,"level":5}]',
    lore: '[{"lineNumber":0,"lineContent":"§7Nur für Tunnelsysteme."}]', repairCost: 7,
  },
  {
    suffix: ' §7(Glück III)', ench: '[{"id":35,"level":3},{"id":34,"level":3}]',
    lore: '[{"lineNumber":0,"lineContent":"§7Handgeschmiedet."},{"lineNumber":1,"lineContent":"§8Kein Umtausch."}]',
    repairCost: 15,
  },
];

const SHOP_STOCK_COLOUR_CODES = ['§f', '§b', '§6', '§a', '§e', '§d'];

const FAKE_SHOP_STOCK_ITEMS = SHOP_STOCK_MATERIALS.flatMap((material, materialIndex) =>
  SHOP_STOCK_VARIANTS.map((variant, variantIndex) => {
    const index = materialIndex * SHOP_STOCK_VARIANTS.length + variantIndex;
    const readableName = material.split('_').map(part => part[0].toUpperCase() + part.slice(1)).join(' ');
    const stackSize = material.includes('sword') || material.includes('pickaxe') || material.includes('helmet') ? 1 : 64;
    const unitPrice = 12 + (index * 37) % 4800;
    return {
      id: `shop-stock-item-${index + 1}`,
      displayName: `${SHOP_STOCK_COLOUR_CODES[index % SHOP_STOCK_COLOUR_CODES.length]}${readableName}${variant.suffix}`,
      materialName: `minecraft:${material}`,
      materialSubId: 0,
      stackSize,
      repairCost: variant.repairCost,
      itemFlags: 0,
      enchantmentsJson: variant.ench,
      loreJson: variant.lore,
      warehouseCount: (index * 137) % 5000,
      isAvailable: index % 11 !== 0,
      prices: stackSize === 1
        ? [{ amount: 1, price: unitPrice * 64 }]
        : [
          { amount: 1, price: unitPrice },
          { amount: 64, price: unitPrice * 62 },
          { amount: 64, price: -Math.round(unitPrice * 44) },
        ],
    };
  }),
);

const FAKE_SHOP_STOCK_BOTS = Array.from({ length: 15 }, (_, botIndex) => ({
  id: `shop-stock-bot-${botIndex + 1}`,
  uuid: `1111111${botIndex}-2222-3333-4444-55555555555${botIndex % 10}`,
  name: `StockShop${botIndex + 1}`,
  citybuild: (botIndex % 8) + 1,
  fetchedAt: new Date(Date.parse(YESTERDAY) + botIndex * 600000).toISOString(),
}));

// Each shop stocks its own window onto the catalogue at its own prices, so no two bots are identical
// and the compare view has something to compare.
const FAKE_SHOP_STOCK_STOCK_BY_BOT = new Map(FAKE_SHOP_STOCK_BOTS.map((bot, botIndex) => [
  bot.id,
  FAKE_SHOP_STOCK_ITEMS
    .filter((_, itemIndex) => (itemIndex + botIndex) % 2 === 0)
    .map((item, itemIndex) => ({
      ...item,
      warehouseCount: (item.warehouseCount + botIndex * 311) % 5000,
      isAvailable: (itemIndex + botIndex) % 13 !== 0,
      prices: item.prices.map(p => ({
        amount: p.amount,
        price: Math.round(p.price * (1 + ((botIndex * 7) % 25) / 100)),
      })),
    })),
]));

const FAKE_SHOP_STOCK_SNAPSHOT = {
  items: FAKE_SHOP_STOCK_ITEMS.map(({ prices, warehouseCount, isAvailable, ...definition }) => definition),
  bots: FAKE_SHOP_STOCK_BOTS.map(bot => ({
    ...bot,
    stock: (FAKE_SHOP_STOCK_STOCK_BY_BOT.get(bot.id) ?? []).map(listing => ({
      itemId: listing.id,
      warehouseCount: listing.warehouseCount,
      isAvailable: listing.isAvailable,
      prices: listing.prices,
    })),
  })),
};

const FAKE_SHOP_STOCK_WATCHED_IDS = FAKE_SHOP_STOCK_BOTS.slice(0, 4).map(bot => bot.id);

const SHOP_STOCK_CHANGE_KINDS = ['Raised', 'Lowered', 'Listed', 'Delisted'] as const;

const FAKE_SHOP_STOCK_PRICE_CHANGES = FAKE_SHOP_STOCK_WATCHED_IDS.flatMap((botId, botIndex) => {
  const bot = FAKE_SHOP_STOCK_BOTS.find(b => b.id === botId)!;
  const stock = FAKE_SHOP_STOCK_STOCK_BY_BOT.get(botId) ?? [];
  return stock.filter((_, i) => i % 9 === botIndex % 9).slice(0, 30).map((listing, i) => {
    const kind = SHOP_STOCK_CHANGE_KINDS[(i + botIndex) % SHOP_STOCK_CHANGE_KINDS.length];
    // Alternate the side the change lands on, so the feed shows both buy (negative) and sell tiers.
    const buyTier = listing.prices.find(p => p.price < 0);
    const tier = (i % 2 === 1 && buyTier) ? buyTier : listing.prices[0];
    const oldPrice = kind === 'Listed' ? null : tier.price;
    const newPrice = kind === 'Delisted'
      ? null
      : kind === 'Raised'
        ? Math.round(tier.price * 1.18)
        : kind === 'Lowered' ? Math.round(tier.price * 0.82) : tier.price;
    return {
      id: `shop-stock-change-${botIndex}-${i}`,
      botId,
      botName: bot.name,
      citybuild: bot.citybuild,
      itemId: listing.id,
      displayName: listing.displayName,
      materialName: listing.materialName,
      materialSubId: listing.materialSubId,
      enchantmentsJson: listing.enchantmentsJson,
      loreJson: listing.loreJson,
      repairCost: listing.repairCost,
      stackSize: listing.stackSize,
      amount: tier.amount,
      oldPrice,
      newPrice,
      kind,
      detectedAt: new Date(Date.now() - (botIndex * 37 + i * 11) * 60000).toISOString(),
      warehouseCount: kind === 'Delisted' ? 0 : listing.warehouseCount,
    };
  });
}).sort((a, b) => Date.parse(b.detectedAt) - Date.parse(a.detectedAt));

function fakeShopStockWatchlist(query: URLSearchParams) {
  const page = Math.max(1, parseInt(query.get('page') ?? '1', 10) || 1);
  const pageSize = Math.min(200, Math.max(1, parseInt(query.get('pageSize') ?? '50', 10) || 50));
  const bots = FAKE_SHOP_STOCK_BOTS.filter(bot => FAKE_SHOP_STOCK_WATCHED_IDS.includes(bot.id)).map((bot, i) => ({
    ...bot,
    nextRefreshAtUtc: new Date(Date.now() + (11 + i * 9) * 60000).toISOString(),
    itemCount: (FAKE_SHOP_STOCK_STOCK_BY_BOT.get(bot.id) ?? []).length,
  }));
  return {
    bots,
    changes: FAKE_SHOP_STOCK_PRICE_CHANGES.slice((page - 1) * pageSize, page * pageSize),
    totalChanges: FAKE_SHOP_STOCK_PRICE_CHANGES.length,
  };
}

function fakeShopStockCompare(itemId: string) {
  const source = FAKE_SHOP_STOCK_ITEMS.find(i => i.id === itemId) ?? FAKE_SHOP_STOCK_ITEMS[0];
  const bots = FAKE_SHOP_STOCK_BOTS.map(bot => {
    const variants = (FAKE_SHOP_STOCK_STOCK_BY_BOT.get(bot.id) ?? [])
      .filter(stocked => stocked.materialName === source.materialName)
      .map(stocked => ({
        itemId: stocked.id,
        enchantmentsJson: stocked.enchantmentsJson,
        loreJson: stocked.loreJson,
        repairCost: stocked.repairCost,
        itemFlags: stocked.itemFlags,
        stackSize: stocked.stackSize,
        warehouseCount: stocked.warehouseCount,
        isAvailable: stocked.isAvailable,
        prices: stocked.prices,
      }));
    return variants.length === 0
      ? null
      : { botId: bot.id, botName: bot.name, citybuild: bot.citybuild, variants };
  }).filter(entry => entry !== null);

  return {
    displayName: source.displayName,
    materialName: source.materialName,
    materialSubId: source.materialSubId,
    bots,
  };
}

const PREVIEW_LICENSES = ['License.ShopStock', 'License.VektorGoggles', 'License.LiveView'];

const PREVIEW_SEAT_COUNT = 2;
const PREVIEW_SEAT_TYPES = ['Seat.Sellbot', 'Seat.PlotAcl'];

// Mutable so assigning and removing a bot license in the preview shows up on the next fetch. BOT1 starts
// with a Sellbot license and no PlotACL license, so the banner's missing state is visible on /plotacl.
const fakeBotLicenses: { id: string; licenseType: string; botRegistryId: string; assignedAt: string }[] = [
  { id: 'preview-seat-1', licenseType: 'Seat.Sellbot', botRegistryId: BOT1, assignedAt: '2026-09-20T10:00:00Z' },
];

function fakeBotLicenseDto(seat: typeof fakeBotLicenses[number]) {
  return {
    id: seat.id,
    licenseType: seat.licenseType,
    botRegistryId: seat.botRegistryId,
    botName: FAKE_BOTS.find(b => b.id === seat.botRegistryId)?.minecraftUsername ?? null,
    holderUserId: 'preview-user',
    holderName: 'PreviewUser',
    state: 'Active',
    assignedAt: seat.assignedAt,
    graceEndsAt: null,
    cooldownEndsAt: null,
    releaseReason: null,
  };
}

function fakeBotLicenseSlots(botId: string) {
  return {
    botRegistryId: botId,
    licenses: PREVIEW_SEAT_TYPES.map(licenseType => {
      const seat = fakeBotLicenses.find(l => l.botRegistryId === botId && l.licenseType === licenseType);
      const used = fakeBotLicenses.filter(l => l.licenseType === licenseType).length;
      return {
        licenseType,
        displayName: licenseType,
        assignment: seat ? fakeBotLicenseDto(seat) : undefined,
        callerSeatsTotal: PREVIEW_SEAT_COUNT,
        callerSeatsFree: PREVIEW_SEAT_COUNT - used,
      };
    }),
  };
}

const FAKE_GRANT_SUMMARY = () => ({
  userId: 'preview-user',
  entries: [
    ...PREVIEW_LICENSES.map(type => ({
      type,
      kind: 'License',
      displayName: type,
      description: '',
      effectiveValue: 1,
      defaultValue: 0,
      used: 0,
      remaining: 1,
      sources: [],
    })),
    ...PREVIEW_SEAT_TYPES.map(type => {
      const used = fakeBotLicenses.filter(l => l.licenseType === type).length;
      return {
        type,
        kind: 'Seat',
        displayName: type,
        description: '',
        effectiveValue: PREVIEW_SEAT_COUNT,
        defaultValue: 0,
        used,
        remaining: PREVIEW_SEAT_COUNT - used,
        sources: [],
      };
    }),
  ],
});

const FAKE_FEATURE_FLAGS_EFFECTIVE: Record<string, boolean> = {
  AUTOMATION: true,
  PAYMENTS: true,
  BEDROCK: true,
  CITYBUILD_STATUS: true,
  SHOP_STOCK: true,
  SHOP_STOCK_WATCHLIST: true,
  VEKTOR_GOGGLES: true,
  SETUP_MODE: true,
  AH_HISTORY: true,
  SELF_SHUTOFF: true,
  AUTO_JOIN: true,
  DEMO_FEATURE: true,
  REMOTEX_LEGACY: true,
  SELLBOT: true,
  PROXIES: true,
  MSGS: true,
  SEED_CRACKER: true,
  SEED_CRACKER_PAGE: true,
  SEED_CRACKER_DIAGNOSTICS: true,
  INVENTORY_PAGE: true,
  GRIEFERUTILS_GUI: true,
  SELLBOT_MULTI_ZONE: true,
  GRIEFER_INFO_STATUS: true,
  PLAYER_LIST: true,
  DEMOLISH: true,
  ORBS_TRADER: true,
  POKEBALL: true,
  REPAIR: true,
  CROP_FARMER: true,
  VILLAGER_TRADING: true,
  FARM: true,
  UNLOAD_AREA: true,
  SPAWNER_UPGRADE: true,
  CASE_OPENING: true,
  CRAFTING: true,
  CRAFT_ORDERS: true,
  STOCK_PLANNING: true,
  MAPART: true,
  MAP_CACHE: true,
  MAP_CACHE_PAGE: true,
  SCHEMATICS: true,
  SCHEMATIC_PRINTER: true,
  FLOATING_BOTS_CARD: true,
  SELLBOT_HOLOGRAMS: true,
  SELLBOT_GRANTS: true,
  PLOTACL: true,
};

const POOL_WEBSHARE_DC = 'preview-pool-webshare-dc';
const POOL_WEBSHARE_RESIDENTIAL = 'preview-pool-webshare-res';
const POOL_BRIGHTDATA = 'preview-pool-brightdata';

const FAKE_EXTERNAL_PROXIES = [
  { id: 'ext-de-001', poolId: POOL_WEBSHARE_DC, poolTitle: 'Webshare Datacenter', name: 'Webshare Datacenter DE 185.12.0.4', host: '185.12.0.4', port: 1080, lastPingMs: 22, lastPingAt: NOW },
  { id: 'ext-de-002', poolId: POOL_WEBSHARE_DC, poolTitle: 'Webshare Datacenter', name: 'Webshare Datacenter DE 185.12.0.7', host: '185.12.0.7', port: 1080, lastPingMs: 480, lastPingAt: NOW },
  { id: 'ext-nl-001', poolId: POOL_WEBSHARE_DC, poolTitle: 'Webshare Datacenter', name: 'Webshare Datacenter NL 91.90.4.10', host: '91.90.4.10', port: 1080, lastPingMs: null, lastPingAt: NOW },
  { id: 'ext-bd-001', poolId: POOL_BRIGHTDATA, poolTitle: 'BrightData DC 1', name: 'BrightData DC 1 185.199.229.14', host: '185.199.229.14', port: 44445, lastPingMs: 34, lastPingAt: NOW },
  { id: 'ext-bd-002', poolId: POOL_BRIGHTDATA, poolTitle: 'BrightData DC 1', name: 'BrightData DC 1 185.199.229.15', host: '185.199.229.15', port: 44445, lastPingMs: 41, lastPingAt: NOW },
];

const FAKE_PROXY_POOLS = [
  {
    id: POOL_WEBSHARE_DC, title: 'Webshare Datacenter', sourceKind: 0, sourceUrl: null, hasApiKey: true,
    gatewayHost: null, gatewayPort: null, gatewayProtocol: 0, usableForBots: true, usableForVoting: true, votingPool: 0,
    isEnabled: true, lastSyncedAt: NOW, lastSyncError: null, lastSyncCount: 3,
    entryCount: 3, activeEntryCount: 3, botsUsingCount: 2,
  },
  {
    id: POOL_BRIGHTDATA, title: 'BrightData DC 1', sourceKind: 3, sourceUrl: null, hasApiKey: true,
    providerAccountId: 'hl_00000000', providerZone: 'datacenter_zone',
    gatewayHost: 'brd.superproxy.io', gatewayPort: 22228, gatewayProtocol: 0, usableForBots: true, usableForVoting: true, votingPool: 0,
    isEnabled: true, lastSyncedAt: NOW, lastSyncError: null, lastSyncCount: 2,
    entryCount: 2, activeEntryCount: 2, botsUsingCount: 0,
  },
  {
    id: POOL_WEBSHARE_RESIDENTIAL, title: 'Webshare Residential', sourceKind: 1,
    sourceUrl: 'https://proxy.webshare.io/api/v2/proxy/list/download/preview', hasApiKey: false,
    gatewayHost: 'p.webshare.io', gatewayPort: 80, gatewayProtocol: 1, usableForBots: false, usableForVoting: true, votingPool: 1,
    isEnabled: true, lastSyncedAt: NOW, lastSyncError: 'List download returned HTTP 429.', lastSyncCount: 0,
    entryCount: 812, activeEntryCount: 806, botsUsingCount: 0,
  },
];

const FAKE_PROXY_BOTS = [
  { botId: '00000000-0000-0000-0000-000000000001', minecraftUsername: 'AlphaBot', ownerUsername: 'demo', state: 'Playing', proxyId: PROXY1, externalProxyId: null, poolId: null, externalIdentity: null },
  { botId: '00000000-0000-0000-0000-000000000002', minecraftUsername: 'BetaBot', ownerUsername: 'demo', state: 'Offline', proxyId: PROXY1, externalProxyId: null, poolId: null, externalIdentity: null },
  { botId: '00000000-0000-0000-0000-000000000003', minecraftUsername: 'GammaBot', ownerUsername: 'demo', state: 'Playing', proxyId: null, externalProxyId: 'ext-de-001', poolId: POOL_WEBSHARE_DC, externalIdentity: '185.12.0.4' },
  { botId: '00000000-0000-0000-0000-000000000004', minecraftUsername: 'DeltaBot', ownerUsername: 'demo', state: 'Connecting', proxyId: null, externalProxyId: 'ext-de-002', poolId: POOL_WEBSHARE_DC, externalIdentity: '185.12.0.7' },
];

function fakeHealthBuckets(seedPing: number) {
  const now = Date.now();
  const n = 240;
  const stepMs = (24 * 60 * 60 * 1000) / n;
  return Array.from({ length: n }, (_, i) => {
    const t = new Date(now - (n - 1 - i) * stepMs).toISOString();
    if (i % 37 === 0) return { t, avg: null, min: null, max: null, failures: 3 };
    const base = seedPing + Math.sin(i / 9) * 6 + (i % 11) * 1.5;
    const avg = Math.max(4, Math.round(base));
    return { t, avg, min: Math.max(2, avg - 5), max: avg + 9, failures: 0 };
  });
}


const FAKE_SELLBOT_STACKS = [
  { key: '12:0', itemId: 12, metadata: 0, count: 1856, snbt: null, nbtHash: null, isCustom: false },
  { key: '264:0', itemId: 264, metadata: 0, count: 192, snbt: null, nbtHash: null, isCustom: false },
  { key: '373:8269', itemId: 373, metadata: 8269, count: 12, snbt: null, nbtHash: null, isCustom: false },
  {
    key: '373:0:cmdpotion', itemId: 373, metadata: 0, count: 3, nbtHash: 'cmdpotion', isCustom: true,
    snbt: '{CustomPotionEffects:[{Id:5b,Amplifier:1b,Duration:3600}]}',
  },
  { key: '278:0:enchpick', itemId: 278, metadata: 0, count: 1, snbt: '{ench:[{id:32,lvl:5}],display:{Name:"§6Mega Pickaxe"}}', nbtHash: 'enchpick', isCustom: true },
  { key: '278:0:enchpick2', itemId: 278, metadata: 0, count: 2, snbt: '{ench:[{id:32,lvl:5}],display:{Name:"§6Legendary Pickaxe"}}', nbtHash: 'enchpick2', isCustom: true },
  {
    key: '1:0:cmpstone2', itemId: 1, metadata: 0, count: 96, nbtHash: 'cmpstone2', isCustom: true,
    snbt: '{compressionLevel:"TWO",display:{Name:"§fStein §eII",Lore:["§781 Blöcke"]}}',
  },
  {
    key: '1:0:cmpstone3', itemId: 1, metadata: 0, count: 12, nbtHash: 'cmpstone3', isCustom: true,
    snbt: '{compressionLevel:"THREE",display:{Name:"§fStein §eIII",Lore:["§7729 Blöcke"]}}',
  },
];

// A GrieferGames craftable nobody stocks, so the search, the detail card and the craft page can be
// driven for a hashed key. Recipe as griefergames-recipes.json has the trapped-chest variant.
const UNLIMITED_STORAGE_HASH = 'speicher';
const UNLIMITED_STORAGE_KEY = `146:0:${UNLIMITED_STORAGE_HASH}`;

// Red carpet shows the buffered chain, anything over 100 shows a blocked line, the rest is a plain
// one-phase craft. Between them the order grid has every line state it can render.
const FAKE_CRAFT_ORDER_PLAN_LINE = (line: { itemKey: string; quantity: number }, lineIndex: number) => {
  const base = { lineIndex, itemKey: line.itemKey, quantity: line.quantity };
  if (line.quantity > 100) return {
    ...base,
    displayName: 'Oak Wood Planks',
    steps: [],
    shortfalls: [{ itemKey: '17:0', displayName: 'Oak Wood', needed: line.quantity, available: 12 }],
    totalConsumed: [],
    estimatedSeconds: 0,
    planError: null,
  };
  if (line.itemKey === '171:14') {
    const crafts = Math.ceil(line.quantity / 3);
    return {
      ...base,
      displayName: 'Red Carpet',
      steps: [
        {
          itemKey: '35:14', displayName: 'Red Wool', times: crafts * 2, resultCount: 1,
          producedQuantity: crafts * 2, disposition: 'Buffered', estimatedSeconds: 0,
          ingredients: [
            { itemKey: '35:0', displayName: 'White Wool', quantity: crafts * 2 },
            { itemKey: '351:1', displayName: 'Rose Red', quantity: crafts * 2 },
          ],
        },
        {
          itemKey: '171:14', displayName: 'Red Carpet', times: crafts, resultCount: 3,
          producedQuantity: crafts * 3, disposition: 'Final', estimatedSeconds: 22 + crafts * 0.4,
          ingredients: [{ itemKey: '35:14', displayName: 'Red Wool', quantity: crafts * 2 }],
        },
      ],
      shortfalls: [],
      totalConsumed: [
        { itemKey: '35:0', displayName: 'White Wool', quantity: crafts * 2 },
        { itemKey: '351:1', displayName: 'Rose Red', quantity: crafts * 2 },
      ],
      estimatedSeconds: 22 + crafts * 0.4,
      planError: null,
    };
  }
  return {
    ...base,
    displayName: 'Crafting Table',
    steps: [{
      itemKey: line.itemKey, displayName: 'Crafting Table', times: line.quantity, resultCount: 1,
      producedQuantity: line.quantity, disposition: 'Final', estimatedSeconds: 14 + line.quantity * 0.5,
      ingredients: [{ itemKey: '5:0', displayName: 'Oak Wood Planks', quantity: line.quantity * 4 }],
    }],
    shortfalls: [],
    totalConsumed: [{ itemKey: '5:0', displayName: 'Oak Wood Planks', quantity: line.quantity * 4 }],
    estimatedSeconds: 14 + line.quantity * 0.5,
    planError: null,
  };
};

// Lines wait as Pending until the dispatcher finds a free bot, which is the state a submit lands in.
const FAKE_MOVE_REQUESTS = (
  sourceStorageId: string,
  destinationStorageId: string,
  lines: { itemKey: string; quantity: number; itemDisplayName?: string }[],
) => lines.map((line, lineIndex) => ({
  id: `move-preview-${lineIndex + 1}`,
  userId: UID,
  username: 'PreviewUser',
  sourceStorageId,
  sourceStorageName: FAKE_STORAGES.find(storage => storage.id === sourceStorageId)?.name ?? 'Main Storage',
  destinationStorageId,
  destinationStorageName: FAKE_STORAGES.find(storage => storage.id === destinationStorageId)?.name ?? 'Main Storage',
  itemKey: line.itemKey,
  itemDisplayName: line.itemDisplayName ?? line.itemKey,
  requestedQuantity: line.quantity,
  actualQuantityMoved: 0,
  assignedBotRegistryId: FAKE_BOTS[0].id,
  status: 'Pending',
  statusValue: 0,
  startedAt: null,
  completedAt: null,
  failureReason: null,
  createdAt: new Date().toISOString(),
}));

const FAKE_CRAFT_ORDER = (lines: { itemKey: string; quantity: number; itemDisplayName?: string }[]) => ({
  id: 'craft-order-preview-1',
  userId: UID,
  username: 'PreviewUser',
  sourceStorageId: 'storage-1',
  sourceStorageName: 'Main Storage',
  resultStorageId: 'storage-1',
  resultStorageName: 'Main Storage',
  status: 'Pending',
  statusValue: 0,
  startedAt: null,
  completedAt: null,
  failureReason: null,
  createdAt: new Date().toISOString(),
  lines: lines.map((line, lineIndex) => ({
    id: `craft-order-line-${lineIndex + 1}`,
    lineIndex,
    itemKey: line.itemKey,
    itemDisplayName: line.itemDisplayName ?? line.itemKey,
    requestedQuantity: line.quantity,
    actualCrafted: 0,
    assignedBotRegistryId: null,
    status: 'Pending',
    statusValue: 0,
    startedAt: null,
    completedAt: null,
    failureReason: null,
  })),
});
const UNLIMITED_STORAGE_SNBT = '{display:{Name:"§6Unbegrenzter Speicher",Lore:["§7Material: §e?","§7Platziert einen unbegrenzten Speicherblock."]},unlimited-storage-item:1b}';
const UNLIMITED_STORAGE_INGREDIENTS = [
  { itemKey: '17:0', id: 17, name: 'log', displayName: 'Oak Wood', count: 4, stock: 12 },
  { itemKey: '41:0', id: 41, name: 'gold_block', displayName: 'Block of Gold', count: 2, stock: 6 },
  { itemKey: '154:0', id: 154, name: 'hopper', displayName: 'Hopper', count: 1, stock: 3 },
  { itemKey: '42:0', id: 42, name: 'iron_block', displayName: 'Block of Iron', count: 1, stock: 3 },
  { itemKey: '146:0', id: 146, name: 'trapped_chest', displayName: 'Trapped Chest', count: 1, stock: 3 },
];
const UNLIMITED_STORAGE_RESOLVE = { status: 'Craftable', maxCraftable: 3, hasRecipe: true, allStatus: 'Craftable', allMaxCraftable: 3 };

type FakeSellbotRule = {
  id: string; ruleType: string; operator: string; numericValue: number;
  stringValue: string | null; stringValue2: string | null; stringValue3: string | null;
  stripColorCodes: boolean; isEnabled: boolean;
};

// SellbotOfferDimensions reads ItemDurability to tell wear from a variant; the fixtures use these ids only.
const DAMAGEABLE_PREVIEW_IDS = new Set([261, 276, 278]);

// Stands in for SellbotRuleBuilder, so re-pointing an offer in preview lands the rules the server would write.
type FakeWizardRequest = {
  items: { key: string; itemId: number; metadata: number; count: number; snbt: string | null; nbtHash: string | null; isCustom: boolean }[];
  pinned: { kind: string; index: number; path: string | null }[];
};

function fakeDeviationRules(item: { itemId: number; metadata: number; snbt: string | null }): FakeSellbotRule[] {
  const blank = { operator: 'Eq', numericValue: 0, stringValue: null, stringValue2: null, stringValue3: null, stripColorCodes: false, isEnabled: true };
  const rules: FakeSellbotRule[] = [{
    ...blank, id: `preview-rule-${Date.now()}-damage`,
    ruleType: DAMAGEABLE_PREVIEW_IDS.has(item.itemId) ? 'DurabilityRemaining' : 'Metadata',
    numericValue: item.metadata,
  }];

  const name = /Name:"([^"]*)"/.exec(item.snbt ?? '')?.[1] ?? null;
  if (name) rules.push({ ...blank, id: `preview-rule-${Date.now()}-name`, ruleType: 'DisplayName', stringValue: name });

  const lore = [...(item.snbt ?? '').matchAll(/"([^"]*)"/g)].map(match => match[1]).filter(line => line !== name);
  lore.forEach((line, index) => rules.push({
    ...blank, id: `preview-rule-${Date.now()}-lore${index}`, ruleType: 'Lore',
    operator: 'Contains', numericValue: index, stringValue: line,
  }));

  for (const [, id, level] of (item.snbt ?? '').matchAll(/\{id:(\d+),lvl:(\d+)\}/g))
    rules.push({ ...blank, id: `preview-rule-${Date.now()}-ench${id}`, ruleType: 'Enchantment', stringValue: id, numericValue: Number(level) });

  const repairCost = /RepairCost:(\d+)/.exec(item.snbt ?? '')?.[1];
  if (repairCost) rules.push({ ...blank, id: `preview-rule-${Date.now()}-repair`, ruleType: 'RepairCost', numericValue: Number(repairCost) });

  const skullName = /SkullOwner:\{[^}]*Name:"([^"]*)"/.exec(item.snbt ?? '')?.[1] ?? null;
  const skullId = /SkullOwner:\{[^}]*Id:"([^"]*)"/.exec(item.snbt ?? '')?.[1] ?? null;
  if (skullName || skullId)
    rules.push({ ...blank, id: `preview-rule-${Date.now()}-skull`, ruleType: 'SkullOwner', stringValue: skullName, stringValue2: skullId });

  return rules;
}

function fakeDimensions(offer: { item: { itemId: number; metadata: number; snbt: string | null }; rules: FakeSellbotRule[] }) {
  // Mirrors SellbotOfferDimensions.KeyOf, including collapsing the two damage kinds onto one key. A
  // preview that grouped them differently would certify a page state the server never produces.
  const keyOf = (rule: { ruleType: string; numericValue: number; stringValue: string | null }) =>
    rule.ruleType === 'DurabilityRemaining' || rule.ruleType === 'Metadata' ? 'Metadata'
      : rule.ruleType === 'Lore' ? `Lore/${rule.numericValue}`
        : rule.ruleType === 'Enchantment' || rule.ruleType === 'NbtContains' ? `${rule.ruleType}/${rule.stringValue}`
          : rule.ruleType;

  const coveredKeys = new Set<string>();
  const dimensions: ReturnType<typeof describe>[] = [];

  function describe(kind: string, index: number, path: string | null) {
    const key = keyOf({ ruleType: kind, numericValue: index, stringValue: path });
    const rule = offer.rules.find(candidate => candidate.isEnabled && keyOf(candidate) === key);
    return {
      kind, index, path,
      value: !rule ? null
        : rule.ruleType === 'Signature' ? `${rule.stringValue ?? ''} ${rule.stringValue3 ?? ''}`.trim()
          : rule.stringValue ?? String(rule.numericValue),
      state: !rule ? 'Original' : rule.operator === 'Ignore' ? 'Ignored' : 'Custom',
      ruleId: rule?.id ?? null,
    };
  }

  const row = (kind: string, index: number, path: string | null) => {
    const key = keyOf({ ruleType: kind, numericValue: index, stringValue: path });
    if (coveredKeys.has(key)) return;
    coveredKeys.add(key);
    dimensions.push(describe(kind, index, path));
  };

  // An offer carries no item, so only the dimensions every item has are listed up front.
  row(DAMAGEABLE_PREVIEW_IDS.has(offer.item.itemId) ? 'DurabilityRemaining' : 'Metadata', 0, null);
  row('DisplayName', 0, null);
  row('Signature', 0, null);
  row('RepairCost', 0, null);
  row('NbtTagCount', 0, null);
  if (offer.item.itemId === 397) row('SkullOwner', 0, null);

  for (const rule of offer.rules) {
    if (!rule.isEnabled) continue;
    row(rule.ruleType, rule.ruleType === 'Lore' ? rule.numericValue : 0,
      rule.ruleType === 'Enchantment' || rule.ruleType === 'NbtContains' ? rule.stringValue : null);
  }
  return dimensions;
}

const FAKE_SELLBOT_OFFERS: {
  id: string;
  item: { key: string; itemId: number; metadata: number; count: number; snbt: string | null; nbtHash: string | null; isCustom: boolean };
  hasRules: boolean;
  availableStock: number;
  tiers: { id: string; count: number; price: number; isEnabled: boolean; isUnlisted?: boolean }[];
  rules: FakeSellbotRule[];
  ankaufTier?: { id: string; isEnabled: boolean; stages: { id: string; count: number; price: number; isEnabled: boolean }[] };
  ankaufLimits: unknown[];
  verkaufLimits: unknown[];
  note?: string | null;
  folderId?: string | null;
  isUnlisted?: boolean;
}[] = [
  {
    id: 'preview-offer-001',
    item: { key: '12:0', itemId: 12, metadata: 0, count: 1, snbt: null, nbtHash: null, isCustom: false },
    hasRules: false, availableStock: 1856,
    tiers: [
      { id: 'preview-tier-001', count: 64, price: 500, isEnabled: true },
      { id: 'preview-tier-002', count: 256, price: 1800, isEnabled: true },
      { id: 'preview-tier-018', count: 128, price: 5000, isEnabled: true },
      { id: 'preview-tier-020', count: 1, price: 7777, isEnabled: true, isUnlisted: true },
    ],
    rules: [], ankaufTier: undefined, ankaufLimits: [], verkaufLimits: [],
  },
  {
    id: 'preview-offer-002',
    item: { key: '264:0', itemId: 264, metadata: 0, count: 1, snbt: null, nbtHash: null, isCustom: false },
    hasRules: false, availableStock: 192,
    tiers: [
      { id: 'preview-tier-003', count: 8, price: 1500, isEnabled: true },
      { id: 'preview-tier-019', count: 16, price: 5000, isEnabled: true },
    ],
    rules: [],
    ankaufTier: { id: 'preview-ankauftier-001', isEnabled: true, stages: [
      { id: 'preview-stage-001', count: 8, price: 1200, isEnabled: true },
      { id: 'preview-stage-002', count: 32, price: 4500, isEnabled: true },
    ] },
    ankaufLimits: [], verkaufLimits: [],
  },
  {
    id: 'preview-offer-003',
    item: { key: '278:0', itemId: 278, metadata: 0, count: 1, snbt: '{ench:[{id:32,lvl:5}],display:{Name:"§6Mega Pickaxe"}}', nbtHash: 'enchpick', isCustom: true },
    hasRules: true, availableStock: 3,
    tiers: [
      { id: 'preview-tier-004', count: 1, price: 25000, isEnabled: true },
      { id: 'preview-tier-017', count: 4, price: 92000, isEnabled: false },
    ],
    note: 'Preis mit Kunde XY abgesprochen',
    rules: [
      { id: 'preview-rule-001', ruleType: 'Enchantment', operator: 'Ge', numericValue: 5, stringValue: '32', stringValue2: null, stringValue3: null, stripColorCodes: false, isEnabled: true },
      { id: 'preview-rule-010', ruleType: 'DisplayName', operator: 'Eq', numericValue: 0, stringValue: '§6Mega Pickaxe', stringValue2: null, stringValue3: null, stripColorCodes: false, isEnabled: true },
      { id: 'preview-rule-002', ruleType: 'RepairCost', operator: 'Le', numericValue: 5, stringValue: null, stringValue2: null, stringValue3: null, stripColorCodes: false, isEnabled: false },
      { id: 'preview-rule-007', ruleType: 'DurabilityRemaining', operator: 'Le', numericValue: 390, stringValue: null, stringValue2: null, stringValue3: null, stripColorCodes: false, isEnabled: true },
      { id: 'preview-rule-009', ruleType: 'NbtTagCount', operator: 'Eq', numericValue: 2, stringValue: null, stringValue2: null, stringValue3: null, stripColorCodes: false, isEnabled: true },
    ],
    ankaufTier: undefined, ankaufLimits: [], verkaufLimits: [],
  },
  {
    id: 'preview-offer-skull',
    item: { key: '397:3', itemId: 397, metadata: 3, count: 1, snbt: '{SkullOwner:{Name:"Notch",Id:"069a79f4-44e9-4726-a5be-fca90e38aaf5"}}', nbtHash: 'notchhead', isCustom: true },
    hasRules: true, availableStock: 4,
    tiers: [
      { id: 'preview-tier-020', count: 1, price: 15000, isEnabled: true },
    ],
    rules: [
      { id: 'preview-rule-skull', ruleType: 'SkullOwner', operator: 'Eq', numericValue: 0, stringValue: 'Notch', stringValue2: '069a79f4-44e9-4726-a5be-fca90e38aaf5', stringValue3: null, stripColorCodes: false, isEnabled: true },
    ],
    ankaufTier: undefined, ankaufLimits: [], verkaufLimits: [],
  },
  {
    id: 'preview-offer-004',
    item: { key: '276:0', itemId: 276, metadata: 0, count: 1, snbt: null, nbtHash: null, isCustom: false },
    hasRules: false, availableStock: 128,
    tiers: [
      { id: 'preview-tier-005', count: 1, price: 50, isEnabled: true },
      { id: 'preview-tier-006', count: 8, price: 350, isEnabled: true },
      { id: 'preview-tier-007', count: 64, price: 2400, isEnabled: true },
    ],
    rules: [], ankaufTier: undefined, ankaufLimits: [], verkaufLimits: [],
    folderId: 'preview-folder-001',
  },
  {
    id: 'preview-offer-005',
    item: { key: '322:1', itemId: 322, metadata: 1, count: 1, snbt: null, nbtHash: null, isCustom: false },
    hasRules: false, availableStock: 4,
    tiers: [
      { id: 'preview-tier-008', count: 1, price: 30, isEnabled: true },
      { id: 'preview-tier-009', count: 4, price: 110, isEnabled: true },
    ],
    rules: [],
    ankaufTier: { id: 'preview-ankauftier-002', isEnabled: true, stages: [
      { id: 'preview-stage-003', count: 32, price: 20, isEnabled: true },
    ] },
    ankaufLimits: [], verkaufLimits: [],
  },
  {
    id: 'preview-offer-006',
    item: { key: '4:0', itemId: 4, metadata: 0, count: 1, snbt: null, nbtHash: null, isCustom: false },
    hasRules: false, availableStock: 0,
    tiers: [{ id: 'preview-tier-010', count: 640, price: 5, isEnabled: true }],
    rules: [], ankaufTier: undefined, ankaufLimits: [], verkaufLimits: [],
  },
  {
    id: 'preview-offer-007',
    item: { key: '399:0', itemId: 399, metadata: 0, count: 1, snbt: null, nbtHash: null, isCustom: false },
    hasRules: false, availableStock: 0,
    tiers: [],
    rules: [],
    ankaufTier: { id: 'preview-ankauftier-003', isEnabled: true, stages: [
      { id: 'preview-stage-004', count: 4, price: 900, isEnabled: true },
    ] },
    ankaufLimits: [], verkaufLimits: [],
  },
  {
    id: 'preview-offer-008',
    item: { key: '265:0', itemId: 265, metadata: 0, count: 1, snbt: null, nbtHash: null, isCustom: false },
    hasRules: false, availableStock: 210,
    tiers: [
      { id: 'preview-tier-011', count: 8, price: 8, isEnabled: true },
      { id: 'preview-tier-012', count: 32, price: 30, isEnabled: true },
      { id: 'preview-tier-013', count: 64, price: 55, isEnabled: true },
      { id: 'preview-tier-014', count: 320, price: 260, isEnabled: true },
    ],
    rules: [], ankaufTier: undefined, ankaufLimits: [], verkaufLimits: [],
  },
  {
    id: 'preview-offer-009',
    item: { key: '370:0', itemId: 370, metadata: 0, count: 1, snbt: null, nbtHash: null, isCustom: false },
    hasRules: true, availableStock: 2,
    tiers: [{ id: 'preview-tier-015', count: 1, price: 1500, isEnabled: true }],
    rules: [
      { id: 'preview-rule-003', ruleType: 'DisplayName', operator: 'Contains', numericValue: 0, stringValue: 'Ghast', stringValue2: null, stringValue3: null, stripColorCodes: true, isEnabled: true },
    ],
    ankaufTier: { id: 'preview-ankauftier-004', isEnabled: true, stages: [
      { id: 'preview-stage-005', count: 1, price: 900, isEnabled: true },
    ] },
    ankaufLimits: [], verkaufLimits: [],
  },
  {
    id: 'preview-offer-010',
    item: { key: '46:0', itemId: 46, metadata: 0, count: 1, snbt: null, nbtHash: null, isCustom: false },
    hasRules: false, availableStock: 0,
    tiers: [],
    rules: [],
    ankaufTier: { id: 'preview-ankauftier-005', isEnabled: true, stages: [
      { id: 'preview-stage-006', count: 64, price: 40, isEnabled: true },
    ] },
    ankaufLimits: [], verkaufLimits: [],
  },
  {
    id: 'preview-offer-011',
    item: {
      key: '347:0', itemId: 347, metadata: 0, count: 1,
      snbt: '{display:{Name:"§bWächteruhr",Lore:["§7Tier I","","§fViel Glück","§fbeim Wächtercasino","§7Signiert von §aAktenkoffer_ §7am §e19.07.2026"]}}',
      nbtHash: 'signedclock', isCustom: true,
    },
    hasRules: true, availableStock: 6,
    tiers: [{ id: 'preview-tier-016', count: 1, price: 90000, isEnabled: true }],
    rules: [
      { id: 'preview-rule-004', ruleType: 'DisplayName', operator: 'Eq', numericValue: 0, stringValue: '§bWächteruhr', stringValue2: null, stringValue3: null, stripColorCodes: false, isEnabled: true },
      { id: 'preview-rule-005', ruleType: 'Lore', operator: 'Contains', numericValue: 0, stringValue: '§7Tier I', stringValue2: null, stringValue3: null, stripColorCodes: false, isEnabled: true },
      { id: 'preview-rule-006', ruleType: 'Signature', operator: 'Eq', numericValue: 0, stringValue: 'Aktenkoffer_', stringValue2: '§fViel Glück\n§fbeim Wächtercasino', stringValue3: '19.07.2026', stripColorCodes: false, isEnabled: true },
    ],
    ankaufTier: undefined, ankaufLimits: [], verkaufLimits: [],
  },
  {
    id: 'preview-offer-012',
    item: { key: '383:66', itemId: 383, metadata: 66, count: 1, snbt: null, nbtHash: null, isCustom: false },
    hasRules: true, availableStock: 12,
    tiers: [{ id: 'preview-tier-018', count: 1, price: 4500, isEnabled: true }],
    rules: [
      { id: 'preview-rule-008', ruleType: 'Metadata', operator: 'Eq', numericValue: 66, stringValue: null, stringValue2: null, stringValue3: null, stripColorCodes: false, isEnabled: true },
    ],
    ankaufTier: { id: 'preview-ankauftier-008', isEnabled: false, stages: [
      { id: 'preview-stage-007', count: 1, price: 3200, isEnabled: false },
    ] },
    ankaufLimits: [], verkaufLimits: [],
  },
];

const FAKE_SELLBOT_ITEM_STATS = [
  // A bundle is its own row, named after the bundle, so its price is counted once instead of being
  // credited to each of its members.
  { itemKey: null, snbt: null, count: 432, amount: 15000, bundleName: 'Starter-Kit' },
  { itemKey: '12:0', count: 1216, amount: 9520 },
  { itemKey: '264:0', count: 96, amount: 18000 },
  { itemKey: '276:0', count: 24, amount: 8800 },
  { itemKey: '265:0', count: 640, amount: 3520 },
  { itemKey: '278:0', count: 1, amount: 25000 },
];

const FAKE_SELLBOT_ANKAUF_ITEM_STATS = [
  // A custom item's offer key carries an NBT hash and the offer's rendered snbt rides along, so the
  // row has to name the item ("Mega Pickaxe"), not print the key.
  { itemKey: '278:0:enchpick', snbt: '{ench:[{id:32,lvl:5}],display:{Name:"§6Mega Pickaxe"}}', count: 1, amount: 20000 },
  { itemKey: '264:0', count: 160, amount: 24500 },
  { itemKey: '322:1', count: 96, amount: 60 },
  { itemKey: '399:0', count: 8, amount: 1800 },
  { itemKey: '370:0', count: 3, amount: 2700 },
];

const FAKE_SELLBOT_PLAYER_STATS = [
  { playerName: 'Steve', count: 14, amount: 12400 },
  { playerName: 'Alex', count: 9, amount: 9800 },
  { playerName: 'Notch', count: 5, amount: 4100 },
];

const FAKE_SELLBOT_ANKAUF_PLAYER_STATS = [
  { playerName: 'Alex', count: 11, amount: 15200 },
  { playerName: 'Steve', count: 6, amount: 7800 },
];

const FAKE_SELLBOT_STATS = {
  from: YESTERDAY, to: NOW,
  totalRevenue: 25400, salesCount: 12, refundsCount: 2,
  totalAnkaufSpend: 18900, ankaufCount: 8,
  avgSaleValue: 2116.67, avgAnkaufValue: 2362.5,
  netMargin: 6500,
  chamberValueAtAnkaufRate: 8420,
  chamberValueAtVerkaufRate: 13100,
  topSellingItems: FAKE_SELLBOT_ITEM_STATS,
  topBoughtItems: FAKE_SELLBOT_ANKAUF_ITEM_STATS,
  topBuyers: FAKE_SELLBOT_PLAYER_STATS,
  topSellers: FAKE_SELLBOT_ANKAUF_PLAYER_STATS,
  salesByDayOfWeek: [
    { day: 0, count: 2, amount: 3200 },
    { day: 1, count: 1, amount: 900 },
    { day: 2, count: 3, amount: 5400 },
    { day: 3, count: 0, amount: 0 },
    { day: 4, count: 2, amount: 4100 },
    { day: 5, count: 3, amount: 8600 },
    { day: 6, count: 1, amount: 3200 },
  ],
  ankaufByDayOfWeek: [
    { day: 0, count: 1, amount: 1800 },
    { day: 1, count: 2, amount: 2600 },
    { day: 2, count: 1, amount: 900 },
    { day: 3, count: 0, amount: 0 },
    { day: 4, count: 2, amount: 3200 },
    { day: 5, count: 1, amount: 1500 },
    { day: 6, count: 1, amount: 900 },
  ],
  itemTurnover: FAKE_SELLBOT_OFFERS.map((o, i) => ({
    itemKey: o.item.key,
    soldCount: [64, 24, 1, 128, 8, 320, 0, 96, 2, 0][i] ?? 0,
    currentStock: o.availableStock,
    outOfStockHits: o.availableStock === 0 ? (i % 3) + 1 : 0,
  })),
};

type FakeSellbotZone = Record<string, unknown> & { id: string; name: string; dropPathCount: number; dropPathRecordedAt: string | null };

const FAKE_SELLBOT_CONFIG = {
  id: 'preview-sellbot-001',
  botRegistryId: BOT1,
  isEnabled: true,
  defaultLogLevel: 'Info',
  sellChamberStorageId: SELLBOT_STORAGE_ID,
  sellChamberStorageName: 'FarmerBot42 Botkammer',
  homeName: 'shop',
  homeX: 124, homeY: 65, homeZ: -42,
  dropYaw: 180, dropPitch: 30, dropSettleDelayMs: 150,
  sellAreaMinX: 122, sellAreaMinY: 64, sellAreaMinZ: -45,
  sellAreaMaxX: 126, sellAreaMaxY: 67, sellAreaMaxZ: -40,
  botshopDomainAssigned: true,
  offers: FAKE_SELLBOT_OFFERS,
  zoneMode: 'Single',
  zones: [] as FakeSellbotZone[],
  zoneMergeConflicts: [] as { firstZoneId: string; secondZoneId: string }[],
};

// Two offers share price 5000, which is what makes them a bundle. buyableBundles is the scarcest
// member: stone 1856/128 = 14, diamonds 192/16 = 12.
const FAKE_SELLBOT_BUNDLES = [
  {
    id: 'preview-bundle-001',
    name: 'Starter-Kit',
    price: 5000,
    buyableBundles: 12,
    members: [
      { offerId: 'preview-offer-001', itemKey: '12:0', snbt: null, count: 128, isEnabled: true },
      { offerId: 'preview-offer-002', itemKey: '264:0', snbt: null, count: 16, isEnabled: true },
    ],
  },
];

const FAKE_SELLBOT_FOLDERS: { id: string; name: string; parentFolderId: string | null; sortOrder: number }[] = [
  { id: 'preview-folder-001', name: 'Baustoffe', parentFolderId: null, sortOrder: 0 },
];

// Derived per request, not once at module load: the rule routes below mutate the fixtures in place.
const sellbotConfigResponse = () => ({
  ...FAKE_SELLBOT_CONFIG,
  offers: FAKE_SELLBOT_OFFERS.map((offer, index) => ({
    ...offer, baseMetadata: offer.item.metadata, dimensions: fakeDimensions(offer),
    folderId: offer.folderId ?? null, sortOrder: index,
  })),
  bundles: FAKE_SELLBOT_BUNDLES,
  folders: FAKE_SELLBOT_FOLDERS,
});

const FAKE_HOLOGRAM_RANKS = [
  { name: 'Matze132', count: 312, amount: 46800 },
  { name: 'Fr_x', count: 188, amount: 28200 },
  { name: 'Bonze99', count: 74, amount: 11100 },
];

const FAKE_HOLOGRAM_SCALARS: Record<string, string> = {
  'offer.name': 'Diamant',
  'offer.buyPrice': '4.500',
  'offer.buyCount': '32',
  'offer.sellPrice': '5.000',
  'offer.sellCount': '16',
  'offer.sellItem': '16x Diamant',
  'offer.stock': '192',
  'offer.limitRemaining': '64',
  'offer.ankaufTrades': '18',
  'offer.ankaufFulfilments': '24',
  'offer.ankaufItems': '768',
  'offer.sellItems': '512',
  'offer.totalItems': '1280',
  'offer.lastSeller': 'Matze132',
  'shop.totalPaid': '1.284.500',
  'shop.totalRevenue': '2.106.000',
  'shop.ankaufCount': '87',
  'shop.salesCount': '214',
  'shop.ankaufItems': '24812',
  'shop.sellItems': '18240',
  'shop.totalItems': '43052',
  'window.label': '30 Tage',
  'bot.name': 'AlphaBot',
};

const HOLOGRAM_BUDGET = (pholoName: number) => 100 - `/pholo edit ${pholoName} 1 `.length;

// Scope is per line, so the fixture answers per line too: a preview that ignored the overrides would
// show one scope twice and disagree with what the bot pushes.
const FAKE_HOLOGRAM_RANKS_BY_SOURCE: Record<string, { name: string; count: number; trades: number; amount: number }[]> = {
  OfferAnkauf: FAKE_HOLOGRAM_RANKS.map(r => ({ ...r, trades: 4 })),
  ShopAnkauf: [
    { name: 'Fr_x', count: 5120, trades: 31, amount: 812000 },
    { name: 'Matze132', count: 3980, trades: 24, amount: 604500 },
    { name: 'Bonze99', count: 910, trades: 9, amount: 141000 },
  ],
  ShopVerkauf: [
    { name: 'Steve', count: 640, trades: 18, amount: 96000 },
    { name: 'Alex', count: 320, trades: 11, amount: 52000 },
    { name: 'Matze132', count: 64, trades: 3, amount: 9500 },
  ],
};

FAKE_HOLOGRAM_RANKS_BY_SOURCE.ShopGesamt = [
  ...FAKE_HOLOGRAM_RANKS_BY_SOURCE.ShopAnkauf,
  ...FAKE_HOLOGRAM_RANKS_BY_SOURCE.ShopVerkauf,
].reduce((merged, row) => {
  const same = merged.find(m => m.name.toLowerCase() === row.name.toLowerCase());
  if (same) {
    same.count += row.count;
    same.trades += row.trades;
    same.amount += row.amount;
  } else {
    merged.push({ ...row });
  }
  return merged;
}, [] as { name: string; count: number; trades: number; amount: number }[]);

// Mirrors SellbotHologramData.CustomWindowLabel: whole-day ranges name the last day they cover, and
// anything with a time of day shows the clock.
const fakeWindowLabel = (scope: any): string => {
  const fixed: Record<string, string> = {
    AllTime: 'Gesamt', Days30: '30 Tage', Days7: '7 Tage', Hours24: '24 Stunden', Today: 'Heute',
  };
  if (scope?.window !== 'Custom') return fixed[scope?.window] ?? 'Gesamt';
  if (scope.windowLabelText) return String(scope.windowLabelText);

  const start = scope.windowFrom ? new Date(scope.windowFrom) : null;
  const end = scope.windowTo ? new Date(scope.windowTo) : null;
  const midnight = (d: Date | null) => !d || (d.getHours() === 0 && d.getMinutes() === 0);
  const wholeDays = midnight(start) && midnight(end);

  const moment = (d: Date, isEnd: boolean) => {
    const shown = new Date(d);
    if (wholeDays && isEnd) shown.setDate(shown.getDate() - 1);
    const day = `${String(shown.getDate()).padStart(2, '0')}.${String(shown.getMonth() + 1).padStart(2, '0')}.`;
    if (wholeDays) return day;
    return `${day} ${String(shown.getHours()).padStart(2, '0')}:${String(shown.getMinutes()).padStart(2, '0')}`;
  };

  if (start && end) return `${moment(start, false)} - ${moment(end, true)}`;
  if (start) return `ab ${moment(start, false)}`;
  if (end) return `bis ${moment(end, true)}`;
  return 'Gesamt';
};

function fakeScalarsFor(line: any, hologram: any): Record<string, string> {
  const offerNames: Record<string, string> = {
    'preview-offer-001': 'Sand',
    'preview-offer-002': 'Diamant',
  };
  return {
    ...FAKE_HOLOGRAM_SCALARS,
    ...(line?.sellbotOfferId ? { 'offer.name': offerNames[line.sellbotOfferId] ?? 'Angebot' } : {}),
    'window.label': fakeWindowLabel(line?.window ? line : hologram),
  };
}

const FAKE_HOLOGRAM_MAX_LINES = 3;

// Shop-wide rows, mutated by the exclusion routes below so the list page reacts like the real one.
const FAKE_HOLOGRAM_EXCLUSIONS: { players: { id: string; playerName: string; playerUuid: string | null; createdAt: string }[]; lists: { id: string; name: string; memberCount: number }[] } = {
  players: [
    { id: 'preview-exclusion-001', playerName: 'Bonze99', playerUuid: null, createdAt: '2026-10-02T17:20:00Z' },
  ],
  lists: [],
};

function fakeExcludedNames(body: any): Set<string> {
  const listIds: string[] = [...FAKE_HOLOGRAM_EXCLUSIONS.lists.map(l => l.id), ...(body?.excludedPlayerListIds ?? [])];
  return new Set([
    ...FAKE_HOLOGRAM_EXCLUSIONS.players.map(p => p.playerName),
    ...(body?.excludedPlayers ?? []),
    ...listIds.flatMap(id => (FAKE_PLAYER_LIST_ENTRIES[id] ?? []).map(entry => entry.currentUsername)),
  ].map(name => String(name).toLowerCase()));
}

function renderFakeHologram(body: any): string[] {
  const lines: string[] = [];
  const excluded = fakeExcludedNames(body);
  for (const line of (body?.lines ?? [])) {
    const repeat = Math.max(1, Number(line.repeatCount) || 1);
    const ranked = (FAKE_HOLOGRAM_RANKS_BY_SOURCE[line.rankSource ?? 'OfferAnkauf'] ?? [])
      .filter(rank => !excluded.has(rank.name.toLowerCase()));
    const sortKey = line.rankBy === 'Amount' ? 'amount' : line.rankBy === 'Trades' ? 'trades' : 'count';
    const sorted = [...ranked].sort((a, b) => b[sortKey] - a[sortKey]);
    const scalars = fakeScalarsFor(line, body);

    const firstPosition = Math.max(1, Number(line.rankStart) || 1);
    for (let position = firstPosition; position < firstPosition + repeat; position++) {
      if (lines.length === FAKE_HOLOGRAM_MAX_LINES) return lines;
      const rank = sorted[position - 1];
      const template = rank || repeat <= 1 ? line.template : line.fillerTemplate;
      if (!template) { lines.push(' '); continue; }
      const text = String(template)
        .replace(/\{rank\.position\}/g, String(position))
        .replace(/\{rank\.name\}/g, rank?.name ?? '')
        .replace(/\{rank\.count\}/g, rank ? String(rank.count) : '')
        .replace(/\{rank\.trades\}/g, rank ? String(rank.trades) : '')
        .replace(/\{rank\.amount\}/g, rank ? rank.amount.toLocaleString('de-DE') : '')
        .replace(/\{([a-z.]+)\}/g, (whole, token) => scalars[token] ?? whole);
      const budget = HOLOGRAM_BUDGET(Number(body?.pholoName) || 1);
      lines.push(text.length > budget ? text.slice(0, budget).replace(/&$/, '') : text);
    }
  }
  return lines;
}

const FAKE_SELLBOT_GRANTS = [
  {
    id: 'preview-grant-001', code: 'AB3K9XQ2', label: 'Turnier Platz 1', boundPlayerName: 'Matze132',
    maxRedemptions: 1, oncePerPlayer: false, expiresAt: null, revokedAt: null, customerMessage: 'Glückwunsch zum Sieg!',
    uses: 0, state: 'Active',
    lines: [{ sellbotOfferId: 'preview-offer-002', itemCount: 64 }, { sellbotOfferId: 'preview-offer-001', itemCount: 640 }],
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(),
  },
  {
    id: 'preview-grant-002', code: 'SOMMER24', label: 'Sommeraktion Discord', boundPlayerName: null,
    maxRedemptions: 50, oncePerPlayer: true, expiresAt: new Date(Date.now() + 1000 * 60 * 60 * 24 * 9).toISOString(), revokedAt: null,
    customerMessage: null, uses: 12, state: 'Active',
    lines: [{ sellbotOfferId: 'preview-offer-001', itemCount: 64 }],
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
  },
  {
    id: 'preview-grant-003', code: 'QX7M2PZD', label: 'Bestellung #412', boundPlayerName: 'Fr_x',
    maxRedemptions: 1, oncePerPlayer: false, expiresAt: null, revokedAt: null, customerMessage: null,
    uses: 1, state: 'UsedUp',
    lines: [{ sellbotOfferId: 'preview-offer-002', itemCount: 32 }],
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 6).toISOString(),
  },
];

const FAKE_SELLBOT_GRANT_REDEMPTIONS = [
  { transactionId: 'tx1', playerName: 'Fr_x', status: 'Completed', createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString() },
  { transactionId: 'tx3', playerName: 'Fr_x', status: 'OutOfStock', createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5 - 600000).toISOString() },
];

const FAKE_HOLOGRAMS: any[] = [
  {
    id: 'preview-holo-001', title: 'Diamant Ankauf', pholoName: 1, isEnabled: true, state: 'Active',
    sellbotOfferId: 'preview-offer-002', ankaufPriceStageId: 'preview-stage-002', window: 'Days30',
    combinedOfferIds: ['preview-offer-001'],
    windowFrom: null, windowTo: null, windowLabelText: null,
    serverHoloId: 260174, citybuild: 'CB1', x: -16269, y: 168, z: -14537,
    lastListedAt: new Date(Date.now() - 1000 * 60 * 6).toISOString(),
    lastPushedAt: new Date(Date.now() - 1000 * 60 * 3).toISOString(),
    lastError: null,
    pushedLines: [
      '&c&lDiamant &7&l(30 Tage)',
      '&e&l#1 &fMatze132 &7312x',
      '&e&l#2 &fFr_x &7188x',
    ],
    lines: [
      { id: 'preview-hololine-001', sortOrder: 0, template: '&c&l{offer.name} &7&l({window.label})', repeatCount: 1, fillerTemplate: '', worstCaseLength: 32, sellbotOfferId: null, window: null, rankSource: 'OfferAnkauf', rankBy: 'ItemCount', rankStart: 1, windowFrom: null, windowTo: null, windowLabelText: null, combinedOfferIds: [] },
      { id: 'preview-hololine-002', sortOrder: 1, template: '&e&l#{rank.position} &f{rank.name} &7{rank.count}x', repeatCount: 2, fillerTemplate: '&8#{rank.position} ---', worstCaseLength: 41, sellbotOfferId: 'preview-offer-003', window: null, rankSource: 'OfferAnkauf', rankBy: 'ItemCount', rankStart: 1, windowFrom: null, windowTo: null, windowLabelText: null, combinedOfferIds: ['preview-offer-004'] },
    ],
    exclusions: {
      players: [{ id: 'preview-holo-exclusion-001', playerName: 'TestAlt', playerUuid: null, createdAt: '2026-10-05T09:00:00Z' }],
      lists: [],
    },
  },
  {
    id: 'preview-holo-002', title: 'Preistafel', pholoName: 3, isEnabled: true, state: 'Missing',
    sellbotOfferId: 'preview-offer-001', ankaufPriceStageId: null, window: 'AllTime',
    combinedOfferIds: [],
    serverHoloId: 260170, citybuild: 'CB1', x: -16258, y: 167, z: -14537,
    lastListedAt: new Date(Date.now() - 1000 * 60 * 6).toISOString(),
    lastPushedAt: null,
    lastError: 'Hologramm 3 steht auf CB1 nicht mehr im Spiel.',
    pushedLines: [],
    lines: [
      { id: 'preview-hololine-003', sortOrder: 0, template: '&6&lPreise', repeatCount: 1, fillerTemplate: '', worstCaseLength: 10, sellbotOfferId: null, window: null, rankSource: 'OfferAnkauf', rankBy: 'ItemCount', rankStart: 1, windowFrom: null, windowTo: null, windowLabelText: null, combinedOfferIds: [] },
    ],
    exclusions: { players: [], lists: [] },
  },
];

const FAKE_HOLOGRAM_LIVE = [
  { pholoName: 1, serverId: 260174, x: -16269, y: 168, z: -14537, lines: FAKE_HOLOGRAMS[0].pushedLines, isClaimed: true },
  { pholoName: 2, serverId: 260173, x: -16269, y: 167, z: -14537, lines: ['&f&lNeues Hologramm'], isClaimed: false },
];

const FAKE_HOLOGRAM_BINDINGS = [
  { token: 'rank.position', category: 'Rank', labelKey: 'HOLO_BIND_RANK_POSITION', needsOffer: false },
  { token: 'rank.name', category: 'Rank', labelKey: 'HOLO_BIND_RANK_NAME', needsOffer: false },
  { token: 'rank.count', category: 'Rank', labelKey: 'HOLO_BIND_RANK_COUNT', needsOffer: false },
  { token: 'rank.trades', category: 'Rank', labelKey: 'HOLO_BIND_RANK_TRADES', needsOffer: false },
  { token: 'rank.amount', category: 'Rank', labelKey: 'HOLO_BIND_RANK_AMOUNT', needsOffer: false },
  { token: 'offer.name', category: 'Offer', labelKey: 'HOLO_BIND_OFFER_NAME', needsOffer: true },
  { token: 'offer.buyPrice', category: 'Offer', labelKey: 'HOLO_BIND_OFFER_BUY_PRICE', needsOffer: true },
  { token: 'offer.buyCount', category: 'Offer', labelKey: 'HOLO_BIND_OFFER_BUY_COUNT', needsOffer: true },
  { token: 'offer.sellPrice', category: 'Offer', labelKey: 'HOLO_BIND_OFFER_SELL_PRICE', needsOffer: true },
  { token: 'offer.sellCount', category: 'Offer', labelKey: 'HOLO_BIND_OFFER_SELL_COUNT', needsOffer: true },
  { token: 'offer.sellItem', category: 'Offer', labelKey: 'HOLO_BIND_OFFER_SELL_ITEM', needsOffer: true },
  { token: 'offer.stock', category: 'Stock', labelKey: 'HOLO_BIND_OFFER_STOCK', needsOffer: true },
  { token: 'offer.limitRemaining', category: 'Stock', labelKey: 'HOLO_BIND_OFFER_LIMIT_REMAINING', needsOffer: true },
  { token: 'offer.ankaufFulfilments', category: 'History', labelKey: 'HOLO_BIND_OFFER_ANKAUF_FULFILMENTS', needsOffer: true },
  { token: 'offer.ankaufTrades', category: 'History', labelKey: 'HOLO_BIND_OFFER_ANKAUF_TRADES', needsOffer: true },
  { token: 'offer.ankaufItems', category: 'History', labelKey: 'HOLO_BIND_OFFER_ANKAUF_ITEMS', needsOffer: true },
  { token: 'offer.sellItems', category: 'History', labelKey: 'HOLO_BIND_OFFER_SELL_ITEMS', needsOffer: true },
  { token: 'offer.totalItems', category: 'History', labelKey: 'HOLO_BIND_OFFER_TOTAL_ITEMS', needsOffer: true },
  { token: 'offer.lastSeller', category: 'History', labelKey: 'HOLO_BIND_OFFER_LAST_SELLER', needsOffer: true },
  { token: 'shop.totalPaid', category: 'Shop', labelKey: 'HOLO_BIND_SHOP_TOTAL_PAID', needsOffer: false },
  { token: 'shop.totalRevenue', category: 'Shop', labelKey: 'HOLO_BIND_SHOP_TOTAL_REVENUE', needsOffer: false },
  { token: 'shop.ankaufCount', category: 'Shop', labelKey: 'HOLO_BIND_SHOP_ANKAUF_COUNT', needsOffer: false },
  { token: 'shop.salesCount', category: 'Shop', labelKey: 'HOLO_BIND_SHOP_SALES_COUNT', needsOffer: false },
  { token: 'shop.ankaufItems', category: 'Shop', labelKey: 'HOLO_BIND_SHOP_ANKAUF_ITEMS', needsOffer: false },
  { token: 'shop.sellItems', category: 'Shop', labelKey: 'HOLO_BIND_SHOP_SELL_ITEMS', needsOffer: false },
  { token: 'shop.totalItems', category: 'Shop', labelKey: 'HOLO_BIND_SHOP_TOTAL_ITEMS', needsOffer: false },
  { token: 'window.label', category: 'Meta', labelKey: 'HOLO_BIND_WINDOW_LABEL', needsOffer: false },
  { token: 'bot.name', category: 'Meta', labelKey: 'HOLO_BIND_BOT_NAME', needsOffer: false },
];

const FAKE_SELLBOT_TXNS = [
  { id: 'tx1', payerName: 'Steve', amount: 500, status: 'Completed', reason: 'Sold 64 x 12:0', reasonCode: 'sold', reasonArgsJson: JSON.stringify({ count: 64, item: '12:0' }), occupantName: 'Steve', matchedListingId: 'preview-listing-001', sellbotZoneId: null, createdAt: new Date(Date.now() - 1000 * 60).toISOString(), completedAt: new Date(Date.now() - 50 * 1000).toISOString() },
  { id: 'tx2', payerName: 'Alex',  amount: 1500, status: 'Completed', reason: 'Sold 8 x 264:0', reasonCode: 'sold', reasonArgsJson: JSON.stringify({ count: 8, item: '264:0' }), occupantName: 'Alex', matchedListingId: 'preview-listing-002', sellbotZoneId: null, createdAt: new Date(Date.now() - 1000 * 60 * 4).toISOString(), completedAt: new Date(Date.now() - 1000 * 60 * 3 + 50000).toISOString() },
  { id: 'tx3', payerName: 'Notch', amount: 999, status: 'NoMatch', reason: 'No offer matched the amount; refunded.', reasonCode: 'no_match_amount', reasonArgsJson: JSON.stringify({ amount: 999 }), occupantName: 'Notch', matchedListingId: null, sellbotZoneId: null, createdAt: new Date(Date.now() - 1000 * 60 * 12).toISOString(), completedAt: new Date(Date.now() - 1000 * 60 * 12 + 2000).toISOString() },
  { id: 'tx4', payerName: 'Random', amount: 200, status: 'Ignored', reason: "Payer 'Random' is not the occupant 'Alex'; money kept.", reasonCode: 'payer_mismatch', reasonArgsJson: JSON.stringify({ payer: 'Random', occupant: 'Alex' }), occupantName: 'Alex', matchedListingId: null, sellbotZoneId: null, createdAt: new Date(Date.now() - 1000 * 60 * 20).toISOString(), completedAt: new Date(Date.now() - 1000 * 60 * 20 + 1000).toISOString() },
  // A sold custom/enchanted item, so the detail page's item tooltip can be previewed with real lore/enchants.
  ...Array.from({ length: 80 }, (_, index) => ({
    id: `tx-history-${index}`, payerName: ['Steve', 'Alex', 'Notch', 'Matze132'][index % 4], amount: 500 * (index % 5 + 1), status: index % 9 === 0 ? 'Refunded' : 'Completed', reason: null, reasonCode: 'sold',
    reasonArgsJson: JSON.stringify({ count: 64, item: '12:0' }), occupantName: null, matchedListingId: 'preview-listing-001', sellbotZoneId: null,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * (index + 1)).toISOString(), completedAt: new Date(Date.now() - 1000 * 60 * 60 * (index + 1) + 5000).toISOString(),
  })),
  { id: 'tx5', payerName: 'Alex', amount: 25000, status: 'Completed', reason: 'Sold 1 x 278:0', reasonCode: 'sold', reasonArgsJson: JSON.stringify({ count: 1, item: '278:0', snbt: '{ench:[{id:32,lvl:5}],display:{Name:"§6Mega Pickaxe"}}' }), occupantName: 'Alex', matchedListingId: 'preview-listing-003', sellbotZoneId: null, createdAt: new Date(Date.now() - 1000 * 60 * 6).toISOString(), completedAt: new Date(Date.now() - 1000 * 60 * 6 + 3000).toISOString() },
];

// Mirrors a real GrieferGames "compressed" bulk sale: the seller drops a single compressed block
// (see CompressionIdentity.cs), which decompresses to a huge ItemCount on the recorded transaction.
// One visit, one AnkaufTransaction, one AnkaufTransactionItem line per item kind bought.
const FAKE_SELLBOT_ANKAUF_TXNS = [
  {
    kind: 'ankauf', id: 'atx1', sellerName: 'Lordreider', amountPaid: 1211209.20 + 11742.66, status: 'Completed', reason: null,
    sellbotZoneId: null, createdAt: new Date(Date.now() - 1000 * 60 * 2).toISOString(), completedAt: new Date(Date.now() - 1000 * 60 * 2 + 8000).toISOString(),
    items: [
      // The compressed portion of the take (2040x "Sand II", each worth 81 raw sand = 165240): the
      // offer line carries the raw-equivalent itemCount it was priced on, the stack under it the real
      // compressed block with its own name, lore and physical count.
      { id: 'atxi1', itemKey: '12:0', ankaufTierId: 'preview-ankauftier-006', itemCount: 165240, amountPaid: 1211209.20, itemId: 12, metadata: 0, snbt: null, receivedCount: null, offerStack: { key: '12:0', itemId: 12, metadata: 0, count: 1, snbt: null, nbtHash: null, isCustom: false }, stacks: [
        { itemId: 12, metadata: 0, snbt: '{compressionLevel:"TWO",stackSize:81,currentAmount:81,warningDisplayed:0b,display:{Name:"§eSand II",Lore:["§7Anzahl: 81"]}}', count: 2040 },
      ] },
      // The loose (uncompressed) remainder of the same take - plain sand, so the row stays one line.
      { id: 'atxi2', itemKey: '12:0', ankaufTierId: 'preview-ankauftier-006', itemCount: 1602, amountPaid: 11742.66, itemId: 12, metadata: 0, snbt: null, receivedCount: null, offerStack: { key: '12:0', itemId: 12, metadata: 0, count: 1, snbt: null, nbtHash: null, isCustom: false }, stacks: [
        { itemId: 12, metadata: 0, snbt: null, count: 1602 },
      ] },
    ],
  },
  // One offer matched by several differently named custom stacks: the listing names the offer, then
  // every real stack under it.
  {
    kind: 'ankauf', id: 'atx4', sellerName: 'Matze132', amountPaid: 285000, status: 'Completed', reason: null,
    sellbotZoneId: null, createdAt: new Date(Date.now() - 1000 * 60 * 21).toISOString(), completedAt: new Date(Date.now() - 1000 * 60 * 21 + 6000).toISOString(),
    items: [
      // The offer's rule preview keeps the open-slot markers (any signing date), the stacks under it
      // carry the dates the two real items were actually signed on.
      { id: 'atxi4', itemKey: '160:14:dbedaa3f', ankaufTierId: 'preview-ankauftier-008', itemCount: 21, amountPaid: 285000, itemId: 160, metadata: 14, snbt: null, receivedCount: null, offerStack: { key: '160:14', itemId: 160, metadata: 14, count: 1, snbt: '{display:{Name:"§b✛ §f§8? §b✛",Lore:["§7Signiert von §aWaechtersino §7am §e??.??.????"]},RepairCost:1}', nbtHash: 'dbedaa3f', isCustom: true }, stacks: [
        { itemId: 160, metadata: 14, snbt: '{display:{Name:"§b✛ §f15K §b✛",Lore:["§615k$ Gewinn von §e/p h WaechterSino","§7Signiert von §aWaechtersino §7am §e21.08.2026"]},RepairCost:1}', count: 12 },
        { itemId: 160, metadata: 14, snbt: '{display:{Name:"§b✛ §f10K §b✛",Lore:["§610k$ Gewinn von §e/p h WaechterSino","§7Signiert von §aWaechtersino §7am §e19.08.2026"]},RepairCost:1}', count: 9 },
      ] },
    ],
  },
  // A bought custom/enchanted item, to preview the full BAB-style tooltip (enchants, lore, name) on the ankauf side too.
  {
    kind: 'ankauf', id: 'atx3', sellerName: 'Steve', amountPaid: 20000, status: 'Completed', reason: null,
    sellbotZoneId: null, createdAt: new Date(Date.now() - 1000 * 60 * 9).toISOString(), completedAt: new Date(Date.now() - 1000 * 60 * 9 + 5000).toISOString(),
    items: [
      { id: 'atxi3', itemKey: '278:0:enchpick', ankaufTierId: 'preview-ankauftier-007', itemCount: 1, amountPaid: 20000, itemId: 278, metadata: 0, snbt: null, receivedCount: null, offerStack: { key: '278:0', itemId: 278, metadata: 0, count: 1, snbt: '{ench:[{id:32,lvl:5}],display:{Name:"§6Mega Pickaxe"}}', nbtHash: 'enchpick', isCustom: true }, stacks: [
        { itemId: 278, metadata: 0, snbt: '{ench:[{id:32,lvl:5}],display:{Name:"§6Mega Pickaxe"}}', count: 1 },
      ] },
    ],
  },
];

const FAKE_SELLBOT_ENTITY_DUMP = [
  'Sell area [100,64,-20] - [104,66,-16], chamber area [90,64,-30] - [110,70,-10]',
  "Bot entity 421 at 102.5 64 -18.5, 0 blocks outside the sell area, home '$Shop' 102.5 64 -18.5, at home True",
  'Tab list holds 87 entries, entity tracker holds 3 entities',
  "Player type 0 entity 154 name '§6Titan §8┃ §6Steve' uuid 8b1e5c0a-1f2d-4a77-9f31-0c5a6d2e7b44 tab 'Steve' display '§6Titan §8┃ §6Steve' at 106.312 64 -18.044 sellArea 2 blocks outside chamber inside yaw 128 pitch 3 headYaw 130 velocity 0/0/0 vehicle -1 leash -1 status -1 objectData 0 facing 0 orb 0 metadata 12B attributes 0B equipment 5 effects 0",
  "Player type 0 entity 158 name '' uuid none tab '' display '' at 102.7 64 -17.9 sellArea inside chamber inside yaw 64 pitch 0 headYaw 64 velocity 0/0/0 vehicle -1 leash -1 status -1 objectData 0 facing 0 orb 0 metadata 0B attributes 0B equipment 0 effects 0",
  "Object type 71 entity 200 name '' uuid none tab '' display '' at 99 65 -21 sellArea 1 blocks outside chamber inside yaw 0 pitch 0 headYaw 0 velocity 0/0/0 vehicle -1 leash -1 status -1 objectData 3 facing 1 orb 0 metadata 0B attributes 0B equipment 0 effects 0",
].join('\n');

const FAKE_SELLBOT_ACTIVITY = [
  { id: 'a7', level: 'Warning', type: 'ankauf_no_occupant', message: 'No player in the sell area [100,64,-20] - [104,66,-16]', reasonCode: 'ankauf_no_occupant', reasonArgsJson: null, playerName: null, transactionId: null, sellbotZoneId: null, createdAt: new Date(Date.now() - 1000 * 40).toISOString() },
  { id: 'a8', level: 'Debug', type: 'sell_area_entity_dump', message: FAKE_SELLBOT_ENTITY_DUMP, reasonCode: null, reasonArgsJson: null, playerName: null, transactionId: null, sellbotZoneId: null, createdAt: new Date(Date.now() - 1000 * 40).toISOString() },
  { id: 'a1', level: 'Info', type: 'sale_completed', message: 'Sold 64 x sand to Steve.', reasonCode: 'sold', reasonArgsJson: JSON.stringify({ count: 64, item: '12:0' }), playerName: 'Steve', transactionId: 'tx1', sellbotZoneId: null, createdAt: new Date(Date.now() - 1000 * 50).toISOString() },
  { id: 'a5', level: 'Info', type: 'ankauf_occupant_left_at_drop', message: 'Seller Notch left the sell area before 192x 12:0, 1x 278:0 could be returned; items diverted.', reasonCode: 'ankauf_occupant_left_at_drop', reasonArgsJson: JSON.stringify({ items: [{ item: '12:0', count: 192, snbt: null }, { item: '278:0', count: 1, snbt: '{ench:[{id:32,lvl:5}],display:{Name:"§6Mega Pickaxe"}}' }] }), playerName: 'Notch', transactionId: null, sellbotZoneId: null, createdAt: new Date(Date.now() - 1000 * 60).toISOString() },
  // Trash deposits written before the reason payloads converged, so the id and the damage arrive split.
  { id: 'a6', level: 'Warning', type: 'ankauf_occupant_left_uncollected', message: '12x 264:0 owed to Alex could not be returned.', reasonCode: 'occupant_left_uncollected', reasonArgsJson: JSON.stringify({ expected: 'Alex', items: [{ itemId: 264, damage: 0, count: 12 }] }), playerName: 'Alex', transactionId: null, sellbotZoneId: null, createdAt: new Date(Date.now() - 1000 * 65).toISOString() },
  { id: 'a2', level: 'Debug', type: 'player_entered_area', message: null, reasonCode: null, reasonArgsJson: null, playerName: 'Steve', transactionId: null, sellbotZoneId: null, createdAt: new Date(Date.now() - 1000 * 70).toISOString() },
  { id: 'a3', level: 'Warning', type: 'sale_payer_mismatch', message: 'Payer Random is not the occupant Alex; money kept.', reasonCode: 'payer_mismatch', reasonArgsJson: JSON.stringify({ payer: 'Random', occupant: 'Alex' }), playerName: 'Random', transactionId: 'tx4', sellbotZoneId: null, createdAt: new Date(Date.now() - 1000 * 60 * 20).toISOString() },
  { id: 'a4', level: 'Info', type: 'payment_received', message: 'Alex paid 1500.', reasonCode: 'payment_received', reasonArgsJson: JSON.stringify({ amount: 1500 }), playerName: 'Alex', transactionId: 'tx2', sellbotZoneId: null, createdAt: new Date(Date.now() - 1000 * 60 * 4).toISOString() },
];

const FAKE_SELLBOT_OVERVIEW = {
  hasConfig: true,
  isEnabled: true,
  online: true,
  totalRevenue: 25400,
  sales: 12,
  refunds: 2,
  outOfStock: 0,
  ignored: 1,
  offerCount: 3,
  outOfStockOffers: 0,
  recentTransactions: FAKE_SELLBOT_TXNS.slice(0, 4),
  recentActivity: FAKE_SELLBOT_ACTIVITY.slice(0, 4),
};

const FAKE_FEATURE_FLAG_OVERRIDES: any[] = [];

const FAKE_SEED_CRACK_WORLDS = [
  {
    key: '128|-512|6000',
    citybuild: 'CB7',
    dimension: 0,
    serverAddress: 'cubecraft.net',
    centerX: 128,
    centerZ: -512,
    diameter: 6000,
    isGlitch: false,
    structureCount: 142,
    dungeonCount: 9,
    usableDungeonCount: 7,
    fortressCount: 0,
    biomeCount: 27,
    status: 3,
    estimatedCandidates: 1,
    crackedSeed: 0,
    crackedSeedText: '-4823197756241039284',
    statusMessage: 'Recovered the full seed from 7 dungeon floor(s) and 27 biome(s).',
    etaSeconds: null,
    contributorCount: 14,
    firstSeenAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 12).toISOString(),
    lastSeenAt: new Date(Date.now() - 1000 * 60 * 6).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 6).toISOString(),
  },
  {
    key: '-64|256|8000',
    citybuild: '',
    dimension: 0,
    serverAddress: 'cubecraft.net',
    centerX: -64,
    centerZ: 256,
    diameter: 8000,
    isGlitch: true,
    structureCount: 38,
    dungeonCount: 0,
    usableDungeonCount: 0,
    fortressCount: 0,
    biomeCount: 11,
    status: 0,
    estimatedCandidates: 184000000000,
    crackedSeed: null,
    crackedSeedText: null,
    statusMessage: 'No dungeon yet. 184,000,000,000 candidate seed(s) from 38 structure(s) and 11 biome(s). A single dungeon would crack it directly.',
    etaSeconds: 999999999,
    contributorCount: 5,
    firstSeenAt: new Date(Date.now() - 1000 * 60 * 60 * 36).toISOString(),
    lastSeenAt: new Date(Date.now() - 1000 * 60 * 2).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 2).toISOString(),
  },
  {
    key: '0|0|4000',
    citybuild: 'CB3',
    dimension: -1,
    serverAddress: 'cubecraft.net',
    centerX: 0,
    centerZ: 0,
    diameter: 4000,
    isGlitch: false,
    structureCount: 71,
    dungeonCount: 4,
    usableDungeonCount: 4,
    fortressCount: 4,
    biomeCount: 18,
    status: 2,
    estimatedCandidates: 49,
    crackedSeed: null,
    crackedSeedText: null,
    statusMessage: 'No seed candidate from 4 dungeon floor(s): the floors are likely occluded or in lake-population chunks. A cleaner or different dungeon is needed.',
    etaSeconds: 185,
    contributorCount: 9,
    // A Nether world mid-sweep, with one fortress read and two only located. Both row states have to render,
    // because telling them apart is the entire point of the panel.
    diagnostics: {
      // Each row carries the note from its last read attempt, because the three outcomes need different answers: read,
      // read but too ambiguous, and a start chunk that was never a fortress.
      fortresses: [
        {
          chunkX: 74, chunkZ: 4, regionX: 4, regionZ: 0,
          hasLayout: true, layoutPieces: 96, layoutCertainPieces: 85,
          readNote: 'Attempt 3/4, 187 chunks loaded nearby: 96 pieces read, 85 unambiguous, 18 site(s) abandoned, facing East',
        },
        {
          chunkX: 138, chunkZ: 9, regionX: 8, regionZ: 0,
          hasLayout: false, layoutPieces: 0, layoutCertainPieces: 0,
          readNote: 'Attempt 2/4, 63 chunks loaded nearby: 14 pieces read, 6 unambiguous, 21 site(s) abandoned; 20 unambiguous are needed to identify a seed',
        },
        // Two starts claimed for region 4/-6, which a real capture produced: the detector named different chunks on
        // different bot runs, so one of them is a misdetection - and the notes are what say which.
        {
          chunkX: 73, chunkZ: -86, regionX: 4, regionZ: -6,
          hasLayout: false, layoutPieces: 0, layoutCertainPieces: 0,
          readNote: 'Attempt 1/4, 44 chunks loaded nearby: no height fits the start crossing, so its chunks are not loaded yet',
        },
        {
          chunkX: 75, chunkZ: -86, regionX: 4, regionZ: -6,
          hasLayout: false, layoutPieces: 0, layoutCertainPieces: 0,
          readNote: null,
        },
      ],
      fortressesWithLayout: 1,
      sweepCursor: 4_611_686_018_427_388,
      sweepTotalStates: 35_184_372_088_832,
      sweepPercent: 13.11,
      sweepCandidates: 0,
      sweptLayoutSize: 2604,
      explanation: '1 of 4 fortress(es) have a layout. Sweeping: 13.11% of the driver space, and it stops at the first hit. A second fortress with a start chunk makes this about 2.8x faster, since it keeps candidates away from the expensive layout check. 1 region(s) have more than one recorded start chunk, and a region can only hold one, so at least one of each pair is a misdetection; the sweep keeps the one that read a layout and drops the rest.',
    },
    firstSeenAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 4).toISOString(),
    lastSeenAt: new Date(Date.now() - 1000 * 30).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 30).toISOString(),
  },
];

const FAKE_PROFILE_HISTORY = () => {
  const now = Date.now();
  const n = 200;
  return Array.from({ length: n }, (_, i) => {
    const t = now - (n - 1 - i) * 7 * 24 * 60 * 60 * 1000 / (n - 1);
    const wave = Math.sin(i / 12) * 0.5 + 0.5;
    return {
      id: 'snap-' + i,
      botRegistryId: BOT1,
      cash: Math.round(50000 + wave * 40000 + i * 120),
      bankBalance: Math.round(1_000_000 + i * 5000 + wave * 200000),
      orbs: Math.round(2_000_000 + wave * 400_000),
      orbsObservedAt: new Date(t - 5 * 60 * 1000).toISOString(),
      kristalle: 500 + Math.round(wave * 150),
      kristalleObservedAt: new Date(t - 12 * 60 * 1000).toISOString(),
      hoursPlayed: 120 + i * 0.05,
      citybuild: 3,
      currentPlayers: Math.round(80 + wave * 120),
      maxPlayers: 300,
      createdAt: new Date(t).toISOString(),
    };
  });
};

const FAKE_ORB_SHOP_CATALOG = [
  { key: 'flugtrank', category: 0, label: 'Flugtrank', itemId: 373, damage: 2, price: 350_000 },
  { key: 'abbautrank', category: 0, label: 'Abbautrank', itemId: 373, damage: 1, price: 350_000 },
  { key: 'faules_ei', category: 0, label: 'Faules Ei', itemId: 344, damage: 0, price: 50_000 },
  { key: 'lager_upgrade_4', category: 0, label: 'Lager Upgrade IV', itemId: 397, damage: 3, price: 40_000_000 },
  { key: 'orb_helm', category: 1, label: 'Orb-Helm', itemId: 310, damage: 0, price: 7_500_000 },
  { key: 'orb_schuhe', category: 1, label: 'Orb-Schuhe', itemId: 313, damage: 0, price: 5_000_000 },
  { key: 'sammler_spitzhacke', category: 2, label: 'Sammler-Spitzhacke', itemId: 278, damage: 0, price: 30_000_000 },
  { key: 'farming_spitzhacke', category: 2, label: 'Farming-Spitzhacke', itemId: 278, damage: 0, price: 5_000_000 },
];

const FAKE_ORBS_TRADER_CONFIGS = [
  {
    id: 'o0000000-0000-0000-0000-000000000001',
    name: 'Flugtränke',
    storageId: STORAGE1,
    citybuild: null,
    lines: [{ productKey: 'flugtrank', count: 4 }, { productKey: 'faules_ei', count: 2 }],
    lastRunAt: new Date(Date.now() - 1000 * 60 * 42).toISOString(),
    lastRun: {
      startedAt: new Date(Date.now() - 1000 * 60 * 42).toISOString(),
      finishedAt: new Date(Date.now() - 1000 * 60 * 40).toISOString(),
      lines: [
        { productKey: 'flugtrank', requested: 4, bought: 4, outcome: 1, livePrice: null },
        { productKey: 'faules_ei', requested: 2, bought: 0, outcome: 3, livePrice: 60_000 },
      ],
      orbsSpent: 1_400_000,
      depositFailed: false,
    },
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
  },
  {
    id: 'o0000000-0000-0000-0000-000000000002',
    name: 'Ausrüstung',
    storageId: STORAGE1,
    citybuild: 4,
    lines: [{ productKey: 'sammler_spitzhacke', count: 1 }, { productKey: 'orb_helm', count: 1 }],
    lastRunAt: null,
    lastRun: null,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
  },
];

const FAKE_SPAWNER_UPGRADE_CONFIGS = [
  {
    id: 's0000000-0000-0000-0000-000000000001',
    name: 'Schwein-Spawner',
    citybuild: 1, citybuildName: 'CitybuildA', location: 'p h spawner',
    range: 40, sourceStorageId: STORAGE1, isEnabled: true,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
  },
];

const FAKE_KNOWN_SPAWNERS = [
  {
    id: 'k0000000-0000-0000-0000-000000000001', serverHost: 'griefergames.net', citybuild: 1, citybuildName: 'CitybuildA',
    x: 14674, y: 65, z: 3840, mobName: 'Schwein', settingsAccessDenied: false, upgradeOneCompleted: false,
    upgradeItemKey: '319:0', upgradeRequired: 6400, upgradeDelivered: 3904, dropLevel: 0, storageLevel: 0, xpLevel: 0,
    lastSeenAt: new Date(Date.now() - 1000 * 60 * 4).toISOString(),
  },
  {
    id: 'k0000000-0000-0000-0000-000000000002', serverHost: 'griefergames.net', citybuild: 1, citybuildName: 'CitybuildA',
    x: 14687, y: 65, z: 3875, mobName: 'Schwein', settingsAccessDenied: false, upgradeOneCompleted: true,
    upgradeItemKey: '319:0', upgradeRequired: 6400, upgradeDelivered: 6400, dropLevel: 1, storageLevel: 1, xpLevel: 1,
    lastSeenAt: new Date(Date.now() - 1000 * 60 * 6).toISOString(),
  },
  {
    id: 'k0000000-0000-0000-0000-000000000003', serverHost: 'griefergames.net', citybuild: 1, citybuildName: 'CitybuildA',
    x: 14674, y: 66, z: 3840, mobName: '', settingsAccessDenied: true, upgradeOneCompleted: false,
    upgradeItemKey: '', upgradeRequired: 0, upgradeDelivered: 0, dropLevel: 0, storageLevel: 0, xpLevel: 0,
    lastSeenAt: new Date(Date.now() - 1000 * 60 * 7).toISOString(),
  },
];

const FAKE_POKEBALL_CONFIGS = [
  {
    id: 'p0000000-0000-0000-0000-000000000001',
    name: 'Spinnenfarm',
    citybuild: 1, citybuildName: 'CitybuildA', location: 'p h spinnen',
    mobType: 52, captureThreshold: 1000, radius: 20,
    mode: 0, storageId: STORAGE1,
    minFreeSlots: 4, emptyBallsToCarry: 16, isEnabled: true, capturedMobs: 18_420, capturedMobsThisMonth: 4_210,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
  },
  {
    id: 'p0000000-0000-0000-0000-000000000002',
    name: 'Skelette /ec',
    citybuild: 1, citybuildName: 'CitybuildA', location: 'p h skelette',
    mobType: 51, captureThreshold: 500, radius: 16,
    mode: 1, storageId: '00000000-0000-0000-0000-000000000000',
    minFreeSlots: 2, emptyBallsToCarry: 16, isEnabled: false, capturedMobs: 3_150, capturedMobsThisMonth: 0,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(),
  },
];

const FAKE_CROP_FIELDS = [
  {
    id: 'c0000000-0000-0000-0000-000000000001',
    name: 'Weizenfeld Nord',
    citybuild: 1, citybuildName: 'CitybuildA', location: 'p 4',
    x1: 100, y1: 64, z1: -40, x2: 147, y2: 64, z2: -9,
    lastScannedAt: new Date(Date.now() - 3 * 3600_000).toISOString(),
    farmlandCount: 1408, soulSandCount: 96, emptyCount: 212, matureCount: 840, revertedCount: 7,
    wheatCount: 900, carrotCount: 180, potatoCount: 120, melonCount: 32, pumpkinCount: 28, netherWartCount: 32,
    harvestConfigs: [
      { id: 'c1000000-0000-0000-0000-000000000001', fieldId: 'c0000000-0000-0000-0000-000000000001', name: 'Ernten und nachpflanzen', mode: 1, unloadStorageId: STORAGE1, minFreeSlots: 4, compress: true, isEnabled: true },
    ],
    plantConfigs: [
      { id: 'c2000000-0000-0000-0000-000000000001', fieldId: 'c0000000-0000-0000-0000-000000000001', name: 'Standardmix', sourceStorageId: STORAGE1, wheatPercent: 60, carrotPercent: 20, potatoPercent: 10, melonPercent: 5, pumpkinPercent: 5, plantNetherWart: true, isEnabled: true },
    ],
    createdAt: new Date(Date.now() - 5 * 86400_000).toISOString(),
  },
  {
    id: 'c0000000-0000-0000-0000-000000000002',
    name: 'Karottenacker',
    citybuild: 2, citybuildName: 'CitybuildB', location: '',
    x1: -20, y1: 70, z1: 5, x2: 11, y2: 70, z2: 36,
    lastScannedAt: null,
    farmlandCount: 0, soulSandCount: 0, emptyCount: 0, matureCount: 0, revertedCount: 0,
    wheatCount: 0, carrotCount: 0, potatoCount: 0, melonCount: 0, pumpkinCount: 0, netherWartCount: 0,
    harvestConfigs: [], plantConfigs: [],
    createdAt: new Date(Date.now() - 86400_000).toISOString(),
  },
];

const VILLAGER_HALL1 = 'v0000000-0000-0000-0000-000000000001';
const POWER_FIVE_BOOK_SNBT = '{StoredEnchantments:[{lvl:5s,id:48s}]}';
const villagerItem = (itemId: number, count: number, snbt: string | null = null, nbtHash: string | null = null) =>
  ({ key: nbtHash ? `${itemId}:0:${nbtHash}` : `${itemId}:0`, itemId, metadata: 0, count, snbt, nbtHash, isCustom: false });
const villagerOffer = (index: number, buyFirst: any, buySecond: any, sell: any, uses: number, maxUses = 7) => ({
  index, offerKey: buySecond ? `${buyFirst.key}+${buySecond.key}>${sell.key}` : `${buyFirst.key}>${sell.key}`,
  buyFirst, buySecond, sell, uses, maxUses, isLocked: uses >= maxUses,
});
const PAPER_FOR_EMERALD_KEY = '339:0>388:0';
const POWER_FIVE_KEY = '340:0+388:0>403:0:5f1c9a';

const FAKE_VILLAGER_HALLS = [
  {
    id: VILLAGER_HALL1,
    name: 'Bibliothekare',
    citybuild: 1, citybuildName: 'CitybuildA', location: 'p h villager',
    x1: 220, y1: 64, z1: 1120, x2: 236, y2: 66, z2: 1130,
    lastScannedAt: new Date(Date.now() - 2 * 3600_000).toISOString(),
    villagerCount: 3, offerCount: 9,
    tradeConfigs: [
      {
        id: 'v1000000-0000-0000-0000-000000000001', hallId: VILLAGER_HALL1, name: 'Power V Bücher',
        sourceStorageId: STORAGE1, targetStorageId: STORAGE2,
        lines: [{ offerKey: POWER_FIVE_KEY, amount: 8 }], fillerOfferKey: PAPER_FOR_EMERALD_KEY,
        maxUnlockAttempts: 5, minFreeSlots: 4, isEnabled: true,
        lastRunAt: new Date(Date.now() - 40 * 60_000).toISOString(),
        lastRun: {
          startedAt: new Date(Date.now() - 40 * 60_000).toISOString(), finishedAt: new Date(Date.now() - 31 * 60_000).toISOString(),
          lines: [{ offerKey: POWER_FIVE_KEY, requested: 8, received: 6, outcome: 4 }], unlocks: 2, depositFailed: false,
        },
      },
    ],
    createdAt: new Date(Date.now() - 3 * 86400_000).toISOString(),
  },
  {
    id: 'v0000000-0000-0000-0000-000000000002',
    name: 'Schmiede',
    citybuild: 2, citybuildName: 'CitybuildB', location: '',
    x1: -40, y1: 70, z1: 12, x2: -28, y2: 72, z2: 30,
    lastScannedAt: null, villagerCount: 0, offerCount: 0, tradeConfigs: [],
    createdAt: new Date(Date.now() - 86400_000).toISOString(),
  },
];

const FAKE_HALL_VILLAGERS = [
  {
    id: 'v2000000-0000-0000-0000-000000000001', x: 222, y: 64, z: 1121, profession: 1, career: 'entity.Villager.librarian', customName: null, isMissing: false,
    offers: [
      villagerOffer(0, villagerItem(339, 27), undefined, villagerItem(388, 1), 3),
      villagerOffer(1, villagerItem(340, 1), villagerItem(388, 38), villagerItem(403, 1, POWER_FIVE_BOOK_SNBT, '5f1c9a'), 7),
      villagerOffer(2, villagerItem(340, 8), undefined, villagerItem(388, 1), 0),
      villagerOffer(3, villagerItem(388, 11), undefined, villagerItem(345, 1), 0),
    ],
    lastScannedAt: new Date(Date.now() - 2 * 3600_000).toISOString(),
  },
  {
    id: 'v2000000-0000-0000-0000-000000000002', x: 224, y: 64, z: 1121, profession: 1, career: null, customName: 'Dorfbewohner', isMissing: false,
    offers: [
      villagerOffer(0, villagerItem(339, 25), undefined, villagerItem(388, 1), 0),
      villagerOffer(1, villagerItem(340, 1), villagerItem(388, 21), villagerItem(403, 1, POWER_FIVE_BOOK_SNBT, '5f1c9a'), 2),
      villagerOffer(2, villagerItem(388, 4), undefined, villagerItem(47, 1), 0),
      villagerOffer(3, villagerItem(340, 1), villagerItem(388, 44), villagerItem(403, 1, '{StoredEnchantments:[{lvl:5s,id:16s}]}', '9b2e41'), 0),
    ],
    lastScannedAt: new Date(Date.now() - 2 * 3600_000).toISOString(),
  },
  {
    id: 'v2000000-0000-0000-0000-000000000003', x: 226, y: 64, z: 1121, profession: 2, career: 'entity.Villager.cleric', customName: null, isMissing: true,
    offers: [
      villagerOffer(0, villagerItem(367, 38), undefined, villagerItem(388, 1), 0),
      villagerOffer(1, villagerItem(266, 8), undefined, villagerItem(388, 1), 0),
    ],
    lastScannedAt: new Date(Date.now() - 26 * 3600_000).toISOString(),
  },
];

function apiOk(value: unknown): Response {
  return new Response(JSON.stringify({ status: 200, value }), {
    status: 200,
    headers: { 'Content-Type': 'application/json' },
  });
}

// HTTP 200 with an envelope status of 400: sfetch takes the status from the envelope, which is how
// the real backend's ApiResponse.Typed.BadRequest reaches a call site.
function apiError(field: string, message: string): Response {
  return new Response(JSON.stringify({ status: 400, errors: { [field]: [message] } }), {
    status: 200,
    headers: { 'Content-Type': 'application/json' },
  });
}

function apiNotFound(message: string): Response {
  return new Response(JSON.stringify({ status: 404, errors: { resource: [message] } }), {
    status: 200,
    headers: { 'Content-Type': 'application/json' },
  });
}

function rawOk(data: unknown): Response {
  return new Response(JSON.stringify(data), {
    status: 200,
    headers: { 'Content-Type': 'application/json' },
  });
}

const FAKE_FARM_CONFIGS = [
  {
    id: 'c1', name: 'Holzfarm Nature', citybuild: 22, citybuildName: 'Nature', mode: 1, wanderResumeStaleAfterHours: 12,
    location: '', blockIdentifiers: ['log', 'log2'], searchRadius: 32, unloadStorageId: STORAGE1,
    everythingExceptRepairable: true, itemIdentifierFilter: [], minFreeSlots: 4, compress: false,
    fetchLeftoverDrops: false, isEnabled: true, createdAt: '2026-10-01T10:00:00Z', minY: null, maxY: null,
  },
  {
    id: 'c2', name: 'Sandwüste', citybuild: 0, citybuildName: 'CB1', mode: 0, wanderResumeStaleAfterHours: 12,
    location: '/home sand', blockIdentifiers: ['sand'], searchRadius: 48, unloadStorageId: STORAGE1,
    everythingExceptRepairable: true, itemIdentifierFilter: [], minFreeSlots: 4, compress: true,
    fetchLeftoverDrops: true, isEnabled: true, createdAt: '2026-09-20T14:30:00Z', minY: 60, maxY: 80,
  },
];

const FAKE_FARM_RECORDINGS = [
  {
    id: 'd1000000-0000-0000-0000-000000000001', configId: 'c1', configName: 'Sand Desert', botId: BOT1, botUsername: 'FarmerBot42',
    startedAt: '2026-06-18T08:12:00Z', endedAt: '2026-06-18T09:48:00Z', endReason: 'Cancelled',
    blocksMined: 4123, depositCount: 7, pickupRuns: 21, pathfindFailures: 38, reachFailures: 12, breakTimeouts: 4, wanderEvents: 9,
    longestStallMs: 41000, totalStallMs: 186000,
  },
  {
    id: 'd1000000-0000-0000-0000-000000000002', configId: 'c2', configName: 'Cactus Row', botId: BOT1, botUsername: 'FarmerBot42',
    startedAt: '2026-06-17T14:02:00Z', endedAt: '2026-06-17T14:31:00Z', endReason: 'StorageFull',
    blocksMined: 980, depositCount: 2, pickupRuns: 5, pathfindFailures: 3, reachFailures: 1, breakTimeouts: 0, wanderEvents: 1,
    longestStallMs: 7000, totalStallMs: 14000,
  },
  {
    id: 'd1000000-0000-0000-0000-000000000003', configId: 'c1', configName: 'Sand Desert', botId: BOT1, botUsername: 'FarmerBot42',
    startedAt: '2026-06-16T11:00:00Z', endedAt: '2026-06-16T13:20:00Z', endReason: 'Completed',
    blocksMined: 6201, depositCount: 11, pickupRuns: 30, pathfindFailures: 5, reachFailures: 2, breakTimeouts: 1, wanderEvents: 2,
    longestStallMs: 6000, totalStallMs: 9000,
  },
];

const FAKE_TRACKED_PROFILES = [
  { trackedMinecraftProfileId: 'f1000000-0000-0000-0000-000000000001', uuid: '069a79f4-44e9-4726-a5be-fca90e38aaf5', currentUsername: 'Notch' },
  { trackedMinecraftProfileId: 'f1000000-0000-0000-0000-000000000002', uuid: 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa', currentUsername: 'AlphaBot' },
  { trackedMinecraftProfileId: 'f1000000-0000-0000-0000-000000000003', uuid: 'bbbbbbbb-bbbb-bbbb-bbbb-bbbbbbbbbbbb', currentUsername: 'BetaBot' },
];

const FAKE_EVENT_CATALOG = [
  {
    key: 'payment_received', category: 'economy', plugin: 'PaymentDetection', observeOnly: false, pluginEnabled: true,
    parameters: [
      { key: 'payer', type: 'string', options: null },
      { key: 'amount', type: 'number', options: null },
      { key: 'confirmed', type: 'boolean', options: null },
    ],
  },
  {
    key: 'cash_changed', category: 'economy', plugin: 'PaymentDetection', observeOnly: false, pluginEnabled: true,
    parameters: [
      { key: 'previous', type: 'number', options: null },
      { key: 'current', type: 'number', options: null },
      { key: 'delta', type: 'number', options: null },
    ],
  },
  {
    key: 'pre_sell_transaction', category: 'economy', plugin: 'Sellbot', observeOnly: true, pluginEnabled: false,
    parameters: [
      { key: 'payerName', type: 'string', options: null },
      { key: 'amount', type: 'number', options: null },
      { key: 'isGranted', type: 'boolean', options: null },
    ],
  },
  {
    key: 'sell_item_sold', category: 'economy', plugin: 'Sellbot', observeOnly: false, pluginEnabled: true,
    parameters: [
      { key: 'payerName', type: 'string', options: null },
      { key: 'offerId', type: 'string', options: null, optionSource: 'SellbotOffers' },
      { key: 'itemKey', type: 'string', options: null },
      { key: 'offerName', type: 'string', options: null },
      { key: 'count', type: 'number', options: null },
      { key: 'isGranted', type: 'boolean', options: null },
    ],
  },
  {
    key: 'chat_received', category: 'chat', plugin: null, observeOnly: false, pluginEnabled: true,
    parameters: [{ key: 'message', type: 'string', options: null }],
  },
  {
    key: 'private_message_received', category: 'chat', plugin: 'PrivateMessage', observeOnly: false, pluginEnabled: true,
    parameters: [
      { key: 'contactName', type: 'string', options: null },
      { key: 'body', type: 'string', options: null },
      { key: 'isOutgoing', type: 'boolean', options: null },
    ],
  },
  {
    key: 'connected', category: 'bot', plugin: null, observeOnly: false, pluginEnabled: true,
    parameters: [
      { key: 'server', type: 'string', options: null },
      { key: 'port', type: 'number', options: null },
      { key: 'username', type: 'string', options: null },
    ],
  },
  {
    key: 'kicked', category: 'bot', plugin: null, observeOnly: false, pluginEnabled: true,
    parameters: [{ key: 'reason', type: 'string', options: null }],
  },
  {
    key: 'reconnecting', category: 'bot', plugin: null, observeOnly: false, pluginEnabled: true,
    parameters: [
      { key: 'attempt', type: 'number', options: null },
      { key: 'delaySeconds', type: 'number', options: null },
    ],
  },
  {
    key: 'bot_died', category: 'bot', plugin: null, observeOnly: false, pluginEnabled: true,
    parameters: [
      { key: 'x', type: 'number', options: null },
      { key: 'y', type: 'number', options: null },
      { key: 'z', type: 'number', options: null },
    ],
  },
  {
    key: 'self_shutdown', category: 'bot', plugin: null, observeOnly: false, pluginEnabled: true,
    parameters: [
      { key: 'reason', type: 'enum', options: ['ServerBan', 'ServerJail', 'StaffKick', 'BlacklistedPlayerSighted', 'BlacklistedPlayerMessaged'] },
      { key: 'detail', type: 'string', options: null },
    ],
  },
  {
    key: 'possession_started', category: 'bot', plugin: null, observeOnly: false, pluginEnabled: true,
    parameters: [
      { key: 'possessor', type: 'string', options: null },
      { key: 'mode', type: 'enum', options: ['ReadOnly', 'FullControl'] },
    ],
  },
  {
    key: 'possession_ended', category: 'bot', plugin: null, observeOnly: false, pluginEnabled: true,
    parameters: [
      { key: 'possessor', type: 'string', options: null },
      { key: 'mode', type: 'enum', options: ['ReadOnly', 'FullControl'] },
    ],
  },
  {
    key: 'task_started', category: 'tasks', plugin: null, observeOnly: false, pluginEnabled: true,
    parameters: [
      { key: 'pluginName', type: 'string', options: null },
      { key: 'taskName', type: 'string', options: null },
    ],
  },
  {
    key: 'task_completed', category: 'tasks', plugin: null, observeOnly: false, pluginEnabled: true,
    parameters: [
      { key: 'pluginName', type: 'string', options: null },
      { key: 'taskName', type: 'string', options: null },
    ],
  },
  {
    key: 'task_failed', category: 'tasks', plugin: null, observeOnly: false, pluginEnabled: true,
    parameters: [
      { key: 'pluginName', type: 'string', options: null },
      { key: 'taskName', type: 'string', options: null },
      { key: 'error', type: 'string', options: null },
      { key: 'isSubtask', type: 'boolean', options: null },
    ],
  },
  {
    key: 'window_opened', category: 'world', plugin: null, observeOnly: false, pluginEnabled: true,
    parameters: [
      { key: 'windowId', type: 'number', options: null },
      { key: 'windowType', type: 'string', options: null },
      { key: 'windowTitle', type: 'string', options: null },
    ],
  },
  {
    key: 'player_joined', category: 'world', plugin: null, observeOnly: false, pluginEnabled: true,
    parameters: [
      { key: 'entityId', type: 'number', options: null },
      { key: 'name', type: 'string', options: null },
      { key: 'uuid', type: 'string', options: null },
    ],
  },
  {
    key: 'plot_access_enforced', category: 'protection', plugin: 'PlotACL', observeOnly: false, pluginEnabled: true,
    parameters: [
      { key: 'playerName', type: 'string', options: null },
      { key: 'action', type: 'enum', options: ['Kick', 'PlotBan', 'PlotAclBan'] },
      { key: 'reason', type: 'string', options: null },
    ],
  },
  {
    key: 'protection_engaged', category: 'protection', plugin: 'Protection', observeOnly: false, pluginEnabled: true,
    parameters: [],
  },
  {
    key: 'schedule_fired', category: 'schedule', plugin: null, observeOnly: false, pluginEnabled: true,
    parameters: [{ key: 'scheduledAt', type: 'datetime', options: null }],
  },
];

const FAKE_BOT_PARAMETERS = [
  { key: 'bot.name', type: 'string', options: null },
  { key: 'bot.uuid', type: 'string', options: null },
  { key: 'bot.id', type: 'string', options: null },
  { key: 'bot.server', type: 'string', options: null },
];

const FAKE_BOT_VALUES: Record<string, string> = {
  'bot.name': 'Kaufmann_Toni',
  'bot.uuid': '3f1c9a2e-7b44-4d0e-9c1a-5e8f2b6d7a90',
  'bot.id': 'b1',
  'bot.server': 'griefergames.net',
};

interface FakeSchedule {
  kind?: string;
  days?: number[];
  daysOfMonth?: number[];
  minutesOfDay?: number[];
  everyMinutes?: number;
  fromMinute?: number;
  untilMinute?: number;
}

// Browser-local slots stand in for the server's Berlin-time calculation; every preview user is in DE/AT.
function fakeUpcomingRuns(scheduleJson: string | null | undefined, count: number): { runs: string[]; error: string | null } {
  let schedule: FakeSchedule;
  try {
    schedule = JSON.parse(scheduleJson || '{}') as FakeSchedule;
  } catch {
    return { runs: [], error: 'The schedule could not be read.' };
  }

  const monthly = schedule.kind === 'monthly';
  const days = schedule.days ?? [];
  const daysOfMonth = schedule.daysOfMonth ?? [];
  if (monthly && daysOfMonth.length === 0) return { runs: [], error: 'Pick at least one day of the month.' };
  if (!monthly && days.length === 0) return { runs: [], error: 'Pick at least one weekday.' };
  const includesDate = (date: Date) => {
    if (!monthly) return days.includes(date.getDay());
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    return daysOfMonth.includes(date.getDate()) || (date.getDate() === lastDay && daysOfMonth.some(day => day > lastDay));
  };

  const minutes: number[] = [];
  if (schedule.kind === 'interval') {
    const fromMinute = schedule.fromMinute ?? 0;
    const untilMinute = schedule.untilMinute ?? 1439;
    const lastMinute = untilMinute < fromMinute ? untilMinute + 1440 : untilMinute;
    for (let minute = fromMinute; minute <= lastMinute; minute += Math.max(5, schedule.everyMinutes ?? 60))
      minutes.push(minute);
  } else {
    minutes.push(...(schedule.minutesOfDay ?? []));
  }
  if (minutes.length === 0) return { runs: [], error: 'Add at least one time.' };

  const runs: string[] = [];
  const now = new Date();
  for (let dayOffset = -1; dayOffset <= (monthly ? 62 : 7) && runs.length < count; dayOffset++) {
    const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + dayOffset);
    if (!includesDate(date)) continue;
    for (const minute of [...minutes].sort((a, b) => a - b)) {
      const slot = new Date(date.getFullYear(), date.getMonth(), date.getDate(), Math.floor(minute / 60), minute % 60);
      if (slot > now && runs.length < count) runs.push(slot.toISOString());
    }
  }
  return { runs, error: null };
}

interface FakeEventHandler {
  id: string;
  botRegistryId: string;
  name: string;
  eventKey: string;
  isEnabled: boolean;
  sortOrder: number;
  cooldownSeconds: number;
  runWhilePossessed: boolean;
  conditionJson: string;
  scheduleJson: string;
  nextRunAt: string | null;
  actions: { id: string; sortOrder: number; actionType: number; webhookId: string | null; webhookName: string | null; configJson: string }[];
  createdAt: string;
}

const NIGHTLY_RESTOCK_SCHEDULE = JSON.stringify({ kind: 'times', days: [1, 2, 3, 4, 5], minutesOfDay: [1320], jitterMinutes: 5 });

const FAKE_EVENT_HANDLERS: FakeEventHandler[] = [
  {
    id: 'f4000000-0000-0000-0000-000000000001', botRegistryId: 'b1', name: 'Große Zahlungen melden',
    eventKey: 'payment_received', isEnabled: true, sortOrder: 0, cooldownSeconds: 30, runWhilePossessed: false,
    conditionJson: JSON.stringify({ group: 'and', children: [{ param: 'amount', op: 'ge', value: '1000' }] }),
    scheduleJson: '', nextRunAt: null,
    actions: [{
      id: 'f4100000-0000-0000-0000-000000000001', sortOrder: 0, actionType: 0,
      webhookId: 'f3000000-0000-0000-0000-000000000001', webhookName: 'Shop-Meldungen',
      configJson: JSON.stringify({ body: '{"content":"{payer} hat {currency(amount, \\"$\\")} gezahlt"}', contentType: 'application/json' }),
    }],
    createdAt: '2026-09-25T08:20:00Z',
  },
  {
    id: 'f4000000-0000-0000-0000-000000000002', botRegistryId: 'b1', name: 'Nach dem Farmlauf einlagern',
    eventKey: 'task_completed', isEnabled: false, sortOrder: 1, cooldownSeconds: 0, runWhilePossessed: false,
    conditionJson: JSON.stringify({ group: 'and', children: [{ param: 'taskName', op: 'eq', value: 'FarmTask' }] }),
    scheduleJson: '', nextRunAt: null,
    actions: [{
      id: 'f4100000-0000-0000-0000-000000000002', sortOrder: 0, actionType: 1,
      webhookId: null, webhookName: null,
      configJson: JSON.stringify({ plugin: 'Storage', task: 'Deposit', arguments: {}, startImmediately: false }),
    }],
    createdAt: '2026-09-26T16:05:00Z',
  },
  {
    id: 'f4000000-0000-0000-0000-000000000003', botRegistryId: 'b1', name: 'Abends den Shop auffüllen',
    eventKey: 'schedule_fired', isEnabled: true, sortOrder: 2, cooldownSeconds: 0, runWhilePossessed: false,
    conditionJson: '', scheduleJson: NIGHTLY_RESTOCK_SCHEDULE,
    nextRunAt: fakeUpcomingRuns(NIGHTLY_RESTOCK_SCHEDULE, 1).runs[0] ?? null,
    actions: [
      {
        id: 'f4100000-0000-0000-0000-000000000003', sortOrder: 0, actionType: 0,
        webhookId: 'f3000000-0000-0000-0000-000000000001', webhookName: 'Shop-Meldungen',
        configJson: JSON.stringify({ body: '{"content":"{bot.name} füllt den Shop auf"}', contentType: 'application/json' }),
      },
      {
        id: 'f4100000-0000-0000-0000-000000000004', sortOrder: 1, actionType: 1,
        webhookId: null, webhookName: null,
        configJson: JSON.stringify({ plugin: 'Storage', task: 'Deposit', arguments: {}, startImmediately: false }),
      },
    ],
    createdAt: '2026-10-20T18:30:00Z',
  },
];

const FAKE_EVENT_EXECUTIONS = [
  {
    id: 'f5000000-0000-0000-0000-000000000001', handlerId: 'f4000000-0000-0000-0000-000000000001',
    handlerName: 'Große Zahlungen melden', eventKey: 'payment_received', actionType: 0, status: 0,
    statusCode: 204, taskId: null, durationMs: 184, attempts: 1, error: null, createdAt: '2026-09-30T19:41:12Z',
  },
  {
    id: 'f5000000-0000-0000-0000-000000000002', handlerId: 'f4000000-0000-0000-0000-000000000001',
    handlerName: 'Große Zahlungen melden', eventKey: 'payment_received', actionType: 0, status: 2,
    statusCode: null, taskId: null, durationMs: 0, attempts: 0, error: null, createdAt: '2026-09-30T19:41:02Z',
  },
  {
    id: 'f5000000-0000-0000-0000-000000000003', handlerId: 'f4000000-0000-0000-0000-000000000001',
    handlerName: 'Große Zahlungen melden', eventKey: 'payment_received', actionType: 0, status: 1,
    statusCode: 404, taskId: null, durationMs: 412,
    attempts: 1, error: 'The destination answered 404 Not Found.', createdAt: '2026-09-30T11:02:55Z',
  },
  {
    id: 'f5000000-0000-0000-0000-000000000004', handlerId: 'f4000000-0000-0000-0000-000000000003',
    handlerName: 'Abends den Shop auffüllen', eventKey: 'schedule_fired', actionType: 1, status: 5,
    statusCode: null, taskId: null, durationMs: 0, attempts: 1,
    error: 'The bot was offline, so the task was not started.', createdAt: '2026-10-21T20:03:41Z',
  },
];

interface FakeWebhookConfig {
  body?: string;
  content?: string;
  embed?: { title?: string; description?: string; url?: string; color?: string; fields?: { name?: string; value?: string; inline?: boolean }[] };
}

function readFakeWebhookConfig(configJson?: string): FakeWebhookConfig {
  try {
    return JSON.parse(configJson || '{}') as FakeWebhookConfig;
  } catch {
    return {};
  }
}

// Mirrors WebhookPayloadBuilder: an embed is assembled as an object, so the preview shows the same
// always-valid payload the dispatcher would send.
function buildFakeEmbedPayload(config: FakeWebhookConfig, samples: Record<string, string>): string {
  const fill = (template?: string) => {
    const rendered = renderFakeTemplate(template ?? '', samples, false);
    return rendered.trim() === '' ? undefined : rendered;
  };

  const fields = (config.embed?.fields ?? [])
    .map(f => ({ name: fill(f.name), value: fill(f.value), inline: !!f.inline }))
    .filter(f => f.name || f.value);

  const card: Record<string, unknown> = {};
  if (fill(config.embed?.title)) card.title = fill(config.embed?.title);
  if (fill(config.embed?.description)) card.description = fill(config.embed?.description);
  if (fill(config.embed?.url)) card.url = fill(config.embed?.url);
  if (config.embed?.color) card.color = parseInt(config.embed.color.replace('#', ''), 16);
  if (fields.length > 0) card.fields = fields;

  const payload: Record<string, unknown> = { embeds: [card] };
  if (fill(config.content)) payload.content = fill(config.content);

  return JSON.stringify(payload);
}

// Stands in for the real template engine so the preview exercises the same shapes: a placeholder is
// substituted and JSON-escaped, everything else is the author's own structure.
function renderFakeTemplate(template: string, samples: Record<string, string>, escapeForJson = true): string {
  return template.replace(/\{([A-Za-z_][^}]*)\}/g, (_whole, expr: string) => {
    const key = expr.split('(')[0].trim();
    const value = samples[key] ?? (expr.includes('(') ? '1.500,00 $' : '');
    return escapeForJson ? JSON.stringify(value).slice(1, -1) : value;
  });
}

function fakeJsonProblem(rendered: string): string | null {
  if (rendered.trim() === '') return null;
  try {
    JSON.parse(rendered);
    return null;
  } catch (e) {
    return `This renders to something that is not valid JSON, so the destination would reject it: ${(e as Error).message}`;
  }
}

function routeAutomation(path: string, method: string, body: unknown): Response | Promise<Response> | null {
  const match = path.match(/^\/api\/bots\/([^/]+)\/automation(\/.*)?$/);
  if (!match) return null;

  const tail = match[2] ?? '';

  if (tail === '/catalog' && method === 'GET') return apiOk({ events: FAKE_EVENT_CATALOG, botParameters: FAKE_BOT_PARAMETERS });
  if (tail === '/executions' && method === 'GET') return apiOk({ executions: FAKE_EVENT_EXECUTIONS });

  if (tail === '/dry-run' && method === 'POST') {
    const sent = body as { eventKey?: string; scheduleJson?: string | null; conditionJson?: string; sampleValues?: Record<string, string>; actions?: { sortOrder: number; actionType: number; configJson?: string }[] };
    const matched = sent?.eventKey === 'schedule_fired' || Object.values(sent?.sampleValues ?? {}).some(value => value.trim() !== '');
    const upcoming = sent?.eventKey === 'schedule_fired' ? fakeUpcomingRuns(sent.scheduleJson, 5) : { runs: [], error: null };
    const samples = { ...FAKE_BOT_VALUES, ...(sent?.sampleValues ?? {}) };

    return slowOk({
      matched,
      conditionError: null,
      scheduleError: upcoming.error,
      upcomingRuns: upcoming.runs,
      actions: (sent?.actions ?? []).map(action => {
        if (action.actionType !== 0) {
          return { sortOrder: action.sortOrder, actionType: action.actionType, rendered: 'Storage / Deposit', error: null };
        }

        const config = readFakeWebhookConfig(action.configJson);

        if (config.embed) {
          return {
            sortOrder: action.sortOrder,
            actionType: action.actionType,
            rendered: buildFakeEmbedPayload(config, samples),
            error: null,
          };
        }

        const rendered = renderFakeTemplate(config.body ?? '', samples);
        return {
          sortOrder: action.sortOrder,
          actionType: action.actionType,
          rendered,
          error: fakeJsonProblem(rendered),
        };
      }),
    }, 400);
  }

  if (tail === '' && method === 'GET') return apiOk({ handlers: FAKE_EVENT_HANDLERS });

  if (tail === '' && method === 'POST') {
    const sent = body as Partial<FakeEventHandler>;
    const created: FakeEventHandler = {
      id: `f4000000-0000-0000-0000-${String(FAKE_EVENT_HANDLERS.length + 1).padStart(12, '0')}`,
      botRegistryId: match[1],
      name: sent?.name ?? 'Neue Regel',
      eventKey: sent?.eventKey ?? 'payment_received',
      isEnabled: sent?.isEnabled ?? true,
      sortOrder: 0,
      cooldownSeconds: sent?.cooldownSeconds ?? 0,
      runWhilePossessed: sent?.runWhilePossessed ?? false,
      conditionJson: sent?.conditionJson ?? '',
      scheduleJson: sent?.scheduleJson ?? '',
      nextRunAt: sent?.eventKey === 'schedule_fired' ? fakeUpcomingRuns(sent.scheduleJson, 1).runs[0] ?? null : null,
      actions: [],
      createdAt: new Date().toISOString(),
    };
    FAKE_EVENT_HANDLERS.push(created);
    return apiOk(created);
  }

  const handlerMatch = tail.match(/^\/([^/]+)$/);
  if (handlerMatch) {
    const index = FAKE_EVENT_HANDLERS.findIndex(h => h.id === handlerMatch[1]);
    if (index < 0) return null;

    if (method === 'DELETE') {
      FAKE_EVENT_HANDLERS.splice(index, 1);
      return apiOk({});
    }
    if (method === 'PUT') {
      const sent = body as Partial<FakeEventHandler>;
      FAKE_EVENT_HANDLERS[index] = { ...FAKE_EVENT_HANDLERS[index], ...sent, id: FAKE_EVENT_HANDLERS[index].id };
      return apiOk(FAKE_EVENT_HANDLERS[index]);
    }
  }

  return null;
}

const WEBHOOK_OWNER_ACTIONS = [
  'webhook.read', 'webhook.test', 'webhook.config.write',
  'webhook.access.read', 'webhook.access.grant', 'webhook.delete',
];

interface FakeWebhook {
  id: string;
  name: string;
  kind: number;
  maskedUrl: string;
  usernameOverride: string | null;
  avatarUrl: string | null;
  ownerId: string;
  createdAt: string;
  myActions: string[];
}

const FAKE_WEBHOOKS: FakeWebhook[] = [
  {
    id: 'f3000000-0000-0000-0000-000000000001', name: 'Shop-Meldungen', kind: 1,
    maskedUrl: 'discord.com/api/webhooks/1284915027/aB3f...', usernameOverride: 'Vektor', avatarUrl: null,
    ownerId: 'u1', createdAt: '2026-09-02T09:14:00Z', myActions: WEBHOOK_OWNER_ACTIONS,
  },
  {
    id: 'f3000000-0000-0000-0000-000000000002', name: 'Bot offline', kind: 1,
    maskedUrl: 'discord.com/api/webhooks/1284915099/zQ7m...', usernameOverride: null, avatarUrl: null,
    ownerId: 'u2', createdAt: '2026-09-19T21:03:00Z', myActions: ['webhook.read', 'webhook.test'],
  },
  {
    id: 'f3000000-0000-0000-0000-000000000003', name: 'Lager-Export', kind: 0,
    maskedUrl: 'hooks.example.net/ingest/4f2a...', usernameOverride: null, avatarUrl: null,
    ownerId: 'u1', createdAt: '2026-09-24T07:45:00Z', myActions: WEBHOOK_OWNER_ACTIONS,
  },
];

const FAKE_REPAIR_CONFIGS = [
  { id: 'preview-repair-001', name: 'Werkzeuglager', storageId: STORAGE1, anvilRadius: 8, repairBelowPercent: 75, maxLevelsPerRepair: 39, isEnabled: true, createdAt: NOW },
  { id: 'preview-repair-002', name: 'Rüstungen', storageId: STORAGE1, anvilRadius: 12, repairBelowPercent: 50, maxLevelsPerRepair: 20, isEnabled: false, createdAt: NOW },
];

function routeRepair(path: string, method: string, body: unknown): Response | Promise<Response> | null {
  if (path === '/api/Repair' && method === 'GET') return apiOk({ configs: FAKE_REPAIR_CONFIGS });
  if (path === '/api/Repair' && method === 'POST') {
    const created = { ...(body as object), id: `preview-repair-${FAKE_REPAIR_CONFIGS.length + 1}`, createdAt: new Date().toISOString() };
    FAKE_REPAIR_CONFIGS.push(created as typeof FAKE_REPAIR_CONFIGS[number]);
    return apiOk({ id: created.id });
  }
  const configMatch = path.match(/^\/api\/Repair\/([^/]+)(\/run|\/stop)?$/);
  if (!configMatch) return null;
  if (configMatch[2] === '/run') return apiOk({ botId: BOT1 });
  if (method === 'PUT') {
    const index = FAKE_REPAIR_CONFIGS.findIndex(c => c.id === configMatch[1]);
    if (index >= 0) FAKE_REPAIR_CONFIGS[index] = { ...FAKE_REPAIR_CONFIGS[index], ...(body as object) };
  }
  if (method === 'DELETE' && !configMatch[2]) {
    const index = FAKE_REPAIR_CONFIGS.findIndex(c => c.id === configMatch[1]);
    if (index >= 0) FAKE_REPAIR_CONFIGS.splice(index, 1);
  }
  return apiOk({});
}

const fakeCase = (name: string, itemId: number, metadata: number, count: number) => ({
  name,
  label: name.replace(/§./g, ''),
  item: { key: `${itemId}:${metadata}`, itemId, metadata, count: 1, snbt: `{display:{Name:"${name}"}}`, nbtHash: null, isCustom: false },
  count,
});

const FAKE_CASE_OPENING_ACCOUNT = {
  botId: BOT1,
  scannedAt: NOW,
  cases: [
    fakeCase('§fVote-Kiste', 54, 0, 125),
    fakeCase('§5Epische Kiste', 130, 0, 10),
    fakeCase('§b§lGroße Winter-Kiste', 174, 0, 5),
    fakeCase('§e§lLu§6§lck§e§ly-§6§lKi§e§lst§6§le', 397, 3, 5),
  ],
  unclaimedRewards: 0,
  lastRun: {
    startedAt: NOW,
    finishedAt: NOW,
    storageId: STORAGE1,
    lines: [{ caseName: '§fVote-Kiste', requested: 6, opened: 6, outcome: 1 }],
    rewards: [
      { text: 'DIAMOND_HELMET', count: 2 },
      { text: '1 Kristall', count: 2 },
      { text: 'DIAMOND_SPADE', count: 1 },
      { text: 'DIAMOND_SWORD', count: 1 },
    ],
    depositFailed: false,
  },
  isRunning: false,
};

const FAKE_CASE_WIN_STATS = {
  wins: 56,
  bots: 2,
  firstWonAt: YESTERDAY,
  lastWonAt: NOW,
  cases: [
    {
      caseName: '§fVote-Kiste', label: 'Vote-Kiste', wins: 48, bots: 2,
      prizes: [
        { prize: 'DIAMOND_AXE', wins: 8 }, { prize: '25.000$ Bargeld', wins: 7 }, { prize: 'DIAMOND_SPADE', wins: 5 },
        { prize: '1 Kristall', wins: 5 }, { prize: '6 Kristalle', wins: 4 }, { prize: 'DIAMOND_LEGGINGS', wins: 4 },
        { prize: 'DIAMOND_CHESTPLATE', wins: 4 }, { prize: 'TELEPORTER', wins: 3 }, { prize: 'GrieferGames TEAM Sammelpack', wins: 3 },
        { prize: 'Flugtrank', wins: 2 }, { prize: 'Abbautrank', wins: 1 }, { prize: 'BEACON', wins: 1 }, { prize: 'Unbegrenzter Speicher', wins: 1 },
      ],
    },
    {
      caseName: '§5Epische Kiste', label: 'Epische Kiste', wins: 8, bots: 1,
      prizes: [{ prize: 'LuckyBlock', wins: 5 }, { prize: 'Plot-Fliegen', wins: 2 }, { prize: '2 Booster', wins: 1 }],
    },
  ],
};

function routeCaseOpening(path: string, method: string): Response | Promise<Response> | null {
  if (path === '/api/caseopening/admin/stats') return apiOk(FAKE_CASE_WIN_STATS);
  const match = path.match(/^\/api\/caseopening\/([^/]+)(\/scan|\/open|\/stop)?$/);
  if (!match) return null;
  if (!match[2] && method === 'GET') return apiOk({ ...FAKE_CASE_OPENING_ACCOUNT, botId: match[1] });
  if (match[2] === '/stop') return apiOk({});
  return apiOk({ botId: match[1] });
}

const FAKE_ALERT_RULES = [
  {
    id: 'f4000000-0000-0000-0000-000000000001', key: 'gg-outage', displayName: 'GrieferGames outage', metric: 12, comparison: 0,
    threshold: 10, windowSeconds: 900, cooldownSeconds: 1800, isEnabled: true,
    webhookId: 'f3000000-0000-0000-0000-000000000001', webhookName: 'Shop-Meldungen',
  },
  {
    id: 'f4000000-0000-0000-0000-000000000002', key: 'reconnect-spike', displayName: 'Reconnect spike', metric: 1, comparison: 0,
    threshold: 20, windowSeconds: 60, cooldownSeconds: 900, isEnabled: true, webhookId: null, webhookName: null,
  },
];

const FAKE_ALERT_EVENTS = [
  {
    id: 'f4100000-0000-0000-0000-000000000001', ruleId: 'f4000000-0000-0000-0000-000000000001', metric: 12,
    ruleDisplayName: 'GrieferGames outage', observedValue: 23, threshold: 10, firedAtUtc: '2026-10-05T13:46:10Z',
    message: 'GrieferGames outage: 23 bots dropped from connect.griefergames.net within 2s (Server closed the connection), also 16 from alt.griefergames.net. Players online went from 130 to 12 on cb7',
  },
];

const ALERT_METRIC_CONTEXTS = [
  '3 bot disconnect(s) in the last 1 min', '5 reconnect event(s) in the last 1 min', '0 possession interruption(s) in the last 1 min',
  '1 vote site(s) currently disabled', '2 voter/site pair(s) auto-paused (stale)', '4 failed vote attempt(s) in the last 1 min',
  '38 healthy proxy/proxies in the pool', '1 proxy/proxies locked out on consecutive failures', '0 bot(s) turned themselves off in the last 1 min',
  '0 bot(s) stayed online through a shutoff trigger in the last 1 min', '0 vote(s) found no unused exit address in the last 1 min',
  '0 vote(s) refused for an empty provider balance in the last 1 min', 'No group of bots dropped from one GrieferGames address together',
];

function routeNotifications(path: string, method: string): Response | null {
  if (path === '/api/notifications' && method === 'GET') {
    return apiOk({
      items: [{
        id: '7b0c2f1e-4d1a-4c52-9a51-2f6c1e0b9a11',
        kind: 0,
        resourceFamily: 0,
        resourceId: BOT1,
        resourceName: 'FarmerBot42',
        args: { chest: '14764 65 3889' },
        occurrenceCount: 47,
        firstSeenAtUtc: minutesAgo(25),
        lastSeenAtUtc: minutesAgo(1),
        isRead: false,
      }, {
        id: '7b0c2f1e-4d1a-4c52-9a51-2f6c1e0b9a12',
        kind: 1,
        resourceFamily: 1,
        resourceId: STORAGE1,
        resourceName: 'Hauptlager',
        args: { item: 'Diamond Pickaxe', quantity: '64', reason: 'Chest at 120 64 -310 could not be opened after 3 attempts.', order: 'f6000000-0000-0000-0000-000000000001' },
        occurrenceCount: 1,
        firstSeenAtUtc: minutesAgo(12),
        lastSeenAtUtc: minutesAgo(12),
        isRead: true,
      }],
    });
  }
  if (method === 'POST') return apiOk({ affected: 1 });
  return null;
}

function routeAlerts(path: string, method: string, body: unknown): Response | Promise<Response> | null {
  if (path === '/api/admin/alerts/rules' && method === 'GET') return apiOk({ rules: FAKE_ALERT_RULES });
  if (path === '/api/admin/alerts/events' && method === 'GET') return apiOk({ events: FAKE_ALERT_EVENTS });
  if (path === '/api/admin/alerts/metrics/current') {
    return apiOk({ metrics: ALERT_METRIC_CONTEXTS.map((context, metric) => ({ metric, metricName: String(metric), value: Number.parseInt(context, 10) || 0, context })) });
  }
  if (path === '/api/admin/alerts/preferences') return apiOk({ emailEnabled: false, email: '', pushEnabled: true });
  if (path === '/api/admin/alerts/test') return slowOk({ emailSent: false, pushSent: true }, 500);

  const sent = body as { webhookId?: string | null } & Record<string, unknown>;
  const webhookName = (id?: string | null) => FAKE_WEBHOOKS.find(w => w.id === id)?.name ?? null;
  if (path === '/api/admin/alerts/rules' && method === 'POST') {
    const created = { ...sent, id: `f4000000-0000-0000-0000-${String(FAKE_ALERT_RULES.length + 1).padStart(12, '0')}`, webhookName: webhookName(sent?.webhookId) };
    FAKE_ALERT_RULES.push(created as typeof FAKE_ALERT_RULES[number]);
    return apiOk(created);
  }
  const ruleMatch = path.match(/^\/api\/admin\/alerts\/rules\/([^/]+)$/);
  if (!ruleMatch) return null;
  const index = FAKE_ALERT_RULES.findIndex(r => r.id === ruleMatch[1]);
  if (index < 0) return null;
  if (method === 'DELETE') { FAKE_ALERT_RULES.splice(index, 1); return apiOk(true); }
  FAKE_ALERT_RULES[index] = { ...FAKE_ALERT_RULES[index], ...sent, webhookName: webhookName(sent?.webhookId) } as typeof FAKE_ALERT_RULES[number];
  return apiOk(FAKE_ALERT_RULES[index]);
}

const FAKE_TASK_CAPSULE_STACK = `System.InvalidOperationException: Chest at 120 64 -310 could not be opened after 3 attempts.
   at Remotex.BotEngine.Plugins.Implementations.Storage.Tasks.ReadChestContentsTask.OpenChestAsync(TaskContext context, ChestTarget chest, CancellationToken ct)
   at Remotex.BotEngine.Plugins.Implementations.Storage.Tasks.ReadChestContentsTask.ExecuteAsync(TaskContext context, CancellationToken ct)
   at Remotex.BotEngine.Tasks.BotTaskRunner.RunOnNodeAsync(BotTaskNode node, BotTask task, CancellationToken ct)`;

const FAKE_TASK_CAPSULES = [
  {
    capsuleId: 'f5000000-0000-0000-0000-000000000001', botId: BOT1, botUsername: 'FarmerBot42',
    rootPluginName: 'Storage', rootTaskName: 'ScanStorageForChests', failedPluginName: 'Storage', failedTaskName: 'ReadChestContents',
    isSubtask: true, error: 'Chest at 120 64 -310 could not be opened after 3 attempts.',
    capturedAtUtc: '2026-10-11T18:42:07Z', windowStartUtc: '2026-10-11T18:40:07Z',
    compressedBytes: 3_412_880, recordCount: 18_244, worldFrameCount: 512, suppressedCount: 0, additionalFailureCount: 1, isManual: false,
  },
  {
    capsuleId: 'f5000000-0000-0000-0000-000000000002', botId: BOT2, botUsername: 'MinerBot7',
    rootPluginName: 'Farm', rootTaskName: 'FarmArea', failedPluginName: 'Farm', failedTaskName: 'FarmArea',
    isSubtask: false, error: 'No path to the next block after 4 replans.',
    capturedAtUtc: '2026-10-11T16:05:51Z', windowStartUtc: '2026-10-11T16:03:51Z',
    compressedBytes: 2_096_330, recordCount: 9_871, worldFrameCount: 389, suppressedCount: 6, additionalFailureCount: 0, isManual: false,
  },
] as {
  capsuleId: string; botId: string; botUsername: string; rootPluginName: string; rootTaskName: string;
  failedPluginName: string; failedTaskName: string; isSubtask: boolean; error: string | null;
  capturedAtUtc: string; windowStartUtc: string; compressedBytes: number; recordCount: number;
  worldFrameCount: number; suppressedCount: number; additionalFailureCount: number; isManual: boolean;
}[];

function routeTaskCapsules(path: string, method: string, query: URLSearchParams): Response | Promise<Response> | null {
  if (path === '/api/vektor/admin/packet-recordings/bots') {
    return apiOk({ bots: FAKE_BOTS.map(b => ({ botId: b.id, username: b.minecraftUsername })) });
  }
  if (path === '/api/vektor/admin/task-capsules/status') {
    return apiOk({ enabled: true, storageAvailable: true, bufferedBots: 2, bufferedRecords: 27_430, capturesInFlight: 0 });
  }
  if (path === '/api/vektor/admin/task-capsules' && method === 'GET') {
    const botId = query.get('botId');
    const search = (query.get('search') ?? '').toLowerCase();
    const matching = FAKE_TASK_CAPSULES.filter(c => (!botId || c.botId === botId)
      && (!search || [c.rootTaskName, c.failedTaskName, c.rootPluginName, c.error ?? ''].some(text => text.toLowerCase().includes(search))));
    return slowOk({ capsules: matching, total: matching.length }, 300);
  }
  const captureMatch = path.match(/^\/api\/vektor\/admin\/task-capsules\/capture\/([^/]+)$/);
  if (captureMatch && method === 'POST') {
    const bot = FAKE_BOTS.find(b => b.id === captureMatch[1]);
    if (!bot) return apiNotFound('bot_not_buffered');
    const capturedAtUtc = new Date().toISOString();
    const capsule = {
      capsuleId: crypto.randomUUID(), botId: bot.id, botUsername: bot.minecraftUsername,
      rootPluginName: '', rootTaskName: '', failedPluginName: '', failedTaskName: '', isSubtask: false, error: null,
      capturedAtUtc, windowStartUtc: new Date(Date.now() - 120_000).toISOString(),
      compressedBytes: 1_804_220, recordCount: 7_512, worldFrameCount: 401, suppressedCount: 0, additionalFailureCount: 0,
      isManual: true,
    };
    FAKE_TASK_CAPSULES.unshift(capsule);
    return slowOk(capsule, 900);
  }
  const capsuleMatch = path.match(/^\/api\/vektor\/admin\/task-capsules\/([^/]+)$/);
  if (!capsuleMatch) return null;
  const index = FAKE_TASK_CAPSULES.findIndex(c => c.capsuleId === capsuleMatch[1]);
  if (index < 0) return apiNotFound('capsule_not_found');
  if (method === 'DELETE') { FAKE_TASK_CAPSULES.splice(index, 1); return apiOk({ success: true }); }
  const capsule = FAKE_TASK_CAPSULES[index];
  const additionalFailures = capsule.additionalFailureCount > 0
    ? [{ atUtc: '2026-10-11T18:42:07Z', pluginName: capsule.rootPluginName, taskName: capsule.rootTaskName, isSubtask: false, error: capsule.error, exceptionDetail: FAKE_TASK_CAPSULE_STACK }]
    : [];
  return slowOk({ capsule, exceptionDetail: capsule.isManual ? null : FAKE_TASK_CAPSULE_STACK, additionalFailures }, 250);
}

function routeWebhooks(path: string, method: string, body: unknown): Response | Promise<Response> | null {
  if (path === '/api/webhooks' && method === 'GET') return apiOk({ webhooks: FAKE_WEBHOOKS });

  if (path === '/api/webhooks' && method === 'POST') {
    const sent = body as { kind?: number; name?: string; url?: string; usernameOverride?: string; avatarUrl?: string };
    const created = {
      id: `f3000000-0000-0000-0000-${String(FAKE_WEBHOOKS.length + 1).padStart(12, '0')}`,
      name: sent?.name ?? 'Neuer Webhook',
      kind: sent?.kind ?? 1,
      maskedUrl: 'discord.com/api/webhooks/1284915100/nEw1...',
      usernameOverride: sent?.usernameOverride || null,
      avatarUrl: sent?.avatarUrl || null,
      ownerId: 'u1', createdAt: new Date().toISOString(), myActions: WEBHOOK_OWNER_ACTIONS,
    };
    FAKE_WEBHOOKS.push(created);
    return apiOk(created);
  }

  const testMatch = path.match(/^\/api\/webhooks\/([^/]+)\/test$/);
  if (testMatch && method === 'POST') {
    const webhook = FAKE_WEBHOOKS.find(w => w.id === testMatch[1]);
    return webhook?.kind === 0
      ? slowOk({ delivered: false, statusCode: 404, error: 'The destination answered 404 Not Found.', attempts: 1, durationMs: 412 }, 700)
      : slowOk({ delivered: true, statusCode: 204, error: null, attempts: 1, durationMs: 186 }, 700);
  }

  const idMatch = path.match(/^\/api\/webhooks\/([^/]+)$/);
  if (idMatch) {
    const index = FAKE_WEBHOOKS.findIndex(w => w.id === idMatch[1]);
    if (index < 0) return null;

    if (method === 'DELETE') {
      FAKE_WEBHOOKS.splice(index, 1);
      return apiOk({});
    }
    if (method === 'PUT') {
      const sent = body as { name?: string; usernameOverride?: string; avatarUrl?: string };
      FAKE_WEBHOOKS[index] = {
        ...FAKE_WEBHOOKS[index],
        name: sent?.name ?? FAKE_WEBHOOKS[index].name,
        usernameOverride: sent?.usernameOverride || null,
        avatarUrl: sent?.avatarUrl || null,
      };
      return apiOk(FAKE_WEBHOOKS[index]);
    }
    if (method === 'GET') return apiOk(FAKE_WEBHOOKS[index]);
  }

  return null;
}

const PLAYERLIST_OWNER_ACTIONS = [
  'playerlist.read', 'playerlist.entries.write', 'playerlist.config.write',
  'playerlist.access.read', 'playerlist.access.grant', 'playerlist.delete',
];

const FAKE_PLAYER_LISTS = [
  { id: 'f2000000-0000-0000-0000-000000000001', name: 'DSM-Clan', ownerId: 'u1', memberCount: 2, createdAt: '2026-08-01T10:12:00Z', myActions: PLAYERLIST_OWNER_ACTIONS },
  { id: 'f2000000-0000-0000-0000-000000000002', name: 'Bekannte Griefer', ownerId: 'u2', memberCount: 1, createdAt: '2026-07-19T18:40:00Z', myActions: ['playerlist.read'] },
];

const FAKE_PLAYER_LIST_ENTRIES: Record<string, { id: string; trackedMinecraftProfileId: string; uuid: string; currentUsername: string; note: string | null; addedAt: string }[]> = {
  'f2000000-0000-0000-0000-000000000001': [
    { id: 'f3000000-0000-0000-0000-000000000001', ...FAKE_TRACKED_PROFILES[0], note: 'Clanleitung', addedAt: '2026-08-01T10:14:00Z' },
    { id: 'f3000000-0000-0000-0000-000000000002', ...FAKE_TRACKED_PROFILES[1], note: null, addedAt: '2026-08-02T09:03:00Z' },
  ],
  'f2000000-0000-0000-0000-000000000002': [
    { id: 'f3000000-0000-0000-0000-000000000003', ...FAKE_TRACKED_PROFILES[2], note: 'Plot CB2 12;-4 gegrieft', addedAt: '2026-07-19T18:41:00Z' },
  ],
};

// The real add-entry path waits on a Mojang lookup, so the preview waits too: an instant fake
// response hides every loading state the page is supposed to show.
function slowOk(value: unknown, ms: number): Promise<Response> {
  return new Promise(resolve => setTimeout(() => resolve(apiOk(value)), ms));
}

const FAKE_BOT_GROUPS: { id: string; name: string; botIds: string[] }[] = [
  { id: 'f3000000-0000-0000-0000-000000000001', name: 'Farmers', botIds: [BOT1, BOT2] },
];

function routeBotGroups(path: string, method: string, body: unknown): Response | null {
  const draft = body as { name?: string; botIds?: string[] } | undefined;
  if (path === '/api/botgroups' && method === 'GET') return apiOk({ groups: FAKE_BOT_GROUPS });
  if (path === '/api/botgroups' && method === 'POST') {
    const created = {
      id: `f3000000-0000-0000-0000-${String(FAKE_BOT_GROUPS.length + 1).padStart(12, '0')}`,
      name: draft?.name ?? 'Neue Gruppe',
      botIds: draft?.botIds ?? [],
    };
    FAKE_BOT_GROUPS.push(created);
    return apiOk(created);
  }
  const groupMatch = path.match(/^\/api\/botgroups\/([^/]+)$/);
  if (!groupMatch) return null;
  const index = FAKE_BOT_GROUPS.findIndex(g => g.id === groupMatch[1]);
  if (index < 0) return apiNotFound('This bot group does not exist.');
  if (method === 'PUT') {
    FAKE_BOT_GROUPS[index] = { ...FAKE_BOT_GROUPS[index], name: draft?.name ?? FAKE_BOT_GROUPS[index].name, botIds: draft?.botIds ?? FAKE_BOT_GROUPS[index].botIds };
    return apiOk(FAKE_BOT_GROUPS[index]);
  }
  if (method === 'DELETE') {
    FAKE_BOT_GROUPS.splice(index, 1);
    return apiOk({});
  }
  return null;
}

function routePlayerLists(path: string, method: string, body: unknown, query: URLSearchParams): Response | Promise<Response> | null {
  if (path === '/api/playerlists/search' && method === 'GET') {
    const typed = (query.get('q') ?? '').trim().toLowerCase();
    const matches = typed.length === 0 ? [] : FAKE_TRACKED_PROFILES.filter(p => p.currentUsername.toLowerCase().startsWith(typed));
    return slowOk({ results: matches }, 600);
  }

  if (path === '/api/playerlists' && method === 'GET') return apiOk({ lists: FAKE_PLAYER_LISTS });

  if (path === '/api/playerlists' && method === 'POST') {
    const created = {
      id: `f2000000-0000-0000-0000-${String(FAKE_PLAYER_LISTS.length + 1).padStart(12, '0')}`,
      name: (body as { name?: string })?.name ?? 'Neue Liste',
      ownerId: 'u1', memberCount: 0, createdAt: new Date().toISOString(), myActions: PLAYERLIST_OWNER_ACTIONS,
    };
    FAKE_PLAYER_LISTS.push(created);
    FAKE_PLAYER_LIST_ENTRIES[created.id] = [];
    return apiOk(created);
  }

  const entriesMatch = path.match(/^\/api\/playerlists\/([^/]+)\/entries$/);
  if (entriesMatch) {
    const listId = entriesMatch[1];
    const entries = FAKE_PLAYER_LIST_ENTRIES[listId] ?? [];
    if (method === 'GET') return slowOk({ entries }, 700);
    if (method === 'POST') {
      const request = body as { name?: string | null; uuid?: string | null };
      const known = FAKE_TRACKED_PROFILES.find(p =>
        (request.uuid && p.uuid === request.uuid) ||
        (request.name && p.currentUsername.toLowerCase() === request.name.toLowerCase()));
      // The real endpoint resolves an unknown name against Mojang and creates the profile, so the
      // preview mints one too instead of rejecting names the local table has never seen.
      const profile = known ?? {
        trackedMinecraftProfileId: `f1000000-0000-0000-0000-${String(FAKE_TRACKED_PROFILES.length + 1).padStart(12, '0')}`,
        uuid: `cccccccc-cccc-cccc-cccc-${String(FAKE_TRACKED_PROFILES.length + 1).padStart(12, '0')}`,
        currentUsername: request.name ?? 'UnknownPlayer',
      };
      if (!known) FAKE_TRACKED_PROFILES.push(profile);

      const existing = entries.find(e => e.trackedMinecraftProfileId === profile.trackedMinecraftProfileId);
      if (existing) return apiOk(existing);

      const entry = { id: `f3000000-0000-0000-0000-${String(Object.values(FAKE_PLAYER_LIST_ENTRIES).flat().length + 1).padStart(12, '0')}`, ...profile, note: null, addedAt: new Date().toISOString() };
      FAKE_PLAYER_LIST_ENTRIES[listId] = [...entries, entry];
      const list = FAKE_PLAYER_LISTS.find(l => l.id === listId);
      if (list) list.memberCount = FAKE_PLAYER_LIST_ENTRIES[listId].length;
      return slowOk(entry, 1800);
    }
  }

  const entryNoteMatch = path.match(/^\/api\/playerlists\/([^/]+)\/entries\/([^/]+)\/note$/);
  if (entryNoteMatch && method === 'PUT') {
    const [, listId, entryId] = entryNoteMatch;
    const entry = (FAKE_PLAYER_LIST_ENTRIES[listId] ?? []).find(e => e.id === entryId);
    if (!entry) return null;
    entry.note = (body as { note?: string | null })?.note?.trim() || null;
    return apiOk(entry);
  }

  const entryMatch = path.match(/^\/api\/playerlists\/([^/]+)\/entries\/([^/]+)$/);
  if (entryMatch && method === 'DELETE') {
    const [, listId, entryId] = entryMatch;
    FAKE_PLAYER_LIST_ENTRIES[listId] = (FAKE_PLAYER_LIST_ENTRIES[listId] ?? []).filter(e => e.id !== entryId);
    const list = FAKE_PLAYER_LISTS.find(l => l.id === listId);
    if (list) list.memberCount = FAKE_PLAYER_LIST_ENTRIES[listId].length;
    return apiOk({});
  }

  const listMatch = path.match(/^\/api\/playerlists\/([^/]+)$/);
  if (listMatch) {
    const listId = listMatch[1];
    const list = FAKE_PLAYER_LISTS.find(l => l.id === listId);
    if (method === 'PUT' && list) {
      list.name = (body as { name?: string })?.name ?? list.name;
      return apiOk(list);
    }
    if (method === 'DELETE') {
      const index = FAKE_PLAYER_LISTS.findIndex(l => l.id === listId);
      if (index >= 0) FAKE_PLAYER_LISTS.splice(index, 1);
      delete FAKE_PLAYER_LIST_ENTRIES[listId];
      return apiOk({});
    }
  }

  return null;
}

const FAKE_PLOTACL_CONFIG = {
  isActive: true,
  autoEnabledBySellbot: false,
  radius: 64,
  cooldownSeconds: 1,
  invisibleAction: 'Kick',
  exemptHelpers: true,
  exemptTrusted: true,
  rankActions: { Spieler: 'Kick', Premium: 'Kick' } as Record<string, string>,
  unrankedAction: 'Kick' as string | null,
  banListMessage: 'Du stehst auf der Bannliste dieses Plots.' as string | null,
  rankMessage: null as string | null,
  accessWhitelistMessage: null as string | null,
  accessBlacklistMessage: null as string | null,
  invisibleMessage: 'Unsichtbar ist auf diesem Plot nicht erlaubt.' as string | null,
};

const FAKE_PLOTACL_BANS = [
  { id: 'p5000000-0000-0000-0000-000000000001', playerName: 'Griefer_Max', playerUuid: null, reason: 'manual', note: 'Hat am 12.10. Kisten geleert', createdAt: YESTERDAY },
];

const FAKE_PLOTACL_WHITELIST = [
  { id: 'p3000000-0000-0000-0000-000000000001', playerName: 'KirstenDelux', playerUuid: null, note: null as string | null, createdAt: YESTERDAY },
];

const FAKE_PLOTACL_RANK_EXEMPTIONS = [
  { id: 'p4000000-0000-0000-0000-000000000001', playerName: 'Anbu_Muha', playerUuid: null, note: 'Stammkunde' as string | null, createdAt: YESTERDAY },
];

const FAKE_PLOTACL_ACTIVITY = [
  { id: 'p1000000-0000-0000-0000-000000000001', level: 'Warning', type: 'action_taken', message: null, reasonCode: 'rank_Premium', reasonArgsJson: '{"action":"Kick"}', playerName: 'Anbu_Muha', playerUuid: null, createdAt: new Date(Date.now() - 120000).toISOString() },
  { id: 'p1000000-0000-0000-0000-000000000002', level: 'Info', type: 'exempted', message: null, reasonCode: 'whitelist', reasonArgsJson: null, playerName: 'KirstenDelux', playerUuid: null, createdAt: new Date(Date.now() - 600000).toISOString() },
];

const FAKE_PLOTACL_PLOTS = [
  {
    id: 'p2000000-0000-0000-0000-000000000001', citybuild: 'CB2', plotX: 12, plotZ: -4,
    ownerName: 'PreviewUser', helpers: ['Anbu_Muha'], trusted: [], denied: [],
    lastInfoAt: new Date(Date.now() - 3600000).toISOString(),
    accessMode: 'Disabled', accessAction: 'Kick', access: [], attachedLists: [],
  },
];

function routePlotAcl(path: string, method: string, body: unknown): Response | null {
  if (path.endsWith('/overview') && method === 'GET') {
    return apiOk({
      config: FAKE_PLOTACL_CONFIG,
      online: true,
      recentActivity: FAKE_PLOTACL_ACTIVITY,
      bans: FAKE_PLOTACL_BANS,
      whitelist: FAKE_PLOTACL_WHITELIST,
      plots: FAKE_PLOTACL_PLOTS,
      whitelistLists: [],
      banLists: [],
      rankExemptions: FAKE_PLOTACL_RANK_EXEMPTIONS,
      rankExemptionLists: [],
    });
  }

  if (path.endsWith('/activity') && method === 'GET') return apiOk({ entries: FAKE_PLOTACL_ACTIVITY });

  if (path.endsWith('/config') && method === 'PUT') {
    Object.assign(FAKE_PLOTACL_CONFIG, body);
    return apiOk(FAKE_PLOTACL_CONFIG);
  }

  const noteMatch = path.match(/\/(bans|whitelist|rank-exemptions)\/([^/]+)\/note$/);
  if (noteMatch && method === 'PUT') {
    const entries = { bans: FAKE_PLOTACL_BANS, whitelist: FAKE_PLOTACL_WHITELIST, 'rank-exemptions': FAKE_PLOTACL_RANK_EXEMPTIONS }[noteMatch[1]] ?? [];
    const entry = entries.find(e => e.id === noteMatch[2]);
    if (!entry) return null;
    entry.note = (body as { note?: string | null })?.note?.trim() || null;
    return apiOk(entry);
  }

  return null;
}


const AH_NOW = Date.now();

const FAKE_AH_LISTINGS = [
  {
    id: 'a1000000-0000-0000-0000-000000000001', fingerprint: 'ah1', itemId: 278, itemDamage: 0,
    itemNameRaw: '§bOrb-Spitzhacke', itemName: 'Orb-Spitzhacke',
    startingBid: 500000, currentBid: 8000002, buyItNow: 12000000,
    minutesRemainingAtFirstSeen: 240, firstSeenAt: new Date(AH_NOW - 240 * 60000).toISOString(), lastSeenAt: new Date(AH_NOW - 60000).toISOString(),
    estimatedExpiryUtc: new Date(AH_NOW + 96 * 60000).toISOString(), expiredAt: null, scanCount: 42,
    outcome: 'Unknown', soldFor: null, seller: null,
    botUsername: 'VektorScan1',
    snbt: '{display:{Name:"§bOrb-Spitzhacke",Lore:["§7Nur für Tunnelsysteme.","","§8░░░","§7Aktuelles Gebot:","§68.000.002$"]},ench:[{id:32,lvl:5}],RepairCost:7}',
  },
  {
    id: 'a1000000-0000-0000-0000-000000000002', fingerprint: 'ah2', itemId: 397, itemDamage: 3,
    itemNameRaw: '§6Spieler-Kopf', itemName: 'Spieler-Kopf',
    startingBid: 40000, currentBid: 40001, buyItNow: null,
    minutesRemainingAtFirstSeen: 30, firstSeenAt: new Date(AH_NOW - 30 * 60000).toISOString(), lastSeenAt: new Date(AH_NOW - 60000).toISOString(),
    estimatedExpiryUtc: new Date(AH_NOW + 90000).toISOString(), expiredAt: null, scanCount: 4,
    outcome: 'Unknown', soldFor: null, seller: null,
    botUsername: 'VektorScan1',
    snbt: '{display:{Name:"§6Spieler-Kopf"}}',
  },
  {
    id: 'a1000000-0000-0000-0000-000000000003', fingerprint: 'ah3', itemId: 264, itemDamage: 0,
    itemNameRaw: '', itemName: 'Diamant',
    startingBid: 10000, currentBid: 10001, buyItNow: 15000,
    minutesRemainingAtFirstSeen: 60, firstSeenAt: '2026-08-21T20:00:00Z', lastSeenAt: '2026-08-21T21:00:00Z',
    estimatedExpiryUtc: '2026-08-21T21:05:00Z', expiredAt: '2026-08-21T21:05:00Z', scanCount: 12,
    outcome: 'Sold', soldFor: 15000, seller: 'AlexBalor',
    botUsername: 'VektorScan2',
    snbt: '{}',
  },
  {
    id: 'a1000000-0000-0000-0000-000000000004', fingerprint: 'ah4', itemId: 388, itemDamage: 0,
    itemNameRaw: '§aGoodie-Kern', itemName: 'Goodie-Kern',
    startingBid: 250000, currentBid: 250000, buyItNow: null,
    minutesRemainingAtFirstSeen: 45, firstSeenAt: '2026-09-11T08:00:00Z', lastSeenAt: '2026-09-11T08:40:00Z',
    estimatedExpiryUtc: '2026-09-11T08:45:00Z', expiredAt: '2026-09-11T08:45:00Z', scanCount: 9,
    outcome: 'Unsold', soldFor: null, seller: 'yehiiii',
    botUsername: 'VektorScan2',
    snbt: '{display:{Name:"§aGoodie-Kern"}}',
  },
  {
    id: 'a1000000-0000-0000-0000-000000000005', fingerprint: 'ah5', itemId: 264, itemDamage: 0,
    itemNameRaw: '§6Kopf Token', itemName: 'Kopf Token',
    startingBid: 1000000, currentBid: 1155552, buyItNow: 1200000,
    minutesRemainingAtFirstSeen: 60, firstSeenAt: '2026-09-12T16:00:00Z', lastSeenAt: '2026-09-12T16:55:00Z',
    estimatedExpiryUtc: '2026-09-12T17:00:00Z', expiredAt: '2026-09-12T17:00:00Z', scanCount: 15,
    outcome: 'Withdrawn', soldFor: null, seller: 'GrafBumsen',
    botUsername: 'VektorScan1',
    snbt: '{display:{Name:"§6Kopf Token"}}',
  },
];

const FAKE_AH_SNAPSHOTS = [
  { id: 'a2000000-0000-0000-0000-000000000001', scannedAt: '2026-08-22T09:00:00Z', currentBid: 500000, minutesRemaining: 180 },
  { id: 'a2000000-0000-0000-0000-000000000002', scannedAt: '2026-08-22T10:00:00Z', currentBid: 4000000, minutesRemaining: 120 },
  { id: 'a2000000-0000-0000-0000-000000000003', scannedAt: '2026-08-22T11:00:00Z', currentBid: 6500000, minutesRemaining: 60 },
  { id: 'a2000000-0000-0000-0000-000000000004', scannedAt: '2026-08-22T11:58:00Z', currentBid: 8000002, minutesRemaining: 2 },
];

const FAKE_BLACKLIST_ENTRIES = [
  {
    id: '9f1c3a20-1111-4a2b-9c3d-000000000001',
    playerName: 'giftlippenstift',
    scope: 1,
    triggerOnSight: true,
    triggerOnMessage: true,
    isEnabled: true,
    note: 'Jailed Waechtersino on cb1',
  },
  {
    id: '9f1c3a20-1111-4a2b-9c3d-000000000002',
    playerName: 'Grizzlybaer_rt',
    scope: 0,
    triggerOnSight: true,
    triggerOnMessage: false,
    isEnabled: false,
    note: null,
  },
];

const FAKE_SCHEMATIC_LICENSES = [
  {
    id: '7c2a4e10-2222-4b3c-8d4e-000000000001',
    minecraftUuid: '069a79f4-44e9-4726-a5be-fca90e38aaf5',
    currentUsername: 'Notch',
    isEnabled: true,
    expiresAt: null,
    createdAt: '2026-06-01T12:00:00Z',
    note: 'Beta tester',
  },
  {
    id: '7c2a4e10-2222-4b3c-8d4e-000000000002',
    minecraftUuid: 'ec561538-f3fd-461d-aff5-086b22154bce',
    currentUsername: 'Dinnerbone',
    isEnabled: true,
    expiresAt: '2026-01-01T00:00:00Z',
    createdAt: '2025-12-01T09:30:00Z',
    note: null,
  },
  {
    id: '7c2a4e10-2222-4b3c-8d4e-000000000003',
    minecraftUuid: '61699b2e-d327-4a01-9f1e-0ea8c3f06bc6',
    currentUsername: 'Grumm',
    isEnabled: false,
    expiresAt: null,
    createdAt: '2026-03-15T18:45:00Z',
    note: 'Refunded, revoked manually',
  },
];

const FAKE_SELF_SHUTDOWN_INCIDENTS = [
  {
    id: '4d2b8e10-2222-4c3d-8e4f-000000000001',
    botRegistryId: 'ec298d89-b2f1-4738-bf15-f8fde8ef34db',
    botUsername: 'Waechtersino',
    trigger: 1,
    detail: '[GrieferGames] Du wurdest von giftlippenstift für Unbetreuter Shop-Bot ins Gefängnis gesperrt.',
    lastRunningTaskKey: 'Sellbot:ProcessAnkauf',
    occurredAtUtc: '2026-08-23T13:55:45.000Z',
    acknowledgedAtUtc: null as string | null,
    acknowledgedByUsername: null as string | null,
  },
  {
    id: '4d2b8e10-2222-4c3d-8e4f-000000000002',
    botRegistryId: 'ec298d89-b2f1-4738-bf15-f8fde8ef34db',
    botUsername: 'Waechtersino',
    trigger: 1,
    detail: '[GrieferGames] Du wurdest von giftlippenstift für Unbetreuter Shop-Bot ins Gefängnis gesperrt.',
    lastRunningTaskKey: 'AutoJoin:AutoJoin Task',
    occurredAtUtc: '2026-08-19T11:16:17.000Z',
    acknowledgedAtUtc: '2026-08-19T12:02:40.000Z' as string | null,
    acknowledgedByUsername: 'preview' as string | null,
  },
  {
    id: '4d2b8e10-2222-4c3d-8e4f-000000000003',
    botRegistryId: 'ec298d89-b2f1-4738-bf15-f8fde8ef34db',
    botUsername: 'Waechtersino',
    trigger: 2,
    detail: '[GrieferGames] Du wurdest vom Server gekickt.',
    lastRunningTaskKey: 'Home:GoToHome',
    occurredAtUtc: '2026-08-14T09:31:03.000Z',
    acknowledgedAtUtc: '2026-08-14T10:15:00.000Z' as string | null,
    acknowledgedByUsername: 'preview' as string | null,
  },
];

const FAKE_SELF_SHUTDOWN_REPORT = {
  incident: FAKE_SELF_SHUTDOWN_INCIDENTS[0],
  autoResumeCleared: true,
  audit: [
    {
      createdAt: '2026-08-23T13:55:45.000Z',
      actionType: 'StoppedBySelfShutdown',
      actorUsername: null,
      description: 'Turned itself off: jailed by the server',
    },
    {
      createdAt: '2026-08-23T13:53:20.000Z',
      actionType: 'Started',
      actorUsername: 'Aktenkoffer_',
      description: 'Bot started',
    },
  ],
  chat: [
    {
      createdAt: '2026-08-23T13:55:45.000Z',
      message: '§8[§6GrieferGames§8] §cDu wurdest von giftlippenstift für Unbetreuter Shop-Bot ins Gefängnis gesperrt.',
    },
    {
      createdAt: '2026-08-23T13:53:26.000Z',
      message: '§6Supreme §8┃ §fWaechtersino §7Wächtersino Coming soon...',
    },
  ],
};

const FAKE_AH_WATCHES = [
  {
    id: 'a3000000-0000-0000-0000-000000000001', name: 'Orb-Spitzhacken', isEnabled: true,
    itemId: 278, metadata: 0, snbt: '{display:{Name:"§bOrb-Spitzhacke"}}',
    maxPrice: 9000000, binOnly: false,
    notifyOnNewMatch: true, notifyBeforeExpiry: true, notifyOnOutbid: true, notifyOnPriceBelowCeiling: true,
    leadMinutes: [60, 10],
    rules: [
      { id: 'a4000000-0000-0000-0000-000000000001', ruleType: 'Enchantment', operator: 'Ge', numericValue: 5, stringValue: '32', stringValue2: null, stringValue3: null, stripColorCodes: false, isEnabled: true },
    ],
    dimensions: [
      { kind: 'DurabilityRemaining', index: 0, path: null, value: '0', state: 'Original', ruleId: null },
      { kind: 'DisplayName', index: 0, path: null, value: null, state: 'Original', ruleId: null },
      { kind: 'Signature', index: 0, path: null, value: null, state: 'Original', ruleId: null },
      { kind: 'RepairCost', index: 0, path: null, value: '0', state: 'Original', ruleId: null },
      { kind: 'NbtTagCount', index: 0, path: null, value: '1', state: 'Original', ruleId: null },
      { kind: 'Enchantment', index: 0, path: '32', value: '5', state: 'Custom', ruleId: 'a4000000-0000-0000-0000-000000000001' },
    ],
    activeMatchCount: 1, nextExpiryUtc: new Date(AH_NOW + 96 * 60000).toISOString(), createdAt: '2026-08-20T10:00:00Z',
  },
  {
    id: 'a3000000-0000-0000-0000-000000000002', name: 'Billige Köpfe', isEnabled: false,
    itemId: 397, metadata: 3, snbt: null, maxPrice: 50000, binOnly: true,
    notifyOnNewMatch: true, notifyBeforeExpiry: false, notifyOnOutbid: false, notifyOnPriceBelowCeiling: false,
    leadMinutes: [], rules: [], dimensions: [],
    activeMatchCount: 0, nextExpiryUtc: null, createdAt: '2026-08-18T14:20:00Z',
  },
];

function routeAh(path: string, method: string, body: unknown, query: URLSearchParams): Response | Promise<Response> | null {
  const lower = path.toLowerCase();

  if (lower === '/api/ah/stats' && method === 'GET') {
    return apiOk({ activeCount: 2, expiringSoonCount: 1, totalCount: 3, lastScanAt: new Date(AH_NOW - 60000).toISOString() });
  }

  if (lower === '/api/ah/listings' && method === 'GET') {
    const status = query.get('status') ?? '';
    const search = (query.get('search') ?? '').toLowerCase();
    const sortBy = query.get('sortBy') ?? '';
    const items = FAKE_AH_LISTINGS
      .filter(l => status === '' || (status === 'expired' ? l.expiredAt !== null : l.expiredAt === null))
      .filter(l => search === '' || l.itemName.toLowerCase().includes(search))
      .sort((a, b) => {
        if (sortBy === 'bid') return b.currentBid - a.currentBid;
        if (sortBy === 'firstSeen') return Date.parse(b.firstSeenAt) - Date.parse(a.firstSeenAt);
        if ((a.expiredAt === null) !== (b.expiredAt === null)) return a.expiredAt === null ? -1 : 1;
        if (a.expiredAt !== null && b.expiredAt !== null) return Date.parse(b.expiredAt) - Date.parse(a.expiredAt);
        return Date.parse(a.estimatedExpiryUtc) - Date.parse(b.estimatedExpiryUtc);
      });
    return slowOk({ items, total: items.length }, 300);
  }

  const snapshotMatch = lower.match(/^\/api\/ah\/listings\/([^/]+)\/snapshots$/);
  if (snapshotMatch && method === 'GET') return apiOk({ snapshots: FAKE_AH_SNAPSHOTS });

  const listingMatch = lower.match(/^\/api\/ah\/listings\/([^/]+)$/);
  if (listingMatch && method === 'GET') {
    const found = FAKE_AH_LISTINGS.find(l => l.id === listingMatch[1]);
    return found ? apiOk(found) : apiOk(FAKE_AH_LISTINGS[0]);
  }

  if (lower === '/api/ah/bid' && method === 'POST') return apiOk({ queued: true, message: null });
  if (lower === '/api/ah/bids' && method === 'GET') return apiOk({ items: [] });

  if (lower === '/api/ah/watches/rule-preview' && method === 'POST') {
    return apiOk({ key: '278:0', itemId: 278, metadata: 0, count: 1, snbt: null, nbtHash: null, isCustom: false });
  }

  if (lower === '/api/ah/watches/rule-wizard-diff' && method === 'POST') {
    const items = (body as FakeWizardRequest).items ?? [];
    const first = items[0] ?? { key: '278:0', itemId: 278, metadata: 0, count: 1, snbt: null, nbtHash: null, isCustom: false };
    return apiOk({ itemId: first.itemId, metadata: first.metadata, agreedCount: 0, varying: [], preview: { ...first, count: 1 } });
  }

  if (lower === '/api/ah/watches' && method === 'GET') return slowOk({ items: FAKE_AH_WATCHES }, 300);
  if (lower === '/api/ah/watches' && method === 'POST') return apiOk(FAKE_AH_WATCHES[0]);
  if (lower.includes('/rules') && lower.startsWith('/api/ah/watches/') && method !== 'GET') {
    return apiOk(FAKE_AH_WATCHES[0]);
  }

  if (lower.startsWith('/api/ah/watches/') && lower.endsWith('/matches') && method === 'GET') {
    return apiOk({ items: [FAKE_AH_LISTINGS[0]], total: 1 });
  }
  if (lower.startsWith('/api/ah/watches/') && method === 'PUT') return apiOk(FAKE_AH_WATCHES[0]);
  if (lower.startsWith('/api/ah/watches/') && method === 'DELETE') return apiOk({ deleted: true });
  if (lower.startsWith('/api/ah/watches/') && method === 'GET') {
    const wanted = lower.split('/')[4];
    return apiOk(FAKE_AH_WATCHES.find(w => w.id === wanted) ?? FAKE_AH_WATCHES[0]);
  }

  return null;
}

const FAKE_BOTSHOP_DOMAINS = [
  { id: 'd1', host: 'botshopgui.com', name: 'GrieferUtils', isEnabled: true, note: null, assignedShopCount: 2 },
  { id: 'd2', host: 'shop.example.com', name: 'Partner network', isEnabled: false, note: 'Paused until the handover is done', assignedShopCount: 0 },
];

const FAKE_BOTSHOP_SHOPS = [
  { sellbotConfigId: 's1', botRegistryId: '00000000-0000-0000-0000-000000000001', botUsername: 'AlphaBot', ownerUsername: 'demo', publishToGrieferUtils: true, ownerHasFeature: true, botshopDomainId: 'd1' },
  { sellbotConfigId: 's2', botRegistryId: '00000000-0000-0000-0000-000000000002', botUsername: 'BetaBot', ownerUsername: 'demo', publishToGrieferUtils: true, ownerHasFeature: true, botshopDomainId: 'd1' },
  { sellbotConfigId: 's3', botRegistryId: '00000000-0000-0000-0000-000000000003', botUsername: 'GammaBot', ownerUsername: 'trader', publishToGrieferUtils: false, ownerHasFeature: false, botshopDomainId: null },
];

let fakeCompressedPlacementBlockedItems = [
  'minecraft:carrot', 'minecraft:comparator', 'minecraft:melon_seeds', 'minecraft:nether_wart',
  'minecraft:potato', 'minecraft:pumpkin_seeds', 'minecraft:redstone_block', 'minecraft:redstone_torch',
  'minecraft:repeater', 'minecraft:string', 'minecraft:wheat_seeds',
];

function routeCompressedPlacement(path: string, method: string, body: unknown): Response | Promise<Response> | null {
  if (path.toLowerCase() !== '/api/admin/compressed-placement/blocked-items') return null;
  if (method === 'PUT') {
    const sent = body as { itemIdentifiers?: string[] };
    fakeCompressedPlacementBlockedItems = [...new Set(sent.itemIdentifiers ?? [])].sort();
  }
  return slowOk({ itemIdentifiers: fakeCompressedPlacementBlockedItems }, 300);
}

function routeBotshopDomains(path: string, method: string): Response | Promise<Response> | null {
  const lower = path.toLowerCase();

  if (lower === '/api/admin/botshop-domains' && method === 'GET') {
    return slowOk({ domains: FAKE_BOTSHOP_DOMAINS }, 300);
  }
  if (lower === '/api/admin/botshop-domains/shops' && method === 'GET') {
    return slowOk({ shops: FAKE_BOTSHOP_SHOPS }, 300);
  }
  if (lower === '/api/admin/botshop-domains' && method === 'POST') return apiOk(FAKE_BOTSHOP_DOMAINS[0]);
  if (lower.startsWith('/api/admin/botshop-domains/shops/') && method === 'POST') return apiOk(FAKE_BOTSHOP_SHOPS[0]);
  if (lower.startsWith('/api/admin/botshop-domains/') && method === 'PUT') return apiOk(FAKE_BOTSHOP_DOMAINS[0]);
  if (lower.startsWith('/api/admin/botshop-domains/') && method === 'DELETE') return apiOk({ deleted: true });

  return null;
}

const PERF_INSTALL_1 = 'f1a2b3c4-1111-4a2b-8c3d-000000000001';
const PERF_INSTALL_2 = 'f1a2b3c4-2222-4a2b-8c3d-000000000002';

const FAKE_PERF_REPORTS = [
  {
    id: 'preview-perf-001', installId: PERF_INSTALL_1, sessionId: 'preview-perf-session-001',
    playerUuid: '8f5c1d9a4b2e4f77a1c3d5e7f9012345', playerName: 'KofferBauer', kind: 'deep', deep: true,
    modVersion: '1.3.9', forgeVersion: '11.15.1.2318',
    windowStartUtc: new Date(Date.now() - 600000).toISOString(),
    windowEndUtc: new Date(Date.now() - 590000).toISOString(),
    frameCount: 612, fpsAverage: 41.3, frameMsP50: 21.4, frameMsP95: 68.2, frameMsP99: 187.5, frameMsMax: 312.4,
    framesOver100Ms: 7, stallCount: 2, topOffender: 'ghost.reset', topOffenderMsPerSecond: 180.5,
    gpuRenderer: 'NVIDIA GeForce RTX 4070', osName: 'Windows 11', cpuCores: 16, maxHeapMb: 4096,
    gcPauseMs: 118, modCount: 14, schematicVolume: 640000, payloadBytes: 8241,
  },
  {
    id: 'preview-perf-002', installId: PERF_INSTALL_2, sessionId: 'preview-perf-session-002',
    playerUuid: '2b7e4411f0a24d2c9f18c1a55b3e7788', playerName: 'RuckelRudi', kind: 'summary', deep: false,
    modVersion: '1.3.8', forgeVersion: '11.15.1.2318',
    windowStartUtc: new Date(Date.now() - 3600000).toISOString(),
    windowEndUtc: new Date(Date.now() - 3300000).toISOString(),
    frameCount: 14820, fpsAverage: 58.9, frameMsP50: 16.6, frameMsP95: 22.4, frameMsP99: 41.0, frameMsMax: 96.3,
    framesOver100Ms: 0, stallCount: 0, topOffender: 'someothermod at.other.Handler.onRenderTick',
    topOffenderMsPerSecond: 3.2, gpuRenderer: 'AMD Radeon RX 6600', osName: 'Windows 10', cpuCores: 8,
    maxHeapMb: 2048, gcPauseMs: 34, modCount: 9, schematicVolume: 0, payloadBytes: 3120,
  },
];

const FAKE_PERF_PAYLOAD = JSON.stringify({
  schemaVersion: 1,
  environment: { modVersion: '1.3.9', mcVersion: '1.8.9', osName: 'Windows 11', cpuCores: 16, maxHeapMb: 4096, glRenderer: 'NVIDIA GeForce RTX 4070' },
  frames: { count: 612, fpsAverage: 41.3, periodMsMax: 312.4 },
  phases: [
    { phase: 'ghost.reset', calls: 3, totalMs: 1805.2, maxMs: 280.4, msPerSecond: 180.5 },
    { phase: 'ghost.mismatchScan', calls: 612, totalMs: 402.8, maxMs: 14.2, msPerSecond: 40.2 },
    { phase: 'printer.collectScanTargets', calls: 200, totalMs: 96.1, maxMs: 3.8, msPerSecond: 9.6 },
  ],
  counters: [{ counter: 'ghost.resets', value: 3 }, { counter: 'ghost.mismatchCells', value: 1253376 }],
  listeners: [
    { mod: 'koffermatic', handler: 'at.vektor.schematicviewer.render.SchematicGhostRenderer.onRender', event: 'RenderWorldLastEvent', invocations: 612, totalMs: 2308.4, maxMs: 291.0, msPerSecond: 230.8 },
    { mod: 'someothermod', handler: 'at.other.Handler.onRenderTick', event: 'RenderTickEvent', invocations: 612, totalMs: 32.0, maxMs: 4.1, msPerSecond: 3.2 },
  ],
  sampling: {
    samples: 1000,
    byOwner: [{ name: 'koffermatic', samples: 604, percent: 60.4 }, { name: 'minecraft', samples: 301, percent: 30.1 }, { name: 'someothermod', samples: 95, percent: 9.5 }],
    byMethod: [{ name: 'at.vektor.schematicviewer.render.SchematicGhostRenderer.countNonAir', samples: 412, percent: 41.2 }],
  },
  stalls: [
    { atMs: Date.now() - 595000, elapsedMs: 291, phase: 'ghost.reset', listener: 'koffermatic ...onRender',
      stack: ['at.vektor.schematicviewer.render.SchematicGhostRenderer.countNonAir:874', 'at.vektor.schematicviewer.render.SchematicGhostRenderer.reset:851'] },
  ],
  worstFrames: [
    { periodMs: 312.4, renderMs: 305.1, worstListener: 'koffermatic ...onRender', topPhases: [{ phase: 'ghost.reset', ms: 280.4 }] },
  ],
  memory: { heapUsedMb: 2100, heapMaxMb: 4096, gcPauseMsTotal: 118, collectors: [] },
});

function routeKoffermaticPerformance(path: string, method: string): Response | Promise<Response> | null {
  const lower = path.toLowerCase();

  if (lower === '/api/koffermatic/admin/performance' && method === 'GET') {
    return slowOk({ reports: FAKE_PERF_REPORTS, totalCount: FAKE_PERF_REPORTS.length }, 300);
  }
  if (lower === '/api/koffermatic/admin/performance/directives' && method === 'GET') {
    return apiOk({ directives: [{ installId: PERF_INSTALL_1, deepProfileUntilUtc: new Date(Date.now() + 540000).toISOString(), setByUserId: UID, setByUsername: 'preview' }] });
  }
  if (lower.startsWith('/api/koffermatic/admin/performance/directives/')) {
    return method === 'DELETE'
      ? apiOk({ success: true })
      : apiOk({ installId: PERF_INSTALL_1, deepProfileUntilUtc: new Date(Date.now() + 900000).toISOString(), setByUserId: UID, setByUsername: 'preview' });
  }
  if (lower.startsWith('/api/koffermatic/admin/performance/') && method === 'GET') {
    return slowOk({ report: FAKE_PERF_REPORTS[0], payloadJson: FAKE_PERF_PAYLOAD }, 200);
  }
  if (lower.startsWith('/api/koffermatic/admin/performance/') && method === 'DELETE') {
    return apiOk({ success: true });
  }

  return null;
}

function routeSelfShutoff(path: string, method: string): Response | Promise<Response> | null {
  const lower = path.toLowerCase();

  if (lower === '/api/admin/blacklist/entries' && method === 'GET') {
    return slowOk({ entries: FAKE_BLACKLIST_ENTRIES }, 300);
  }
  if (lower === '/api/admin/blacklist/entries' && method === 'POST') return apiOk(FAKE_BLACKLIST_ENTRIES[0]);
  if (lower.startsWith('/api/admin/blacklist/entries/') && method === 'PUT') return apiOk(FAKE_BLACKLIST_ENTRIES[0]);
  if (lower.startsWith('/api/admin/blacklist/entries/') && method === 'DELETE') return apiOk({ deleted: true });

  if (lower.startsWith('/api/botengine/selfshutdown/bot/') && method === 'GET') {
    return slowOk({ incidents: FAKE_SELF_SHUTDOWN_INCIDENTS }, 300);
  }

  if (lower === '/api/botengine/selfshutdown/open' && method === 'GET') {
    return apiOk({ incidents: FAKE_SELF_SHUTDOWN_INCIDENTS.filter(i => i.acknowledgedAtUtc === null) });
  }
  if (lower.startsWith('/api/botengine/selfshutdown/') && lower.endsWith('/acknowledge') && method === 'POST') {
    // Mutates so the banner clearing and the acknowledged state are both previewable.
    for (const incident of FAKE_SELF_SHUTDOWN_INCIDENTS) {
      incident.acknowledgedAtUtc = new Date().toISOString();
      incident.acknowledgedByUsername = 'PreviewUser';
    }
    return apiOk({ acknowledged: true });
  }
  if (lower.startsWith('/api/botengine/selfshutdown/') && method === 'GET') {
    return slowOk(FAKE_SELF_SHUTDOWN_REPORT, 300);
  }

  return null;
}

const FAKE_SCHEMATICS = [
  {
    id: 'preview-schematic-001',
    name: 'Lighthouse',
    citybuild: 22,
    citybuildName: 'Nature',
    location: '/p h Vektor 1',
    buildCitybuild: 22,
    buildLocation: '/p h Vektor 1',
    anchorX: 128,
    anchorY: 64,
    anchorZ: -310,
    width: 9,
    height: 21,
    length: 9,
    dimension: 'overworld',
    createdAt: YESTERDAY,
  },
  {
    id: 'preview-schematic-002',
    name: 'Barn',
    citybuild: 1,
    citybuildName: 'CB2',
    location: '',
    buildCitybuild: 1,
    buildLocation: '',
    anchorX: -40,
    anchorY: 70,
    anchorZ: 512,
    width: 15,
    height: 8,
    length: 21,
    dimension: 'overworld',
    createdAt: NOW,
  },
];

const FAKE_SCHEMATIC_MATERIALS = [
  { itemKey: '5:0', itemId: 5, meta: 0, need: 640, have: 1216, infinite: false, sufficient: true },
  { itemKey: '98:0', itemId: 98, meta: 0, need: 412, have: 412, infinite: false, sufficient: true },
  { itemKey: '20:0', itemId: 20, meta: 0, need: 168, have: 64, infinite: false, sufficient: false },
  { itemKey: '89:0', itemId: 89, meta: 0, need: 24, have: -1, infinite: true, sufficient: true },
  { itemKey: '53:0', itemId: 53, meta: 0, need: 16, have: 0, infinite: false, sufficient: false },
];

function schematicPlan(schematic: (typeof FAKE_SCHEMATICS)[number], storageId: string | null) {
  const availabilityKnown = !!storageId;
  const stocked = storageId === STORAGE2;
  const materials = availabilityKnown
    ? FAKE_SCHEMATIC_MATERIALS.map(m => (stocked && !m.sufficient ? { ...m, have: m.need, sufficient: true } : m))
    : FAKE_SCHEMATIC_MATERIALS.map(m => ({ ...m, have: 0, infinite: false, sufficient: true }));
  return {
    schematic,
    blockCount: materials.reduce((sum, m) => sum + m.need, 0),
    layerCount: schematic.height,
    availabilityKnown,
    ready: availabilityKnown && materials.every(m => m.sufficient),
    materials,
  };
}

function routeSchematics(path: string, method: string, body: unknown, query: URLSearchParams): Response | Promise<Response> | null {
  const lower = path.toLowerCase();

  if (lower === '/api/schematics' && method === 'GET') return slowOk({ schematics: FAKE_SCHEMATICS }, 300);

  const planMatch = lower.match(/^\/api\/schematics\/([^/]+)\/plan$/);
  if (planMatch && method === 'GET') {
    const schematic = FAKE_SCHEMATICS.find(s => s.id === planMatch[1]);
    if (!schematic) return apiNotFound('Schematic not found');
    return slowOk(schematicPlan(schematic, query.get('storageId')), 300);
  }

  const siteMatch = lower.match(/^\/api\/schematics\/([^/]+)\/build-site$/);
  if (siteMatch && method === 'PUT') {
    const schematic = FAKE_SCHEMATICS.find(s => s.id === siteMatch[1]);
    if (!schematic) return apiNotFound('Schematic not found');
    const request = (body ?? {}) as { citybuild?: number; location?: string | null };
    schematic.buildCitybuild = request.citybuild ?? schematic.buildCitybuild;
    schematic.buildLocation = request.location || schematic.location;
    return apiOk(schematic);
  }

  if (lower.startsWith('/api/schematics/') && method === 'DELETE') {
    return apiOk({ id: path.substring('/api/schematics/'.length) });
  }

  return null;
}

function routeSchematicLicenses(path: string, method: string): Response | Promise<Response> | null {
  const lower = path.toLowerCase();

  if (lower === '/api/admin/schematic-licenses/entries' && method === 'GET') {
    return slowOk({ licenses: FAKE_SCHEMATIC_LICENSES }, 300);
  }
  if (lower === '/api/admin/schematic-licenses/entries' && method === 'POST') return apiOk(FAKE_SCHEMATIC_LICENSES[0]);
  if (lower.startsWith('/api/admin/schematic-licenses/entries/') && method === 'PUT') return apiOk(FAKE_SCHEMATIC_LICENSES[0]);
  if (lower.startsWith('/api/admin/schematic-licenses/entries/') && method === 'DELETE') return apiOk({ deleted: true });

  return null;
}

const FAKE_CACHED_MAPS = [
  { id: 'c1a0e1f2-0000-4000-8000-000000000001', registrableHost: 'griefergames.net', map3: 'map3_aa077dbc-8406-4262-bd74-cbf01b4444bd', displayName: 'Aktenkoffer', pixelHash: '9f2c', firstSeenAt: new Date(Date.now() - 86400000).toISOString(), lastSeenAt: new Date(Date.now() - 600000).toISOString() },
  { id: 'c1a0e1f2-0000-4000-8000-000000000002', registrableHost: 'griefergames.net', map3: 'msv3_a4f1263f-c35d-400e-a364-0124bc1504dc', displayName: null, pixelHash: '41be', firstSeenAt: new Date(Date.now() - 3600000).toISOString(), lastSeenAt: new Date(Date.now() - 1200000).toISOString() },
  { id: 'c1a0e1f2-0000-4000-8000-000000000003', registrableHost: 'griefergames.net', map3: 'map3_9b74e5d9-e9b0-499c-8f80-f8dae38e9505', displayName: 'Leuchtturm', pixelHash: '07d1', firstSeenAt: new Date(Date.now() - 7200000).toISOString(), lastSeenAt: new Date(Date.now() - 1800000).toISOString() },
];

function routeMapCache(path: string, method: string, query: URLSearchParams): Response | Promise<Response> | null {
  if (path.toLowerCase() !== '/api/mapcache' || method !== 'GET') return null;
  const search = (query.get('search') ?? '').toLowerCase();
  const maps = FAKE_CACHED_MAPS.filter(m => search === '' || m.map3.toLowerCase().includes(search) || (m.displayName ?? '').toLowerCase().includes(search));
  return slowOk({ maps, total: maps.length }, 300);
}

interface FakeLedgerEntry {
  id: string;
  kind: 'Payment' | 'BalanceChange';
  direction: 'Incoming' | 'Outgoing';
  status: string;
  source: string;
  playerName: string | null;
  amount: number;
  account: 'Cash' | 'Bank';
  balanceBefore: number | null;
  balanceAfter: number | null;
  explainedByEntryId: string | null;
  explainedByKind: string | null;
  comparedToStaleValue: boolean;
  referenceId: string | null;
  occurrenceUtc: string | null;
  declineReason: string | null;
  note: string | null;
  reason: string | null;
  messageText: string | null;
  messageStatus: string;
  resolvedBy: string | null;
  resolvedAt: string | null;
  createdAt: string;
  completedAt: string | null;
}

const ledgerId = (index: number) => `f5000000-0000-0000-0000-${String(index).padStart(12, '0')}`;
const scheduleId = (index: number) => `f6000000-0000-0000-0000-${String(index).padStart(12, '0')}`;

function fakeLedgerEntry(index: number, partial: Partial<FakeLedgerEntry>): FakeLedgerEntry {
  return {
    id: ledgerId(index), kind: 'Payment', direction: 'Incoming', status: 'Confirmed', source: 'Received',
    playerName: null, amount: 0, account: 'Cash', balanceBefore: null, balanceAfter: null, explainedByEntryId: null, explainedByKind: null,
    comparedToStaleValue: false, referenceId: null, occurrenceUtc: null, declineReason: null, note: null, reason: null,
    messageText: null, messageStatus: 'None', resolvedBy: null, resolvedAt: null, createdAt: minutesAgo(index * 37), completedAt: minutesAgo(index * 37),
    ...partial,
  };
}

const FAKE_LEDGER: FakeLedgerEntry[] = [
  fakeLedgerEntry(1, { playerName: 'Notch', amount: 12_500, balanceBefore: 4_210_500, balanceAfter: 4_223_000, referenceId: 'sale-1', createdAt: minutesAgo(4) }),
  fakeLedgerEntry(2, { direction: 'Outgoing', status: 'Unknown', source: 'Scheduled', playerName: 'Alex', amount: 50_000, referenceId: scheduleId(3), occurrenceUtc: minutesAgo(70), note: 'Miete Plot 12', createdAt: minutesAgo(12), completedAt: null }),
  fakeLedgerEntry(3, { direction: 'Outgoing', status: 'Paid', source: 'Scheduled', playerName: 'Steve', amount: 250_000, balanceBefore: 4_460_500, balanceAfter: 4_210_500, referenceId: scheduleId(1), note: 'Lohn Oktober', reason: 'Lohn', messageText: 'Danke für deine Arbeit!', messageStatus: 'Sent', createdAt: minutesAgo(35) }),
  fakeLedgerEntry(4, { direction: 'Outgoing', status: 'Paid', source: 'AnkaufPayout', playerName: 'Jeb_', amount: 8_000, balanceBefore: 4_468_500, balanceAfter: 4_460_500, referenceId: 'ankauf-1', createdAt: minutesAgo(80) }),
  fakeLedgerEntry(5, { kind: 'BalanceChange', direction: 'Incoming', status: 'Recorded', source: 'Balance', amount: 42_000, balanceBefore: 4_426_500, balanceAfter: 4_468_500, createdAt: minutesAgo(130) }),
  fakeLedgerEntry(6, { status: 'Unconfirmed', playerName: 'Grumm', amount: 1_000, createdAt: minutesAgo(190) }),
  fakeLedgerEntry(7, { direction: 'Outgoing', status: 'Declined', source: 'Refund', playerName: 'Dinnerbone', amount: 500, declineReason: 'Der Spieler ist nicht online.', referenceId: 'sale-2', createdAt: minutesAgo(260) }),
  fakeLedgerEntry(8, { kind: 'BalanceChange', direction: 'Incoming', status: 'Recorded', source: 'Balance', account: 'Bank', amount: 1_000_000, balanceBefore: 9_000_000, balanceAfter: 10_000_000, explainedByEntryId: ledgerId(13), explainedByKind: 'BalanceChange', createdAt: minutesAgo(400) }),
  fakeLedgerEntry(9, { direction: 'Outgoing', status: 'Paid', source: 'Redirect', playerName: 'Notch', amount: 2_500, balanceBefore: 5_426_500, balanceAfter: 5_424_000, createdAt: minutesAgo(520) }),
  fakeLedgerEntry(10, { playerName: 'Xisuma', amount: 75_000, note: 'Anzahlung Farm', reason: 'Farm Anzahlung', balanceBefore: 5_351_500, balanceAfter: 5_426_500, createdAt: minutesAgo(700) }),
  fakeLedgerEntry(11, { kind: 'BalanceChange', direction: 'Outgoing', status: 'Recorded', source: 'Balance', account: 'Bank', amount: 200_000, balanceBefore: 9_200_000, balanceAfter: 9_000_000, comparedToStaleValue: true, createdAt: minutesAgo(1500) }),
  fakeLedgerEntry(13, { kind: 'BalanceChange', direction: 'Outgoing', status: 'Recorded', source: 'Balance', amount: 1_000_000, balanceBefore: 6_426_500, balanceAfter: 5_426_500, explainedByEntryId: ledgerId(8), explainedByKind: 'BalanceChange', createdAt: minutesAgo(400) }),
  fakeLedgerEntry(12, { direction: 'Outgoing', status: 'NotPaid', source: 'Scheduled', playerName: 'Alex', amount: 50_000, referenceId: scheduleId(3), resolvedBy: 'PreviewUser', resolvedAt: minutesAgo(1700), createdAt: minutesAgo(1800) }),
];

const FAKE_LEDGER_EVENTS: Record<string, { kind: string; detail: string | null; createdAt: string }[]> = {
  [ledgerId(2)]: [
    { kind: 'written', detail: '/pay Alex 50000', createdAt: minutesAgo(12) },
    { kind: 'no_reply', detail: null, createdAt: minutesAgo(12) },
  ],
  [ledgerId(3)]: [
    { kind: 'written', detail: '/pay Steve 250000', createdAt: minutesAgo(35) },
    { kind: 'paid', detail: '§aDu hast Steve $250.000 gegeben.', createdAt: minutesAgo(35) },
    { kind: 'balance_linked', detail: null, createdAt: minutesAgo(35) },
    { kind: 'message_sent', detail: 'Sent', createdAt: minutesAgo(35) },
  ],
  [ledgerId(7)]: [
    { kind: 'written', detail: '/pay Dinnerbone 500', createdAt: minutesAgo(260) },
    { kind: 'declined', detail: 'Der Spieler ist nicht online.', createdAt: minutesAgo(260) },
  ],
};

interface FakeScheduledPayment {
  id: string; playerName: string; amount: number; note: string | null; message: string | null; reason: string | null; kind: 'Once' | 'Recurring';
  dueAtUtc: string | null; scheduleJson: string | null; startsAtUtc: string | null; endsAtUtc: string | null; maxPayments: number | null;
  nextDueUtc: string | null; paidCount: number; state: string; waitingReason: string | null; reviewEntryId: string | null; createdAt: string;
}

const FAKE_SCHEDULED: FakeScheduledPayment[] = [
  { id: scheduleId(1), playerName: 'Steve', amount: 250_000, note: 'Lohn', message: 'Danke für deine Arbeit!', reason: 'Lohn', kind: 'Recurring', dueAtUtc: null,
    scheduleJson: JSON.stringify({ kind: 'monthly', daysOfMonth: [1], minutesOfDay: [1080], jitterMinutes: 0 }), startsAtUtc: minutesAgo(60 * 24 * 40), endsAtUtc: null, maxPayments: 12,
    nextDueUtc: new Date(Date.now() + 3 * 86_400_000).toISOString(), paidCount: 3, state: 'Active', waitingReason: null, reviewEntryId: null, createdAt: minutesAgo(60 * 24 * 40) },
  { id: scheduleId(2), playerName: 'Bob', amount: 10_000, note: null, message: null, reason: null, kind: 'Recurring', dueAtUtc: null,
    scheduleJson: JSON.stringify({ kind: 'times', days: [1], minutesOfDay: [1080], jitterMinutes: 0 }), startsAtUtc: minutesAgo(60 * 24 * 20), endsAtUtc: null, maxPayments: null,
    nextDueUtc: minutesAgo(60 * 26), paidCount: 2, state: 'Active', waitingReason: 'PlayerOffline', reviewEntryId: null, createdAt: minutesAgo(60 * 24 * 20) },
  { id: scheduleId(3), playerName: 'Alex', amount: 50_000, note: 'Miete Plot 12', message: null, reason: 'Miete Plot 12', kind: 'Recurring', dueAtUtc: null,
    scheduleJson: JSON.stringify({ kind: 'times', days: [3], minutesOfDay: [1200], jitterMinutes: 0 }), startsAtUtc: minutesAgo(60 * 24 * 30), endsAtUtc: null, maxPayments: null,
    nextDueUtc: minutesAgo(70), paidCount: 4, state: 'Active', waitingReason: 'NeedsReview', reviewEntryId: ledgerId(2), createdAt: minutesAgo(60 * 24 * 30) },
  { id: scheduleId(4), playerName: 'Xisuma', amount: 125_000, note: 'Restzahlung Farm', message: 'Rest für die Farm ist da.', reason: 'Restzahlung Farm', kind: 'Once', dueAtUtc: new Date(Date.now() + 2 * 86_400_000).toISOString(),
    scheduleJson: null, startsAtUtc: null, endsAtUtc: null, maxPayments: null,
    nextDueUtc: new Date(Date.now() + 2 * 86_400_000).toISOString(), paidCount: 0, state: 'Active', waitingReason: null, reviewEntryId: null, createdAt: minutesAgo(60 * 5) },
  { id: scheduleId(5), playerName: 'Grumm', amount: 30_000, note: null, message: null, reason: null, kind: 'Recurring', dueAtUtc: null,
    scheduleJson: JSON.stringify({ kind: 'times', days: [5], minutesOfDay: [720], jitterMinutes: 0 }), startsAtUtc: minutesAgo(60 * 24 * 60), endsAtUtc: null, maxPayments: null,
    nextDueUtc: new Date(Date.now() + 5 * 86_400_000).toISOString(), paidCount: 6, state: 'Paused', waitingReason: null, reviewEntryId: null, createdAt: minutesAgo(60 * 24 * 60) },
];

function fakePreview(sent: Partial<FakeScheduledPayment>): { occurrences: string[]; error: string | null } {
  if (!sent?.playerName || !/^[A-Za-z0-9_]{3,16}$/.test(sent.playerName)) return { occurrences: [], error: "Enter the player's exact Minecraft name." };
  if (!sent.amount || sent.amount <= 0) return { occurrences: [], error: 'The amount has to be more than 0.' };
  if (sent.kind === 'Once') return sent.dueAtUtc ? { occurrences: [sent.dueAtUtc], error: null } : { occurrences: [], error: 'Choose when the payment is due.' };
  const upcoming = fakeUpcomingRuns(sent.scheduleJson, sent.maxPayments ? Math.min(5, sent.maxPayments) : 5);
  return { occurrences: upcoming.runs, error: upcoming.error };
}

const FAKE_KNOWN_PLAYERS: { name: string; currentlyOnline: boolean; lastSeen: string }[] = [
  { name: 'Alex', currentlyOnline: true, lastSeen: minutesAgo(0) },
  { name: 'Alex_99', currentlyOnline: false, lastSeen: minutesAgo(180) },
  { name: 'Alexander', currentlyOnline: false, lastSeen: minutesAgo(60 * 30) },
  { name: 'AlexGamingDE', currentlyOnline: true, lastSeen: minutesAgo(0) },
  { name: 'Steve', currentlyOnline: true, lastSeen: minutesAgo(0) },
  { name: 'SteveOnTour', currentlyOnline: false, lastSeen: minutesAgo(600) },
  { name: 'Xisuma', currentlyOnline: false, lastSeen: minutesAgo(45) },
];

function routePayments(path: string, method: string, body: unknown, query: URLSearchParams): Response | Promise<Response> | null {
  const match = path.match(/^\/api\/bots\/([^/]+)\/payments(\/.*)?$/);
  if (!match) return null;
  const tail = match[2] ?? '';

  if (tail === '/ledger' && method === 'GET') {
    const kind = query.get('kind');
    const direction = query.get('direction');
    const source = query.get('source');
    const status = query.get('status');
    const search = (query.get('search') ?? '').toLowerCase();
    const matching = FAKE_LEDGER
      .filter(entry => !(entry.kind === 'BalanceChange' && entry.explainedByKind === 'Payment'))
      .filter(entry => kind === 'payments' ? entry.kind === 'Payment' : kind === 'balance' ? entry.kind === 'BalanceChange' : true)
      .filter(entry => !direction || entry.direction.toLowerCase() === direction.toLowerCase())
      .filter(entry => !source || entry.source === source)
      .filter(entry => !status || entry.status === status)
      .filter(entry => !search || (entry.playerName ?? '').toLowerCase().includes(search));
    const page = Number(query.get('page') ?? 1);
    const pageSize = Number(query.get('pageSize') ?? 20);
    return slowOk({ total: matching.length, page, pageSize, entries: matching.slice((page - 1) * pageSize, page * pageSize) }, 300);
  }

  if (tail === '/summary' && method === 'GET') {
    const search = (query.get('search') ?? '').toLowerCase();
    const settled = FAKE_LEDGER.filter(entry => entry.kind === 'Payment' && (entry.status === 'Confirmed' || entry.status === 'Paid')
      && (!search || (entry.playerName ?? '').toLowerCase().includes(search)));
    const incoming = settled.filter(entry => entry.direction === 'Incoming');
    const outgoing = settled.filter(entry => entry.direction === 'Outgoing');
    const sum = (entries: FakeLedgerEntry[]) => entries.reduce((total, entry) => total + entry.amount, 0);
    return slowOk({
      incoming: sum(incoming), outgoing: sum(outgoing), net: sum(incoming) - sum(outgoing),
      incomingCount: incoming.length, outgoingCount: outgoing.length,
      cash: 4_223_000, bank: 10_000_000, dueSoonCount: 3, dueSoonAmount: 310_000,
      reviewCount: FAKE_LEDGER.filter(entry => entry.status === 'Unknown').length,
    }, 250);
  }

  const entryMatch = tail.match(/^\/ledger\/([^/]+)(\/note|\/resolve)?$/);
  if (entryMatch) {
    const entry = FAKE_LEDGER.find(candidate => candidate.id === entryMatch[1]);
    if (!entry) return apiError('resource', 'This payment does not exist.');
    if (entryMatch[2] === '/note' && method === 'PUT') entry.note = (body as { note?: string })?.note?.trim() || null;
    if (entryMatch[2] === '/resolve' && method === 'POST') {
      const paid = (body as { paid?: boolean })?.paid === true;
      entry.status = paid ? 'Paid' : 'NotPaid';
      entry.resolvedBy = 'PreviewUser';
      entry.resolvedAt = new Date().toISOString();
      (FAKE_LEDGER_EVENTS[entry.id] ??= []).push({ kind: paid ? 'marked_paid' : 'marked_not_paid', detail: 'PreviewUser', createdAt: entry.resolvedAt });
      FAKE_SCHEDULED.filter(payment => payment.reviewEntryId === entry.id).forEach(payment => { payment.reviewEntryId = null; payment.waitingReason = null; });
    }
    return slowOk({ entry, events: FAKE_LEDGER_EVENTS[entry.id] ?? [{ kind: entry.kind === 'Payment' && entry.direction === 'Incoming' ? 'received' : 'written', detail: null, createdAt: entry.createdAt }] }, 200);
  }

  if (tail === '/scheduled' && method === 'GET') return slowOk(FAKE_SCHEDULED.filter(payment => payment.state !== 'Cancelled'), 300);
  if (tail === '/scheduled/preview' && method === 'POST') return slowOk(fakePreview(body as Partial<FakeScheduledPayment>), 150);

  if (tail === '/scheduled' && method === 'POST') {
    const sent = body as Partial<FakeScheduledPayment>;
    const preview = fakePreview(sent);
    if (preview.error) return apiError('payment', preview.error);
    const created: FakeScheduledPayment = {
      id: scheduleId(FAKE_SCHEDULED.length + 1), playerName: sent.playerName!, amount: sent.amount!, note: sent.note ?? null, message: sent.message ?? null, reason: sent.reason ?? null,
      kind: sent.kind ?? 'Once', dueAtUtc: sent.dueAtUtc ?? null, scheduleJson: sent.scheduleJson ?? null, startsAtUtc: sent.startsAtUtc ?? null,
      endsAtUtc: sent.endsAtUtc ?? null, maxPayments: sent.maxPayments ?? null, nextDueUtc: preview.occurrences[0] ?? null, paidCount: 0,
      state: 'Active', waitingReason: null, reviewEntryId: null, createdAt: new Date().toISOString(),
    };
    FAKE_SCHEDULED.push(created);
    return slowOk(created, 300);
  }

  const scheduleMatch = tail.match(/^\/scheduled\/([^/]+)(\/pause|\/resume|\/skip|\/pay-now)?$/);
  if (scheduleMatch) {
    const payment = FAKE_SCHEDULED.find(candidate => candidate.id === scheduleMatch[1]);
    if (!payment) return apiError('payment', 'This scheduled payment no longer exists.');
    const action = scheduleMatch[2];
    if (method === 'DELETE') payment.state = 'Cancelled';
    else if (method === 'PUT') Object.assign(payment, body as Partial<FakeScheduledPayment>);
    else if (action === '/pause') payment.state = 'Paused';
    else if (action === '/resume') payment.state = 'Active';
    else if (action === '/pay-now') payment.nextDueUtc = new Date().toISOString();
    else if (action === '/skip') payment.nextDueUtc = fakeUpcomingRuns(payment.scheduleJson, 2).runs[1] ?? null;
    return slowOk(payment, 250);
  }

  return null;
}

function route(path: string, method: string, body: unknown, query: URLSearchParams = new URLSearchParams()): Response | Promise<Response> | null {
  if (path.toLowerCase().startsWith('/api/mapcache')) {
    const handled = routeMapCache(path, method, query);
    if (handled !== null) return handled;
  }
  if (path.toLowerCase().startsWith('/api/ah')) {
    const handled = routeAh(path, method, body, query);
    if (handled !== null) return handled;
  }
  if (path.toLowerCase().startsWith('/api/admin/blacklist')
      || path.toLowerCase().startsWith('/api/botengine/selfshutdown')) {
    const handled = routeSelfShutoff(path, method);
    if (handled !== null) return handled;
  }
  if (path.toLowerCase().startsWith('/api/admin/compressed-placement')) {
    const handled = routeCompressedPlacement(path, method, body);
    if (handled !== null) return handled;
  }
  if (path.toLowerCase().startsWith('/api/admin/botshop-domains')) {
    const handled = routeBotshopDomains(path, method);
    if (handled !== null) return handled;
  }
  if (path.toLowerCase().startsWith('/api/koffermatic/admin/performance')) {
    const handled = routeKoffermaticPerformance(path, method);
    if (handled !== null) return handled;
  }
  if (path.toLowerCase().startsWith('/api/schematics')) {
    const handled = routeSchematics(path, method, body, query);
    if (handled !== null) return handled;
  }
  if (path.toLowerCase().startsWith('/api/admin/schematic-licenses')) {
    const handled = routeSchematicLicenses(path, method);
    if (handled !== null) return handled;
  }
  if (path.startsWith('/api/botgroups')) {
    const handled = routeBotGroups(path, method, body);
    if (handled !== null) return handled;
  }
  if (path.startsWith('/api/playerlists')) {
    const handled = routePlayerLists(path, method, body, query);
    if (handled !== null) return handled;
  }
  if (path.startsWith('/api/Repair')) {
    const handled = routeRepair(path, method, body);
    if (handled !== null) return handled;
  }
  if (path.startsWith('/api/caseopening')) {
    const handled = routeCaseOpening(path, method);
    if (handled !== null) return handled;
  }
  if (path.startsWith('/api/admin/alerts')) {
    const handled = routeAlerts(path, method, body);
    if (handled !== null) return handled;
  }
  if (path.startsWith('/api/notifications')) {
    const handled = routeNotifications(path, method);
    if (handled !== null) return handled;
  }
  if (path.startsWith('/api/vektor/admin/task-capsules') || path === '/api/vektor/admin/packet-recordings/bots') {
    const handled = routeTaskCapsules(path, method, query);
    if (handled !== null) return handled;
  }
  if (path.startsWith('/api/webhooks')) {
    const handled = routeWebhooks(path, method, body);
    if (handled !== null) return handled;
  }
  if (path.startsWith('/api/bots/')) {
    const handled = routeAutomation(path, method, body) ?? routePayments(path, method, body, query);
    if (handled !== null) return handled;
  }
  if (path.toLowerCase().startsWith('/api/plotacl/')) {
    const handled = routePlotAcl(path, method, body);
    if (handled !== null) return handled;
  }
  if (path === '/api/onlineplayers/known' && method === 'GET') {
    const prefix = (query.get('q') ?? '').trim().toLowerCase();
    return apiOk(prefix.length < 3 ? [] : FAKE_KNOWN_PLAYERS.filter(player => player.name.toLowerCase().startsWith(prefix)));
  }
  if (path.toLowerCase() === '/api/koffermatic/info' && method === 'GET') {
    return apiOk({ available: true, version: '1.0.0', mcVersion: '1.8.9', loader: 'Forge' });
  }
  if (path.toLowerCase() === '/api/vektorutils/info' && method === 'GET') {
    return apiOk({ available: true, version: '1.5.6', mcVersion: '1.8.9', loader: 'Forge' });
  }
  if (path.toLowerCase() === '/api/farm/recordings' && method === 'GET') return apiOk({ recordings: FAKE_FARM_RECORDINGS });
  if (path === '/api/Farm' && method === 'GET') return apiOk({ configs: FAKE_FARM_CONFIGS });
  if (path === '/api/cropfarmer/fields' && method === 'GET') return apiOk({ fields: FAKE_CROP_FIELDS });
  if (path === '/api/villagertrading/halls' && method === 'GET') return apiOk({ halls: FAKE_VILLAGER_HALLS });
  if (path === `/api/villagertrading/halls/${VILLAGER_HALL1}/villagers` && method === 'GET') return apiOk({ villagers: FAKE_HALL_VILLAGERS });
  if (path.match(/^\/api\/villagertrading\/halls\/[^/]+\/villagers$/) && method === 'GET') return apiOk({ villagers: [] });
  if (path.startsWith('/api/villagertrading/') && method !== 'GET') return apiOk({ id: 'v0000000-0000-0000-0000-000000000009', botId: BOT1 });
  if (path === '/api/OrbsTrader/catalog' && method === 'GET') return apiOk({ products: FAKE_ORB_SHOP_CATALOG });
  if (path === '/api/OrbsTrader' && method === 'GET') return apiOk({ configs: FAKE_ORBS_TRADER_CONFIGS });
  if (path.match(/^\/api\/OrbsTrader\/[^/]+\/run$/) && method === 'POST') return apiOk({ botId: BOT1 });
  if (path.startsWith('/api/OrbsTrader') && method !== 'GET') return apiOk({ id: 'o0000000-0000-0000-0000-000000000003' });
  if (path === '/api/SpawnerUpgrade' && method === 'GET') return apiOk({ configs: FAKE_SPAWNER_UPGRADE_CONFIGS });
  if (path === '/api/SpawnerUpgrade/spawners' && method === 'GET') return apiOk({ spawners: FAKE_KNOWN_SPAWNERS });
  if (path.match(/^\/api\/SpawnerUpgrade\/[^/]+\/run$/) && method === 'POST') return apiOk({ botId: BOT1 });
  if (path.startsWith('/api/SpawnerUpgrade') && method !== 'GET') return apiOk({ id: 's0000000-0000-0000-0000-000000000002' });
  if (path === '/api/Pokeball' && method === 'GET') return apiOk({ configs: FAKE_POKEBALL_CONFIGS });
  if (path.match(/^\/api\/Pokeball\/[^/]+\/run$/) && method === 'POST') return apiOk({ botId: BOT1 });
  if (path.startsWith('/api/Pokeball') && method !== 'GET') return apiOk({ id: 'p0000000-0000-0000-0000-000000000003' });
  if (path.startsWith('/api/cropfarmer/') && method !== 'GET') return apiOk({ id: 'crop-new', botId: 'b0000000-0000-0000-0000-000000000001' });
  if (path === '/api/User' && method === 'GET') {
    // Mirrors the real masking: the mode clears the Root and Admin bits, which is what makes the
    // admin navigation disappear, so previewing it has to reduce the permissions here too.
    const on = readMemberView();
    const demoted = on
      ? { ...FAKE_USER, permissions: (FAKE_USER as any).permissions & ~(64 | 4) }
      : FAKE_USER;
    return rawOk({
      status: 200, value: demoted,
      featureFlags: FAKE_FEATURE_FLAGS_EFFECTIVE,
      viewingAsMember: on,
    });
  }
  if (path === '/api/access/view-mode') {
    writeMemberView(method === 'POST');
    return apiOk({});
  }
  if (path === '/api/User/tenant' && method === 'GET')        return apiOk(FAKE_TENANT_MEMBERSHIP);
  if (path === '/api/User/Users' && method === 'GET')         return apiOk([FAKE_USER, FAKE_USER2, FAKE_USER3]);
  if (path === '/api/User/Sessions/me' && method === 'GET')   return apiOk(FAKE_SESSIONS);
  if (path === '/api/User/OtherAccounts' && method === 'GET') return apiOk([]);
  if (path.match(/^\/api\/User\/AuditLog/) && method === 'GET') return apiOk(FAKE_AUDIT_LOG);
  if (path.match(/^\/api\/User\/ChatLogs\//)  && method === 'GET') return apiOk(FAKE_CHAT_MESSAGES);
  if (path.match(/^\/api\/User\/EnabledClasses\/[^/]+$/) && method === 'GET') return apiOk(['at.vektor.example.DemoAddonModule']);
  if (path.match(/^\/api\/User\/[^/]+$/) && method === 'GET') {
    const id = path.substring('/api/User/'.length);
    const found = [FAKE_USER, FAKE_USER2, FAKE_USER3].find(u => u.id === id);
    return found ? rawOk(found) : rawOk({ status: 400, errors: { id: ['User nicht gefunden'] } });
  }
  if (path.match(/^\/api\/User\//) && method !== 'GET')       return apiOk(null);
  if (path === '/api/JarDownload/AllClassNames' && method === 'GET')
    return apiOk({ classNames: ['at.vektor.example.DemoAddonModule', 'at.vektor.example.OtherAddonModule'] });

  if (path === '/api/feature-flags/me' && method === 'GET')   return apiOk({ flags: FAKE_FEATURE_FLAGS_EFFECTIVE });

  // Without this the preview user holds no license, so every GatedAndLicensed route renders its denial
  // page and the pages behind them cannot be looked at locally at all.
  if (path === '/api/grants/me' && method === 'GET')          return apiOk(FAKE_GRANT_SUMMARY());

  if (path === '/api/botlicenses/me' && method === 'GET')
    return apiOk({ seats: fakeBotLicenses.map(fakeBotLicenseDto), licensedTypesOnAccessibleBots: [...new Set(fakeBotLicenses.map(l => l.licenseType))] });
  {
    const m = path.match(/^\/api\/botlicenses\/bots\/([^/]+)(?:\/([^/]+))?$/);
    if (m && method === 'GET' && !m[2]) return apiOk(fakeBotLicenseSlots(m[1]));
    if (m && m[2] && method === 'POST') {
      const licenseType = decodeURIComponent(m[2]);
      if (!fakeBotLicenses.some(l => l.botRegistryId === m[1] && l.licenseType === licenseType))
        fakeBotLicenses.push({ id: `preview-seat-${fakeBotLicenses.length + 2}`, licenseType, botRegistryId: m[1], assignedAt: new Date().toISOString() });
      return apiOk(fakeBotLicenseSlots(m[1]));
    }
    if (m && m[2] && method === 'DELETE') {
      const licenseType = decodeURIComponent(m[2]);
      const index = fakeBotLicenses.findIndex(l => l.botRegistryId === m[1] && l.licenseType === licenseType);
      if (index >= 0) fakeBotLicenses.splice(index, 1);
      return apiOk(fakeBotLicenseSlots(m[1]));
    }
  }

  if (path === '/api/SeedCrack/worlds' && method === 'GET')   return apiOk({ worlds: FAKE_SEED_CRACK_WORLDS });
  if (path === '/api/SeedCrack/world' && method === 'GET') {
    const world = FAKE_SEED_CRACK_WORLDS.find(w => w.key === query.get('key'));
    return world ? apiOk(world) : apiOk(null);
  }
  if (path === '/api/SeedCrack/map/features' && method === 'GET') {
    return apiOk({
      centerX: 128, centerZ: -512, diameter: 6000, dimension: 0,
      predicted: [
        { kind: 'Village', chunkX: 8, chunkZ: -32, blockX: 136, blockZ: -504 },
        { kind: 'DesertTemple', chunkX: 20, chunkZ: -20, blockX: 328, blockZ: -312 },
        { kind: 'JungleTemple', chunkX: -10, chunkZ: -40, blockX: -152, blockZ: -632 },
        { kind: 'WitchHut', chunkX: 30, chunkZ: -28, blockX: 488, blockZ: -440 },
      ],
      observedDungeons: [{ x: 150, y: 32, z: -498 }, { x: 90, y: 28, z: -540 }],
      observedStructures: [{ kind: 'Village', chunkX: 9, chunkZ: -31, blockX: 152, blockZ: -488 }],
    });
  }

  if (path.match(/^\/api\/profileinfo\/[^/]+\/history$/) && method === 'GET') {
    const rangeStart = query.get('from') ? Date.parse(query.get('from')!) : Number.NEGATIVE_INFINITY;
    const rangeEnd = query.get('to') ? Date.parse(query.get('to')!) : Number.POSITIVE_INFINITY;
    const snapshotsInRange = FAKE_PROFILE_HISTORY().filter(s => {
      const recordedAt = Date.parse(s.createdAt);
      return recordedAt >= rangeStart && recordedAt <= rangeEnd;
    });
    return apiOk({ snapshots: snapshotsInRange });
  }
  if (path.match(/^\/api\/profileinfo\/[^/]+\/latest$/) && method === 'GET') { const h = FAKE_PROFILE_HISTORY(); return apiOk(h[h.length - 1]); }

  // Setup Mode needs a live Goggles client on the other end, which preview has no way to fake, so it
  // answers with the same refusal a signed-in operator without full control would get.
  if (path === '/api/setup-mode/arm' && method === 'POST') {
    return apiError('no_fullcontrol_session', 'Take full control of the bot in Vektor Goggles first, then start the setup again.');
  }
  if (path === '/api/setup-mode/cancel' && method === 'POST') return apiOk({ cancelled: false });
  if (path === '/api/setup-mode/pending' && method === 'GET') return apiOk({ armed: null, capture: null, endedReason: null });

  if (path.match(/^\/api\/sellbot\/[^/]+\/config$/) && method === 'GET') return apiOk(sellbotConfigResponse());
  if (path.match(/^\/api\/sellbot\/[^/]+\/config$/) && method === 'PUT') {
    const sent = body as { zoneMode?: string; zones?: (Record<string, unknown> & { id?: string | null; name: string })[] | null } | undefined;
    if (sent?.zoneMode) FAKE_SELLBOT_CONFIG.zoneMode = sent.zoneMode;
    if (sent?.zones) FAKE_SELLBOT_CONFIG.zones = sent.zones.map((zone, index) => {
      const saved = FAKE_SELLBOT_CONFIG.zones.find(existing => existing.id === zone.id);
      return {
        ...zone, id: zone.id ?? `preview-zone-${Date.now()}-${index}`, sortOrder: index,
        dropPathCount: saved?.dropPathCount ?? 0, dropPathRecordedAt: saved?.dropPathRecordedAt ?? null,
      };
    });
    return apiOk(sellbotConfigResponse());
  }
  const recordPathMatch = path.match(/^\/api\/sellbot\/[^/]+\/zones\/([^/]+)\/record-path$/);
  if (recordPathMatch && method === 'POST') {
    const zone = FAKE_SELLBOT_CONFIG.zones.find(existing => existing.id === recordPathMatch[1]);
    if (FAKE_SELLBOT_CONFIG.zoneMode !== 'Multi' || !zone) return apiError('zone', 'That zone does not exist, or the shop is not in multi-zone mode.');
    setTimeout(() => { zone.dropPathCount += 1; zone.dropPathRecordedAt = new Date().toISOString(); }, 5000);
    return apiOk({ botId: BOT1 });
  }
  if (path.match(/^\/api\/sellbot\/[^/]+\/overview$/) && method === 'GET') return apiOk(FAKE_SELLBOT_OVERVIEW);
  if (path.match(/^\/api\/sellbot\/[^/]+\/stats$/) && method === 'GET') return apiOk(FAKE_SELLBOT_STATS);
  if (path.match(/^\/api\/sellbot\/[^/]+\/customers$/) && method === 'GET') {
    const term = (query.get('query') ?? '').trim().toLowerCase();
    const customerNames = new Set([...FAKE_SELLBOT_TXNS.map(sale => sale.payerName), ...FAKE_SELLBOT_ANKAUF_TXNS.map(visit => visit.sellerName)]);
    const matchingNames = [...customerNames].filter(name => term && name.toLowerCase().includes(term));
    const startsWithTerm = (name: string) => name.toLowerCase().startsWith(term);
    return apiOk({ names: matchingNames.sort((left, right) => Number(startsWithTerm(right)) - Number(startsWithTerm(left)) || left.localeCompare(right)).slice(0, 20) });
  }

  if (path.match(/^\/api\/sellbot\/[^/]+\/grants$/) && method === 'GET') return apiOk(FAKE_SELLBOT_GRANTS);
  if (path.match(/^\/api\/sellbot\/[^/]+\/grants\/[^/]+\/redemptions$/) && method === 'GET') return apiOk(FAKE_SELLBOT_GRANT_REDEMPTIONS);
  if (path.match(/^\/api\/sellbot\/[^/]+\/grants$/) && method === 'POST') return apiOk(FAKE_SELLBOT_GRANTS[0]);
  if (path.match(/^\/api\/sellbot\/[^/]+\/grants\/[^/]+$/) && method === 'PUT') return apiOk(FAKE_SELLBOT_GRANTS[0]);
  if (path.match(/^\/api\/sellbot\/[^/]+\/grants\/[^/]+\/revoke$/) && method === 'POST') return apiOk({ ...FAKE_SELLBOT_GRANTS[0], revokedAt: new Date().toISOString(), state: 'Revoked' });
  if (path.match(/^\/api\/sellbot\/[^/]+\/grants\/[^/]+$/) && method === 'DELETE') return apiOk({});
  if (path.match(/^\/api\/sellbot\/[^/]+\/holograms$/) && method === 'GET') return apiOk(FAKE_HOLOGRAMS);
  if (path.match(/^\/api\/sellbot\/[^/]+\/holograms\/live$/) && method === 'GET') return apiOk({ lastListedAt: new Date(Date.now() - 1000 * 60 * 6).toISOString(), citybuild: "CB1", entries: FAKE_HOLOGRAM_LIVE });
  if (path.match(/^\/api\/sellbot\/[^/]+\/holograms\/live\/refresh$/) && method === 'POST') return apiOk({});
  if (path.match(/^\/api\/sellbot\/[^/]+\/holograms\/bindings$/) && method === 'GET') return apiOk({ bindings: FAKE_HOLOGRAM_BINDINGS });
  if (path.match(/^\/api\/sellbot\/[^/]+\/holograms\/exclusions$/) && method === 'GET') return apiOk(FAKE_HOLOGRAM_EXCLUSIONS);
  if (path.match(/^\/api\/sellbot\/[^/]+\/holograms\/exclusions\/players$/) && method === 'POST') {
    const playerName = String((body as any)?.playerName ?? '').trim();
    if (playerName && !FAKE_HOLOGRAM_EXCLUSIONS.players.some(p => p.playerName === playerName))
      FAKE_HOLOGRAM_EXCLUSIONS.players.push({ id: `preview-exclusion-${Date.now()}`, playerName, playerUuid: null, createdAt: new Date().toISOString() });
    return apiOk(FAKE_HOLOGRAM_EXCLUSIONS);
  }
  const removedPlayer = path.match(/^\/api\/sellbot\/[^/]+\/holograms\/exclusions\/players\/([^/]+)$/);
  if (removedPlayer && method === 'DELETE') {
    FAKE_HOLOGRAM_EXCLUSIONS.players = FAKE_HOLOGRAM_EXCLUSIONS.players.filter(p => p.id !== removedPlayer[1]);
    return apiOk(FAKE_HOLOGRAM_EXCLUSIONS);
  }
  const excludedList = path.match(/^\/api\/sellbot\/[^/]+\/holograms\/exclusions\/lists\/([^/]+)$/);
  if (excludedList && method === 'POST') {
    const list = FAKE_PLAYER_LISTS.find(l => l.id === excludedList[1]);
    if (list && !FAKE_HOLOGRAM_EXCLUSIONS.lists.some(l => l.id === list.id))
      FAKE_HOLOGRAM_EXCLUSIONS.lists.push({ id: list.id, name: list.name, memberCount: list.memberCount });
    return apiOk(FAKE_HOLOGRAM_EXCLUSIONS);
  }
  if (excludedList && method === 'DELETE') {
    FAKE_HOLOGRAM_EXCLUSIONS.lists = FAKE_HOLOGRAM_EXCLUSIONS.lists.filter(l => l.id !== excludedList[1]);
    return apiOk(FAKE_HOLOGRAM_EXCLUSIONS);
  }
  if (path.match(/^\/api\/sellbot\/[^/]+\/holograms\/render$/) && method === 'POST') {
    const draft = body as any;
    return apiOk({ lines: renderFakeHologram(draft), budget: HOLOGRAM_BUDGET(Number(draft?.pholoName) || 1) });
  }
  if (path.match(/^\/api\/sellbot\/[^/]+\/holograms$/) && method === 'POST') return apiOk(FAKE_HOLOGRAMS[0]);
  if (path.match(/^\/api\/sellbot\/[^/]+\/holograms\/[^/]+$/) && method === 'PUT') return apiOk(FAKE_HOLOGRAMS[0]);
  if (path.match(/^\/api\/sellbot\/[^/]+\/holograms\/[^/]+$/) && method === 'DELETE') return apiOk({});
  if (path.match(/^\/api\/sellbot\/[^/]+\/selftest\/steps$/) && method === 'GET') return apiOk({ steps: [
    { key: 'config_consistency', configured: true },
    { key: 'home', configured: true },
    { key: 'special_chest_cleanup', configured: true },
    { key: 'ankauf_chest', configured: true },
    { key: 'verification_chest', configured: false },
    { key: 'trash_chest', configured: false },
    { key: 'lager_chest', configured: false },
    { key: 'ankauf_lamp', configured: true },
    { key: 'quote_lamp', configured: false },
    { key: 'chamber_scan', configured: true },
    { key: 'price_sanity', configured: true },
    { key: 'limits_sanity', configured: true },
  ] });
  if (path.match(/^\/api\/sellbot\/[^/]+\/selftest\/run$/) && method === 'POST') return apiOk({ botId: BOT1 });
  if (path.match(/^\/api\/sellbot\/[^/]+\/transactions\/[^/]+\/journal$/) && method === 'GET') {
    const txnId = path.split('/')[5];
    if (txnId === 'atx1') return apiOk([
      { transactionId: txnId, kind: 'ankauf', step: 'take_intent', state: 'Intent', dataJson: JSON.stringify({ seller: 'Lordreider', x: 101, y: 65, z: -18 }), createdAt: new Date(Date.now() - 1000 * 60 * 2 - 2000).toISOString() },
      { transactionId: txnId, kind: 'ankauf', step: 'items_taken', state: 'Ok', dataJson: JSON.stringify({
        itemKey: '12:0', count: 165240, amount: 1211209.20,
        stacks: [{ itemId: 12, damage: 0, snbt: '{compressionLevel:"TWO",stackSize:81,currentAmount:81,warningDisplayed:0b,display:{Name:"§eSand II",Lore:["§7Anzahl: 81"]}}', count: 2040 }],
      }), createdAt: new Date(Date.now() - 1000 * 60 * 2).toISOString() },
      { transactionId: txnId, kind: 'ankauf', step: 'items_taken', state: 'Ok', dataJson: JSON.stringify({
        itemKey: '12:0', count: 1602, amount: 11742.66,
        stacks: [{ itemId: 12, damage: 0, snbt: null, count: 1602 }],
      }), createdAt: new Date(Date.now() - 1000 * 60 * 2).toISOString() },
      { transactionId: txnId, kind: 'ankauf', step: 'returned', state: 'Ok', dataJson: JSON.stringify({
        reason: 'excess', dropped: 3, needed: 3,
        items: [{ item: '12:0', count: 3, snbt: '{compressionLevel:"TWO",stackSize:81,currentAmount:81,warningDisplayed:0b,display:{Name:"§eSand II",Lore:["§7Anzahl: 81"]}}' }],
      }), createdAt: new Date(Date.now() - 1000 * 60 * 2 + 500).toISOString() },
      { transactionId: txnId, kind: 'ankauf', step: 'payment_issued', state: 'Intent', dataJson: JSON.stringify({ amount: 1211209.20 + 11742.66, seller: 'Lordreider' }), createdAt: new Date(Date.now() - 1000 * 60 * 2 + 1000).toISOString() },
      { transactionId: txnId, kind: 'ankauf', step: 'paid', state: 'Ok', dataJson: null, createdAt: new Date(Date.now() - 1000 * 60 * 2 + 2000).toISOString() },
      { transactionId: txnId, kind: 'ankauf', step: 'deposited_lager', state: 'Ok', dataJson: JSON.stringify({ itemKey: '12:0', count: 1602 }), createdAt: new Date(Date.now() - 1000 * 60 * 2 + 3000).toISOString() },
      { transactionId: txnId, kind: 'ankauf', step: 'deposit_started', state: 'Intent', dataJson: null, createdAt: new Date(Date.now() - 1000 * 60 * 2 + 4000).toISOString() },
      { transactionId: txnId, kind: 'ankauf', step: 'subtask_failed', state: 'Failed', dataJson: JSON.stringify({
        task: 'Storage/ChestDeposit', error: 'Chest at 118 64 -308 is full; 640 item(s) stayed in the inventory.',
      }), createdAt: new Date(Date.now() - 1000 * 60 * 2 + 6000).toISOString() },
      { transactionId: txnId, kind: 'ankauf', step: 'deposited', state: 'Ok', dataJson: null, createdAt: new Date(Date.now() - 1000 * 60 * 2 + 8000).toISOString() },
      { transactionId: txnId, kind: 'ankauf', step: 'done', state: 'Ok', dataJson: null, createdAt: new Date(Date.now() - 1000 * 60 * 2 + 8000).toISOString() },
    ]);
    return apiOk([]);
  }
  if (path.match(/^\/api\/sellbot\/[^/]+\/transactions\/[^/]+$/) && method === 'GET') {
    const txnId = path.split('/')[5];
    const sale = FAKE_SELLBOT_TXNS.find(t => t.id === txnId);
    const ankauf = FAKE_SELLBOT_ANKAUF_TXNS.find(t => t.id === txnId);
    if (!sale && !ankauf) return apiNotFound('Transaction not found.');
    return apiOk({ sale: sale ? { kind: 'sale', ...sale } : null, ankauf: ankauf ?? null });
  }
  if (path.match(/^\/api\/sellbot\/[^/]+\/transactions$/) && method === 'GET') {
    const kind = query.get('kind');
    const status = query.get('status');
    const zone = query.get('zone');
    const nameNeedle = (query.get('search') ?? '').toLowerCase();
    const from = query.get('from');
    const to = query.get('to');
    const page = Math.max(1, Number(query.get('page') ?? 1));
    const pageSize = Number(query.get('pageSize') ?? 30) || 30;
    const entries = [
      ...FAKE_SELLBOT_TXNS.map(sale => ({ name: sale.payerName, createdAt: sale.createdAt, status: sale.status, zoneId: sale.sellbotZoneId as string | null, isSale: true, entry: { sale: { kind: 'sale', ...sale }, ankauf: null } })),
      ...FAKE_SELLBOT_ANKAUF_TXNS.map(ankauf => ({ name: ankauf.sellerName, createdAt: ankauf.createdAt, status: ankauf.status, zoneId: ankauf.sellbotZoneId as string | null, isSale: false, entry: { sale: null, ankauf } })),
    ]
      .filter(row => !kind || (kind === 'sale') === row.isSale)
      .filter(row => !status || row.status === status)
      .filter(row => !zone || (zone === 'outside' ? !row.zoneId : row.zoneId === zone))
      .filter(row => !nameNeedle || row.name.toLowerCase().includes(nameNeedle))
      .filter(row => (!from || row.createdAt >= from) && (!to || row.createdAt <= to))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return apiOk({ total: entries.length, page, pageSize, entries: entries.slice((page - 1) * pageSize, page * pageSize).map(row => row.entry) });
  }
  if (path.match(/^\/api\/sellbot\/[^/]+\/activity/) && method === 'GET') return apiOk({ entries: FAKE_SELLBOT_ACTIVITY });
  if (path.match(/^\/api\/sellbot\/[^/]+\/enabled$/) && method === 'POST') { FAKE_SELLBOT_CONFIG.isEnabled = !FAKE_SELLBOT_CONFIG.isEnabled; FAKE_SELLBOT_OVERVIEW.isEnabled = FAKE_SELLBOT_CONFIG.isEnabled; return apiOk({}); }
  if (path.match(/^\/api\/sellbot\/[^/]+\/chamber-stock$/) && method === 'GET') return apiOk({ ...FAKE_ALL_ITEMS, stacks: FAKE_SELLBOT_STACKS });
  if (path.match(/^\/api\/sellbot\/[^/]+\/rescan$/) && method === 'POST') return apiOk({ botId: BOT1 });
  if (path.match(/^\/api\/sellbot\/[^/]+\/scan-stock$/) && method === 'POST') return apiOk({ botId: BOT1 });
  if (path.match(/^\/api\/sellbot\/[^/]+\/capture-home$/) && method === 'POST') {
    setTimeout(() => { FAKE_SELLBOT_CONFIG.homeX = 150; FAKE_SELLBOT_CONFIG.homeY = 66; FAKE_SELLBOT_CONFIG.homeZ = -30; }, 2000);
    return apiOk({ botId: BOT1 });
  }
  if (path.match(/^\/api\/sellbot\/[^/]+\/offers\/import$/) && method === 'POST') {
    const dryRun = String((body as { dryRun?: string })?.dryRun ?? 'false') === 'true';
    return apiOk({
      dryRun,
      applied: !dryRun,
      counts: [
        { entity: 'Offers', created: 1, updated: 4, deleted: 0 },
        { entity: 'Tiers', created: 2, updated: 6, deleted: 1 },
        { entity: 'AnkaufTiers', created: 0, updated: 2, deleted: 0 },
        { entity: 'AnkaufStages', created: 0, updated: 3, deleted: 0 },
        { entity: 'Limits', created: 0, updated: 1, deleted: 0 },
        { entity: 'Rules', created: 0, updated: 2, deleted: 0 },
      ],
      issues: [],
    });
  }
  if (path.match(/^\/api\/sellbot\/[^/]+\/offers$/) && method === 'POST') {
    const dto = body as { wizard: FakeWizardRequest; folderId?: string | null };
    const first = dto.wizard.items[0];
    const rules = fakeDeviationRules(first);
    const id = `preview-offer-${Date.now()}`;
    FAKE_SELLBOT_OFFERS.push({
      id, item: { ...first, count: 1 }, hasRules: rules.length > 0, availableStock: 0,
      tiers: [], rules, ankaufTier: undefined, ankaufLimits: [], verkaufLimits: [],
      folderId: dto.folderId ?? null,
    });
    return apiOk({ id });
  }
  if (path.match(/^\/api\/sellbot\/[^/]+\/offer-folders$/) && method === 'POST') {
    const dto = body as { name: string; parentFolderId?: string | null };
    const id = `preview-folder-${Date.now()}`;
    FAKE_SELLBOT_FOLDERS.push({ id, name: dto.name, parentFolderId: dto.parentFolderId ?? null, sortOrder: FAKE_SELLBOT_FOLDERS.length });
    return apiOk({ id });
  }
  if (path.match(/^\/api\/sellbot\/[^/]+\/offers\/[^/]+$/) && method === 'DELETE') {
    const offerId = path.split('/')[5];
    const index = FAKE_SELLBOT_OFFERS.findIndex(o => o.id === offerId);
    if (index >= 0) FAKE_SELLBOT_OFFERS.splice(index, 1);
    return apiOk({});
  }
  if (path.match(/^\/api\/sellbot\/[^/]+\/offer-folders\/[^/]+$/) && method === 'PUT') {
    const folder = FAKE_SELLBOT_FOLDERS.find(f => f.id === path.split('/')[5]);
    const dto = body as { name: string };
    if (folder) folder.name = dto.name;
    return apiOk({});
  }
  if (path.match(/^\/api\/sellbot\/[^/]+\/offer-folders\/[^/]+$/) && method === 'DELETE') {
    const folderId = path.split('/')[5];
    const hasChildren = FAKE_SELLBOT_OFFERS.some(o => o.folderId === folderId) || FAKE_SELLBOT_FOLDERS.some(f => f.parentFolderId === folderId);
    if (hasChildren) return apiError('error', 'Folder still has offers or subfolders.');
    const index = FAKE_SELLBOT_FOLDERS.findIndex(f => f.id === folderId);
    if (index >= 0) FAKE_SELLBOT_FOLDERS.splice(index, 1);
    return apiOk({});
  }
  if (path.match(/^\/api\/sellbot\/[^/]+\/offer-tree\/move$/) && method === 'PUT') {
    const dto = body as { itemType: 'Offer' | 'Folder'; itemId: string; targetFolderId: string | null; targetOrder: { itemType: 'Offer' | 'Folder'; itemId: string }[] };
    if (dto.itemType === 'Offer') {
      const offer = FAKE_SELLBOT_OFFERS.find(o => o.id === dto.itemId);
      if (offer) offer.folderId = dto.targetFolderId;
    } else {
      const folder = FAKE_SELLBOT_FOLDERS.find(f => f.id === dto.itemId);
      if (folder) folder.parentFolderId = dto.targetFolderId;
    }
    // Response order mirrors array order, so replay targetOrder by moving each referenced item to
    // the end of its array in sequence - same trick real drag-reorder previews rely on.
    for (const ref of dto.targetOrder) {
      if (ref.itemType === 'Offer') {
        const index = FAKE_SELLBOT_OFFERS.findIndex(o => o.id === ref.itemId);
        if (index >= 0) FAKE_SELLBOT_OFFERS.push(FAKE_SELLBOT_OFFERS.splice(index, 1)[0]);
      } else {
        const index = FAKE_SELLBOT_FOLDERS.findIndex(f => f.id === ref.itemId);
        if (index >= 0) FAKE_SELLBOT_FOLDERS.push(FAKE_SELLBOT_FOLDERS.splice(index, 1)[0]);
      }
    }
    return apiOk({});
  }
  if (path.match(/^\/api\/sellbot\/[^/]+\/offers\/[^/]+\/tiers$/) && method === 'POST') return apiOk({ id: `preview-tier-${Date.now()}` });
  if (path.match(/^\/api\/sellbot\/[^/]+\/offers\/[^/]+\/rules$/) && method === 'POST') {
    const offer = FAKE_SELLBOT_OFFERS.find(o => o.id === path.split('/')[5]);
    const rule = { ...(body as FakeSellbotRule), id: `preview-rule-${Date.now()}` };
    if (offer) { offer.rules.push(rule); offer.hasRules = offer.rules.some(r => r.isEnabled); }
    return apiOk({ id: rule.id });
  }
  // Ahead of the rule-by-id branch below, which would otherwise read "apply" as a rule id.
  if (path.match(/^\/api\/sellbot\/[^/]+\/offers\/[^/]+\/rules\/apply$/) && method === 'POST') {
    const offer = FAKE_SELLBOT_OFFERS.find(o => o.id === path.split('/')[5]);
    const first = (body as FakeWizardRequest).items[0];
    if (offer) {
      offer.item = { ...offer.item, ...first, count: 1 };
      offer.rules = fakeDeviationRules(first);
      offer.hasRules = offer.rules.length > 0;
    }
    return apiOk({});
  }
  if (path.match(/^\/api\/sellbot\/[^/]+\/offers\/[^/]+\/rules\/[^/]+$/) && method !== 'GET') {
    const offer = FAKE_SELLBOT_OFFERS.find(o => o.id === path.split('/')[5]);
    const index = offer?.rules.findIndex(r => r.id === path.split('/')[7]) ?? -1;
    if (!offer || index < 0) return apiOk({});

    const edited = body as FakeSellbotRule;
    if (method !== 'DELETE' && edited.ruleType === 'Lore' && edited.numericValue < 0)
      return apiError('NumericValue', 'A lore rule needs the tooltip line it applies to.');

    if (method === 'DELETE') offer.rules.splice(index, 1);
    else offer.rules[index] = { ...edited, id: offer.rules[index].id };
    offer.hasRules = offer.rules.some(r => r.isEnabled);
    return apiOk({});
  }
  // The real renderer lives in the backend. The preview here just echoes the demo offer for that
  // material, which is close enough to show the page working and never a second implementation of it.
  if (path.match(/^\/api\/sellbot\/[^/]+\/rules\/wizard-diff$/) && method === 'POST') {
    const items = (body as FakeWizardRequest).items;
    const agreed = fakeDeviationRules(items[0]);
    const nameRule = agreed.find(rule => rule.ruleType === 'DisplayName');
    const namesDiffer = items.length > 1
      && new Set(items.map(item => fakeDeviationRules(item).find(rule => rule.ruleType === 'DisplayName')?.stringValue ?? null)).size > 1;
    const varying = namesDiffer && nameRule
      ? [{
        kind: 'DisplayName', index: 0, path: null,
        sampleValues: items.map(item => fakeDeviationRules(item).find(rule => rule.ruleType === 'DisplayName')?.stringValue ?? null),
      }]
      : [];
    return apiOk({
      itemId: items[0].itemId, metadata: items[0].metadata,
      agreedCount: varying.length > 0 ? agreed.length - 1 : agreed.length,
      varying,
      preview: FAKE_SELLBOT_OFFERS.find(o => o.item.itemId === items[0].itemId)?.item ?? { ...items[0], count: 1 },
    });
  }
  if (path.match(/^\/api\/sellbot\/[^/]+\/rule-preview$/) && method === 'POST') {
    const asked = body as { itemId: number; metadata: number };
    const shown = FAKE_SELLBOT_OFFERS.find(o => o.item.itemId === asked.itemId)?.item;
    return apiOk(shown ?? {
      key: `${asked.itemId}:${asked.metadata}`, itemId: asked.itemId, metadata: asked.metadata,
      count: 1, snbt: null, nbtHash: null, isCustom: false,
    });
  }
  if (path.match(/^\/api\/sellbot\/[^/]+\/offers\/[^/]+\/note$/) && method === 'PUT') {
    const offer = FAKE_SELLBOT_OFFERS.find(o => o.id === path.split('/')[5]);
    if (offer) offer.note = (body as { note: string | null }).note || null;
    return apiOk({});
  }
  if (path.match(/^\/api\/sellbot\/[^/]+\/offers\/[^/]+\/unlisted$/) && method === 'PUT') {
    const offer = FAKE_SELLBOT_OFFERS.find(o => o.id === path.split('/')[5]);
    if (offer) offer.isUnlisted = (body as { isUnlisted: boolean }).isUnlisted;
    return apiOk({});
  }
  if (path.match(/^\/api\/sellbot\/[^/]+\/offers\/[^/]+$/) && method !== 'GET') return apiOk({});
  if (path.match(/^\/api\/sellbot\/[^/]+\/tiers\/[^/]+$/) && method === 'PUT') {
    const tierId = path.split('/')[5];
    const tier = FAKE_SELLBOT_OFFERS.flatMap(o => o.tiers).find(t => t.id === tierId);
    const patch = body as { isEnabled: boolean; isUnlisted: boolean; count: number; price: number };
    if (tier) { tier.isEnabled = patch.isEnabled; tier.isUnlisted = patch.isUnlisted; tier.count = patch.count; tier.price = patch.price; }
    return apiOk({});
  }
  if (path.match(/^\/api\/sellbot\/[^/]+\/tiers\/[^/]+$/) && method !== 'GET') return apiOk({});
  if (path.match(/^\/api\/sellbot\/[^/]+\/bundles\/[^/]+$/) && method === 'PUT') {
    const renamed = (body as { name?: string } | undefined)?.name;
    if (renamed) FAKE_SELLBOT_BUNDLES[0].name = renamed;
    return apiOk({});
  }
  if (path === '/api/admin/feature-flags/catalog' && method === 'GET') return apiOk(FAKE_FEATURE_FLAGS_CATALOG);
  if (path.startsWith('/api/admin/feature-flags/overrides') && method === 'GET') return apiOk(FAKE_FEATURE_FLAG_OVERRIDES);
  if (path.startsWith('/api/admin/feature-flags/overrides') && method !== 'GET') return apiOk(null);
  if (path === '/api/GrantedUsers/GetAllActiveBots')          return apiOk([...FAKE_GRANTED_BOTS, { id: BOT3, username: 'CityMule' }]);
  if (path === '/api/GrantedUsers/GetMyBots')                 return apiOk(FAKE_MY_GRANTS);
  if (path === '/api/GrantedUsers/GetGrantedAccounts')        return apiOk(FAKE_GRANTED_ACCOUNTS);
  if (path === '/api/GrantedUsers/GetCommandPrefabs')         return apiOk(FAKE_COMMANDS);
  if (path.match(/^\/api\/GrantedUsers\/GetGrantedCommandPrefabsForBot\//)) return apiOk(FAKE_COMMANDS);
  if (path.match(/^\/api\/GrantedUsers\/GetCommandPrefabsInGrant\//))      return apiOk([]);
  if (path.match(/^\/api\/GrantedUsers\/GetNameOfBot\//))     return apiOk(FAKE_GRANTED_BOTS[0].username);
  if (path.match(/^\/api\/GrantedUsers\//) && method !== 'GET') return apiOk(null);

  if (path === '/api/botregistry' && method === 'GET') {
    if (query.get('all') === 'true') return apiOk({ bots: [
      ...FAKE_BOTS,
      { id: '00000000-0000-0000-0000-000000000001', minecraftUuid: 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa', minecraftUsername: 'AlphaBot', ownerUserId: 'u1', isOwner: false, level: 2, levelName: 'Admin', state: 'Playing', votingEnabled: false },
      { id: '00000000-0000-0000-0000-000000000002', minecraftUuid: 'bbbbbbbb-bbbb-bbbb-bbbb-bbbbbbbbbbbb', minecraftUsername: 'BetaBot', ownerUserId: 'u1', isOwner: false, level: 2, levelName: 'Admin', state: 'Offline', votingEnabled: false },
    ] });
    return apiOk({ bots: FAKE_BOTS });
  }
  if (path.match(/^\/api\/botregistry\/[^/]+\/chatlog\/search$/) && method === 'GET') {
    const q = (query.get('q') ?? '').toLowerCase();
    const all = FAKE_CHATLOG_ENTRIES;
    const matches = all
      .map((e, i) => ({ e, i }))
      .filter(({ e }) => e.message.replace(/§./g, '').toLowerCase().includes(q))
      .map(({ i }) => ({ match: all[i], before: all.slice(Math.max(0, i - 2), i), after: all.slice(i + 1, i + 3) }));
    return apiOk({ matches, total: matches.length });
  }
  if (path.match(/^\/api\/botregistry\/[^/]+\/chatlog$/) && method === 'GET') {
    const limit = Math.min(Math.max(parseInt(query.get('take') ?? '200', 10) || 200, 1), 5000);
    const anchorId = query.get('anchorId');
    const fromUtc = query.get('fromUtc');
    const toUtc = query.get('toUtc');
    let pool = FAKE_CHATLOG_ENTRIES;
    if (!anchorId) {
      if (fromUtc) pool = pool.filter(e => e.createdAt >= fromUtc);
      if (toUtc) pool = pool.filter(e => e.createdAt <= toUtc);
    }
    const desc = [...pool].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    const total = desc.length;
    let offset = Math.max(parseInt(query.get('skip') ?? '0', 10) || 0, 0);
    if (anchorId) {
      const idx = desc.findIndex(e => e.id === anchorId);
      if (idx >= 0) offset = Math.floor(idx / limit) * limit;
    }
    const entries = desc.slice(offset, offset + limit).reverse();
    return apiOk({ entries, total, skip: offset, limit });
  }
  // The detail endpoint every bot management page loads first. Without it those pages saw a null
  // bot and rendered as though the caller held no permissions at all.
  {
    const m = path.match(/^\/api\/botregistry\/([^/]+)$/);
    if (m && method === 'GET') {
      const bot = FAKE_BOTS.find((b: any) => b.id === m[1]);
      if (bot) return apiOk(bot);
    }
  }
  if (path.match(/^\/api\/botregistry\/[^/]+\/proxy$/) && method === 'GET') {
    return apiOk({
      proxyId: null, proxyName: null, canReassignCurrent: true, assignable: [],
      externalProxyEnabled: false, externalProxyId: null, externalProxyName: null, assignableExternal: [], canUseDirect: true,
    });
  }
  if (path.match(/^\/api\/botregistry\//) && method !== 'GET') return apiOk(null);
  if (path === '/api/botregistry/link' && method === 'POST')  return apiOk({ state: 'preview-state', authorizationUrl: '#' });

  if (path === '/api/citybuild-status' && method === 'GET')   return apiOk({ statuses: FAKE_CITYBUILD_STATUSES });

  if (path === '/api/voting/sites' && method === 'GET')       return apiOk({ sites: FAKE_VOTE_SITES });
  if (path === '/api/voting/scheduler-status' && method === 'GET') return apiOk(FAKE_VOTE_SCHEDULER_STATUS);
  if (path === '/api/voting/admin/today' && method === 'GET') return apiOk({ votingDayStartUtc: new Date(new Date().setUTCHours(0, 0, 0, 0)).toISOString(), rows: FAKE_VOTE_TODAY_ROWS });
  if (path === '/api/voting/admin/log' && method === 'GET')   return apiOk({ entries: FAKE_VOTE_LOG });
  if (path === '/api/voting/admin/stats' && method === 'GET') return apiOk({
    rangeFromUtc: FAKE_VOTE_DAILY[0].date, rangeToUtc: NOW,
    range: fakeVoteRangeSummary(FAKE_VOTE_DAILY), today: FAKE_VOTE_TODAY_STAT,
    daily: FAKE_VOTE_DAILY, bySite: FAKE_VOTE_BY_SITE, byBot: FAKE_VOTE_BY_BOT, byAdHoc: FAKE_VOTE_BY_ADHOC,
  });
  if (path.match(/^\/api\/voting\//) && method !== 'GET')     return apiOk(null);

  if (path === '/api/botengine/stats/system-queue-history')   return apiOk(FAKE_SYSTEM_QUEUE_HISTORY);
  if (path.match(/^\/api\/botengine\/[^/]+\/status$/))        return apiOk(FAKE_BOT_STATUS);
  if (path.match(/^\/api\/botengine\/[^/]+\/auth\/status$/))  return apiOk(FAKE_SESSION_INFO);
  if (path.match(/^\/api\/botengine\/[^/]+\/playerstate$/))   return apiOk(FAKE_PLAYER_STATE);
  if (path.match(/^\/api\/botengine\/[^/]+\/inventory$/))     return apiOk(FAKE_INVENTORY_STATE);
  if (path.match(/^\/api\/botengine\/[^/]+\/msgs\/contacts$/) && method === 'GET') return apiOk({ contacts: FAKE_MSG_CONTACTS });
  if (path.match(/^\/api\/botengine\/[^/]+\/msgs\/conversation$/) && method === 'GET') return apiOk({ messages: FAKE_MSG_CONVERSATION });
  if (path.match(/^\/api\/botengine\/[^/]+\/msgs\/send$/) && method === 'POST') return apiOk({ delivered: true });
  if (path.match(/^\/api\/botengine\/[^/]+\/msgs\/notifications$/) && method === 'GET') return apiOk({ enabled: true });
  if (path.match(/^\/api\/botengine\/[^/]+\/msgs\/notifications$/) && method === 'PUT') return apiOk({ enabled: !!(body as { enabled?: boolean })?.enabled });
  if (path === '/api/msgs/muted-contacts' && method === 'GET') return apiOk({ contacts: fakeMutedContacts.map((name) => ({ name })) });
  if (path === '/api/msgs/muted-contacts' && method === 'POST') {
    const name = String((body as { name?: string })?.name ?? '').toLowerCase();
    if (name && !fakeMutedContacts.includes(name)) fakeMutedContacts.push(name);
    return apiOk({ contacts: fakeMutedContacts.map((n) => ({ name: n })) });
  }
  if (path.match(/^\/api\/msgs\/muted-contacts\/[^/]+$/) && method === 'DELETE') {
    const name = decodeURIComponent(path.split('/').pop() ?? '').toLowerCase();
    fakeMutedContacts = fakeMutedContacts.filter((n) => n !== name);
    return apiOk({ contacts: fakeMutedContacts.map((n) => ({ name: n })) });
  }
  if (path.match(/^\/api\/botengine\/[^/]+\/chatlog$/))       return apiOk({ messages: FAKE_CHAT_MESSAGES });
  if (path.match(/^\/api\/botengine\/[^/]+\/consolelog$/))    return apiOk({ messages: ['[FarmPlugin] Starting harvest cycle...', '[Nav] Walking to 104 64 218', '[Farm] Harvested 16 wheat'] });
  if (path.match(/^\/api\/botengine\/[^/]+\/tasks$/) && method === 'GET') return apiOk(FAKE_AVAILABLE_TASKS);
  const taskOptionsMatch = path.match(/^\/api\/botengine\/[^/]+\/tasks\/options\/([^/]+)$/);
  if (taskOptionsMatch && method === 'GET') return apiOk({ options: FAKE_TASK_PARAMETER_OPTIONS[taskOptionsMatch[1]] ?? [] });
  if (path.match(/^\/api\/botengine\/[^/]+\/tasks\/status$/)) return apiOk(FAKE_TASK_STATUS);
  if (path.match(/^\/api\/botengine\/[^/]+\/tasks\/history$/) && method === 'GET') {
    return query.has('beforeId')
      ? apiOk({ items: FAKE_TASK_HISTORY_OLDER, nextBeforeId: null, connectionMarkers: FAKE_CONNECTION_MARKERS_OLDER })
      : apiOk({ items: FAKE_TASK_HISTORY_NEWEST, nextBeforeId: FAKE_TASK_HISTORY_NEWEST[FAKE_TASK_HISTORY_NEWEST.length - 1].id, connectionMarkers: FAKE_CONNECTION_MARKERS_NEWEST });
  }
  if (path.match(/^\/api\/botengine\/[^/]+\/tasks\/run$/) && method === 'POST') return apiOk({ queued: true });
  if (path.match(/^\/api\/botengine\/[^/]+\/settings$/) && method === 'GET') return apiOk(FAKE_PLUGIN_SETTINGS);
  if (path.match(/^\/api\/botengine\/[^/]+\/settings\/[^/]+\/reset$/) && method === 'POST') {
    const plugin = FAKE_PLUGIN_SETTINGS.plugins.find(p => p.plugin === decodeURIComponent(path.split('/')[5])) as any;
    if (plugin) plugin.values = Object.fromEntries(plugin.definitions.map((d: any) => [d.name, d.default]));
    return apiOk({ saved: true, applied: true });
  }
  if (path.match(/^\/api\/botengine\/[^/]+\/plugins$/) && method === 'GET') return apiOk(FAKE_BOT_PLUGINS);
  if (path.match(/^\/api\/botengine\/[^/]+\/resume-config$/) && method === 'GET') return apiOk({
    behavior: 1,
    pinnedTaskKey: null,
    pinnedTaskArgs: null,
    lastRunningTaskKey: null,
    lastRunningTaskArgs: null,
    lastSnapshotAt: null,
  });
  if (path.match(/^\/api\/botengine\/[^/]+\/resume-config$/) && method === 'PUT') return apiOk(true);
  if (path.match(/^\/api\/botengine\/[^/]+\/restart$/) && method === 'POST') {
    const bot = FAKE_PROXY_BOTS.find(b => b.botId === path.split('/')[3]);
    if (bot) bot.state = 'Playing';
    return apiOk({ minecraftUsername: bot?.minecraftUsername ?? 'AlphaBot', server: 'griefergames.net:25565' });
  }
  if (path.match(/^\/api\/botengine\//))                      return apiOk(null);

  if (path === '/api/Inventory/storages' && method === 'GET') return apiOk({ storages: FAKE_STORAGES });
  if (path.match(/^\/api\/Inventory\/storages\/[^/]+\/bots$/) && method === 'GET') {
    return apiOk({
      bots: FAKE_BOTS.slice(0, 2).map((b: any, i: number) => ({
        id: `sba-${i}`,
        botRegistryId: b.id,
        assignedByUserId: UID,
        createdAt: YESTERDAY,
        minecraftUsername: b.minecraftUsername,
        minecraftUuid: b.minecraftUuid,
      })),
    });
  }
  if (path === '/api/Inventory/craft/plan' && method === 'POST') {
    // Preview affordance: asking for more than 100 shows the shortfall state, so both blocking
    // branches of the craft page are reachable without a backend.
    const requestedQuantity = Number((body as { quantity?: number } | null)?.quantity ?? 0);
    if (requestedQuantity > 100) {
      return apiOk({
        steps: [],
        shortfalls: [{ itemKey: '17:0', displayName: 'Oak Wood', needed: requestedQuantity, available: 12 }],
        totalConsumed: [],
        estimatedSeconds: 0,
        eligibleBotIds: FAKE_BOTS.slice(0, 2).map((b: any) => b.id),
        resolvedBotId: FAKE_BOTS[0].id,
        botError: null,
        planError: null,
      });
    }
    if ((body as { itemKey?: string } | null)?.itemKey === UNLIMITED_STORAGE_KEY) {
      const times = Math.max(1, requestedQuantity);
      const ingredients = UNLIMITED_STORAGE_INGREDIENTS.map(ingredient => ({
        itemKey: ingredient.itemKey, displayName: ingredient.displayName, quantity: ingredient.count * times,
      }));
      return apiOk({
        steps: [{
          itemKey: UNLIMITED_STORAGE_KEY, displayName: 'Unbegrenzter Speicher', times, resultCount: 1,
          producedQuantity: times, disposition: 'Final', estimatedSeconds: 18 + times * 2, ingredients,
        }],
        shortfalls: [],
        totalConsumed: ingredients,
        estimatedSeconds: 18 + times * 2,
        eligibleBotIds: FAKE_BOTS.slice(0, 2).map((b: any) => b.id),
        resolvedBotId: FAKE_BOTS[0].id,
        botError: null,
        planError: null,
      });
    }
    // A carpet storage of Lager blocks takes white wool, dye and finished carpet, but nothing there
    // holds coloured wool, so the bot carries it through inside the carpet round.
    if ((body as { itemKey?: string } | null)?.itemKey === '171:14') {
      const carpets = Math.max(3, requestedQuantity);
      const crafts = Math.ceil(carpets / 3);
      return apiOk({
        steps: [
          {
            itemKey: '35:14', displayName: 'Red Wool', times: crafts * 2, resultCount: 1,
            producedQuantity: crafts * 2, disposition: 'Buffered', estimatedSeconds: 0,
            ingredients: [
              { itemKey: '35:0', displayName: 'White Wool', quantity: crafts * 2 },
              { itemKey: '351:1', displayName: 'Rose Red', quantity: crafts * 2 },
            ],
          },
          {
            itemKey: '171:14', displayName: 'Red Carpet', times: crafts, resultCount: 3,
            producedQuantity: crafts * 3, disposition: 'Final', estimatedSeconds: 22 + crafts * 0.4,
            ingredients: [{ itemKey: '35:14', displayName: 'Red Wool', quantity: crafts * 2 }],
          },
        ],
        shortfalls: [],
        totalConsumed: [
          { itemKey: '35:0', displayName: 'White Wool', quantity: crafts * 2 },
          { itemKey: '351:1', displayName: 'Rose Red', quantity: crafts * 2 },
        ],
        estimatedSeconds: 22 + crafts * 0.4,
        eligibleBotIds: FAKE_BOTS.slice(0, 2).map((b: any) => b.id),
        resolvedBotId: FAKE_BOTS[0].id,
        botError: null,
        planError: null,
      });
    }
    // Two tables need 8 planks: 4 sawn from a log, 4 birch already in stock, which is what a recipe
    // cell without a metadata looks like once it draws across wood types.
    return apiOk({
      steps: [
        {
          itemKey: '5:0', displayName: 'Oak Wood Planks', times: 1, resultCount: 4,
          producedQuantity: 4, disposition: 'Parked', estimatedSeconds: 14,
          ingredients: [{ itemKey: '17:0', displayName: 'Oak Wood', quantity: 1 }],
        },
        {
          itemKey: '58:0', displayName: 'Crafting Table', times: 2, resultCount: 1,
          producedQuantity: 2, disposition: 'Final', estimatedSeconds: 16,
          ingredients: [
            { itemKey: '5:0', displayName: 'Oak Wood Planks', quantity: 4 },
            { itemKey: '5:2', displayName: 'Birch Wood Planks', quantity: 4 },
          ],
        },
      ],
      shortfalls: [],
      totalConsumed: [
        { itemKey: '5:2', displayName: 'Birch Wood Planks', quantity: 4 },
        { itemKey: '17:0', displayName: 'Oak Wood', quantity: 1 },
      ],
      estimatedSeconds: 30,
      eligibleBotIds: FAKE_BOTS.slice(0, 2).map((b: any) => b.id),
      resolvedBotId: FAKE_BOTS[0].id,
      botError: null,
      planError: null,
    });
  }
  if (path === '/api/Inventory/crafts' && method === 'POST') {
    return apiOk({ id: 'craft-preview-1', itemKey: '58:0', itemDisplayName: 'Crafting Table', requestedTimes: 2, status: 'Pending' });
  }
  if (path === '/api/Inventory/craft/orders/plan' && method === 'POST') {
    const requestedLines = ((body as { lines?: { itemKey: string; quantity: number }[] } | null)?.lines) ?? [];
    const planned = requestedLines.map((line, lineIndex) => FAKE_CRAFT_ORDER_PLAN_LINE(line, lineIndex));
    const consumed = new Map<string, { itemKey: string; displayName: string; quantity: number }>();
    for (const line of planned.filter(l => !l.planError && l.shortfalls.length === 0))
      for (const entry of line.totalConsumed) {
        const seen = consumed.get(entry.itemKey);
        consumed.set(entry.itemKey, seen ? { ...seen, quantity: seen.quantity + entry.quantity } : { ...entry });
      }
    return apiOk({
      lines: planned,
      totalConsumed: [...consumed.values()].sort((a, b) => b.quantity - a.quantity),
      estimatedSeconds: planned.filter(l => !l.planError && l.shortfalls.length === 0)
        .reduce((total, l) => total + l.estimatedSeconds, 0),
      eligibleBotIds: FAKE_BOTS.slice(0, 2).map((b: any) => b.id),
      botError: null,
    });
  }
  if (path === '/api/Inventory/craft/orders' && method === 'POST') {
    const order = body as { resultStorageId?: string; lines?: { itemKey: string; quantity: number; itemDisplayName?: string }[] } | null;
    const requestedLines = order?.lines ?? [];
    if (order?.resultStorageId) previewRecordOpenSupply(order.resultStorageId, requestedLines);
    return apiOk(FAKE_CRAFT_ORDER(requestedLines));
  }
  if (path === '/api/Inventory/craft/orders' && method === 'GET') {
    return apiOk({ orders: [FAKE_CRAFT_ORDER([{ itemKey: '58:0', quantity: 2, itemDisplayName: 'Crafting Table' }])] });
  }
  if (path.match(/^\/api\/Inventory\/craft\/orders\/[^/]+(\/lines\/[^/]+)?$/) && method === 'DELETE') return apiOk({});
  if (path === '/api/Inventory/moves/plan' && method === 'POST') {
    return apiOk({
      eligibleBotIds: FAKE_BOTS.slice(0, 2).map((b: any) => b.id),
      resolvedBotId: FAKE_BOTS[0].id,
      resolvedBotBusy: false,
      botError: null,
    });
  }
  if (path === '/api/Inventory/moves' && method === 'POST') {
    const move = body as { sourceStorageId: string; destinationStorageId: string; lines?: { itemKey: string; quantity: number; itemDisplayName?: string }[] } | null;
    const requestedLines = move?.lines ?? [];
    const duplicateIndex = requestedLines.findIndex((line, lineIndex) =>
      requestedLines.findIndex(other => other.itemKey === line.itemKey) !== lineIndex);
    if (duplicateIndex >= 0)
      return apiError(`Lines[${duplicateIndex}]`, `${requestedLines[duplicateIndex].itemDisplayName ?? requestedLines[duplicateIndex].itemKey} is listed twice.`);
    return apiOk({ moves: FAKE_MOVE_REQUESTS(move?.sourceStorageId ?? STORAGE1, move?.destinationStorageId ?? STORAGE1, requestedLines) });
  }
  if (path === '/api/Inventory/moves' && method === 'GET') return apiOk({ moves: [] });
  if (path.match(/^\/api\/Inventory\/moves\/[^/]+$/) && method === 'DELETE') return apiOk({});
  if (path === '/api/Inventory/storages/all/items')           return apiOk({ ...FAKE_ALL_ITEMS, stacks: FAKE_SELLBOT_STACKS });
  if (path.match(/^\/api\/Inventory\/storages\/[^/]+\/items$/) && method === 'GET') return apiOk({ ...FAKE_STORAGE_ITEMS, stacks: FAKE_SELLBOT_STACKS });
  {
    const stockMatch = path.match(/^\/api\/Inventory\/storages\/([^/]+)\/stock$/);
    if (stockMatch && method === 'GET') return apiOk({ rows: previewStockRows(stockMatch[1]) });
    if (stockMatch && method === 'PUT') return previewSaveStock(stockMatch[1], body as Parameters<typeof previewSaveStock>[1]);
    const plannedMatch = path.match(/^\/api\/Inventory\/storages\/([^/]+)\/stock\/planned-orders$/);
    if (plannedMatch && method === 'GET') return apiOk(previewPlannedOrders(plannedMatch[1]));
    const demandMatch = path.match(/^\/api\/Inventory\/storages\/([^/]+)\/stock\/external-demand$/);
    if (demandMatch && method === 'GET') return apiOk(previewExternalDemand(demandMatch[1]));
  }
  if (path.match(/^\/api\/Inventory\/storages\/[^/]+\/audit-log/)) return apiOk(FAKE_INVENTORY_AUDIT_LOG);
  {
    const m = path.match(/^\/api\/access\/resources\/([^/]+)\/assignments$/);
    if (m && method === 'GET') {
      const family = FAKE_BOTS.some((b: any) => b.id === m[1]) ? 'bot'
        : FAKE_PROXIES.some((p: any) => p.id === m[1]) ? 'proxy'
          : 'storage';
      return apiOk(FAKE_RESOURCE_ASSIGNMENTS(m[1], family));
    }
    if (m && method === 'POST') return apiOk({});
  }
  {
    const m = path.match(/^\/api\/access\/resources\/([^/]+)\/effective$/);
    if (m && method === 'GET') {
      const family = FAKE_BOTS.some((b: any) => b.id === m[1]) ? 'bot'
        : FAKE_PROXIES.some((p: any) => p.id === m[1]) ? 'proxy'
          : 'storage';
      const roleId = family === 'bot' ? '22222222-0000-4000-8000-000000000003'
        : '11111111-0000-4000-8000-000000000003';
      const roleName = family === 'bot' ? 'Bot owner' : 'Storage owner';
      const mine = FAKE_RESOURCE_ASSIGNMENTS(m[1], family).myActions as string[];
      return apiOk({
        resourceId: m[1],
        principalId: UID,
        actions: [
          ...mine.map((action: string) => ({
            action, assignmentId: 'ra-001', roleId, roleName, scopeType: 2, expiresAt: null,
          })),
          // A platform administrator who also owns the resource holds each action twice. Real and
          // worth previewing, since it is what makes the explanation look duplicated.
          ...mine.slice(0, 2).map((action: string) => ({
            action, assignmentId: 'ra-platform', roleId: '11111111-0000-4000-8000-0000000000ff',
            roleName: 'Platform administrator', scopeType: 0, expiresAt: null,
          })),
        ],
      });
    }
  }
  if (path.match(/^\/api\/access\/resources\/[^/]+\/audit$/) && method === 'GET') return apiOk({
    total: 3,
    entries: [
      { id: 'al-003', action: 3, principalType: 0, principalId: UID2, principalName: 'FormerMember', previousRoleName: 'Storage contributor', newRoleName: null, previousExpiresAt: null, newExpiresAt: null, actorUserId: UID, actorName: 'PreviewUser', createdAt: NOW },
      { id: 'al-002', action: 1, principalType: 0, principalId: UID3, principalName: 'TenantMember', previousRoleName: 'Storage contributor', newRoleName: 'Storage reader', previousExpiresAt: null, newExpiresAt: null, actorUserId: UID, actorName: 'PreviewUser', createdAt: new Date(Date.now() - 86400000).toISOString() },
      { id: 'al-001', action: 0, principalType: 0, principalId: UID, principalName: 'PreviewUser', previousRoleName: null, newRoleName: 'Storage owner', previousExpiresAt: null, newExpiresAt: null, actorUserId: UID, actorName: 'PreviewUser', createdAt: new Date(Date.now() - 172800000).toISOString() },
    ],
  });
  if (path.match(/^\/api\/access\/resources\/[^/]+\/assignments\/[^/]+$/) && method === 'DELETE') return apiOk({});
  if (path === '/api/access/roles' && method === 'GET') return apiOk({
    roles: [
      { id: '11111111-0000-4000-8000-000000000001', name: 'Storage reader', description: 'See the storage and its contents', isBuiltIn: true, actions: ['storage.read'] },
      { id: '11111111-0000-4000-8000-000000000002', name: 'Storage contributor', description: 'Work with the storage without managing access', isBuiltIn: true, actions: ['storage.read', 'storage.items.write', 'storage.chests.write'] },
      { id: '11111111-0000-4000-8000-000000000003', name: 'Storage owner', description: 'Full control, including access and deletion', isBuiltIn: true, actions: ['storage.read', 'storage.items.write', 'storage.access.grant', 'storage.delete'] },
      { id: '11111111-0000-4000-8000-000000000004', name: 'Storage access administrator', description: 'Manage who has access, without touching contents', isBuiltIn: true, actions: ['storage.read', 'storage.access.read', 'storage.access.grant'] },
      { id: '22222222-0000-4000-8000-000000000001', name: 'Bot viewer', description: 'See the bot, its logs and what it is doing', isBuiltIn: true, actions: ['bot.read', 'bot.tasks.read'] },
      { id: '22222222-0000-4000-8000-000000000002', name: 'Bot operator', description: 'Run the bot day to day, without its account, money or messages', isBuiltIn: true, actions: BOT_OPERATOR_ACTIONS },
      { id: '22222222-0000-4000-8000-000000000003', name: 'Bot owner', description: 'Full control of the bot', isBuiltIn: true, actions: BOT_OWNER_ACTIONS },
      { id: '22222222-0000-4000-8000-000000000004', name: 'Bot access administrator', description: 'Manage who has access to the bot, without operating it', isBuiltIn: true, actions: ['bot.read', 'bot.tasks.read', 'bot.access.read', 'bot.access.grant', 'bot.audit.read'] },
      { id: '22222222-0000-4000-8000-000000000005', name: 'Bot administrator', description: 'Full control of the bot, except deleting it', isBuiltIn: true, actions: BOT_OWNER_ACTIONS.filter(a => a !== 'bot.delete') },
    ]
  });
  const chestRouteMatch = path.match(/^\/api\/Inventory\/storages\/([^/]+)\/chests$/);
  if (chestRouteMatch && method === 'GET') {
    const selection = { chestIds: null as string[] | null, match: previewChestFilterFromQuery(query) };
    const matching = previewMatchingChests(chestRouteMatch[1], selection);
    const sorted = previewSortChests(matching, query.get('sort'), query.get('desc') === 'true');
    const page = parseInt(query.get('page') ?? '0');
    const pageSize = Math.min(Math.max(parseInt(query.get('pageSize') ?? '50'), 1), 200);
    return apiOk({
      total: sorted.length,
      page,
      pageSize,
      chests: sorted.slice(page * pageSize, page * pageSize + pageSize),
    });
  }
  if (chestRouteMatch && method === 'PUT') {
    const request = (body ?? {}) as any;
    const matching = previewMatchingChests(chestRouteMatch[1], request);
    for (const chest of matching) {
      if (request.type != null) {
        chest.type = request.type;
        chest.typeName = request.type === 2 ? 'InfiniteStorage' : request.type === 1 ? 'DoubleChest' : 'SingleChest';
      }
      if (request.filterMode != null) {
        chest.filterMode = request.filterMode;
        chest.filterModeName = request.filterMode === 2 ? 'Blacklist' : request.filterMode === 1 ? 'Whitelist' : 'Disabled';
      }
      if (request.filterItems != null) chest.filterItems = request.filterItems;
    }
    return apiOk({ updated: matching.length });
  }
  if (chestRouteMatch && method === 'DELETE') {
    const matching = previewMatchingChests(chestRouteMatch[1], (body ?? {}) as any);
    for (const chest of matching) {
      const at = FAKE_CHESTS.indexOf(chest);
      if (at >= 0) FAKE_CHESTS.splice(at, 1);
    }
    previewSyncChestCounts();
    return apiOk({ deleted: matching.length });
  }
  if (path.match(/^\/api\/Inventory\/storages\/[^/]+\/chests\/read$/) && method === 'POST') {
    return apiOk({ botId: BOT1 });
  }
  const slotsMatch = path.match(/^\/api\/Inventory\/storages\/[^/]+\/chests\/([^/]+)\/slots$/);
  if (slotsMatch) {
    const chest = FAKE_CHESTS.find(c => c.id === slotsMatch[1]) ?? FAKE_CHESTS[0];
    const visibleCount = chest.type === 1 ? 54 : 27;
    const slots = Array.from({ length: visibleCount }, (_, i) => ({
      slotIndex: i,
      itemId: [264, 265, 266, 388, 296][i % 5],
      metadata: 0,
      count: 1 + (i * 7) % 64,
    }));
    // ChestSlot.InfiniteBackingSlotIndex: an infinite chest also reports its hidden stock as slot -1.
    if (chest.type === 2) {
      slots.unshift({ slotIndex: -1, itemId: chest.filterItems[0]?.itemId ?? 264, metadata: 0, count: 184320 });
    }
    return apiOk({
      id: chest.id, x: chest.x, y: chest.y, z: chest.z,
      x2: chest.x2 ?? null, y2: chest.y2 ?? null, z2: chest.z2 ?? null,
      type: chest.type, typeName: chest.typeName, lastScannedAt: chest.lastScannedAt ?? NOW,
      slots,
    });
  }
  if (path === '/api/Inventory/pricing' && method === 'GET')  return apiOk(FAKE_PRICING);
  if (path === '/api/Inventory/custom-items')                 return apiOk({ items: [
    { hash: 'enchpick', productHash: 'enchpick', itemId: 278, metadata: 0, displayName: '§6Mega Pickaxe', snbt: '{ench:[{id:32,lvl:5}],display:{Name:"§6Mega Pickaxe"}}', enchantmentsJson: '[{"id":32,"lvl":5}]', lore: null, damage: null },
    { hash: 'godsword', productHash: 'godsword', itemId: 276, metadata: 0, displayName: '§cGottesschwert', snbt: '{ench:[{id:16,lvl:10}],display:{Name:"§cGottesschwert",Lore:["§7Legendär"]}}', enchantmentsJson: '[{"id":16,"lvl":10}]', lore: 'Legendär', damage: null },
    { hash: UNLIMITED_STORAGE_HASH, productHash: UNLIMITED_STORAGE_HASH, itemId: 146, metadata: 0, displayName: '§6Unbegrenzter Speicher', snbt: UNLIMITED_STORAGE_SNBT, enchantmentsJson: null, lore: 'Material: ?', damage: null },
    { hash: 'clanhome1', productHash: 'clanhome', itemId: 339, metadata: 0, displayName: '§6Clan-Home', snbt: '{display:{Name:"§6Clan-Home",Lore:["§7Einlösbar","f3Kq9ZpL2xTb7WcR"]}}', enchantmentsJson: null, lore: 'Einlösbar\nf3Kq9ZpL2xTb7WcR', damage: null },
    { hash: 'clanhome2', productHash: 'clanhome', itemId: 339, metadata: 0, displayName: '§6Clan-Home', snbt: '{display:{Name:"§6Clan-Home",Lore:["§7Einlösbar","Hd8sPq2LmZ4vNx6K"]}}', enchantmentsJson: null, lore: 'Einlösbar\nHd8sPq2LmZ4vNx6K', damage: null },
  ] });
  if (path === '/api/Inventory/tasks' && method === 'GET')    return apiOk([]);
  if (path === '/api/Inventory/tenant-settings')              return apiOk({ defaultProfitMargin: 15 });
  if (path === '/api/Inventory/resolve-batch' && method === 'POST') {
    return apiOk({
      '264:0': { status: 'InStorage', maxCraftable: 512, hasRecipe: false, allStatus: 'InStorage', allMaxCraftable: 512 },
      [UNLIMITED_STORAGE_KEY]: UNLIMITED_STORAGE_RESOLVE,
    });
  }
  if (path.match(/^\/api\/Inventory\/resolve\//) && decodeURIComponent(path).endsWith(`/${UNLIMITED_STORAGE_KEY}`))
    return apiOk(UNLIMITED_STORAGE_RESOLVE);
  if (path.match(/^\/api\/Inventory\/resolve\//))             return apiOk({ status: 'InStorage', maxCraftable: 64, hasRecipe: false, allStatus: 'InStorage', allMaxCraftable: 64 });
  if (path.match(/^\/api\/Inventory\/recipe-detail\/146\/0$/) && query.get('hash') === UNLIMITED_STORAGE_HASH) {
    return apiOk({
      resultId: 146, resultCount: 1, resultMetadata: 0, resultKey: UNLIMITED_STORAGE_KEY,
      ingredients: UNLIMITED_STORAGE_INGREDIENTS.map(ingredient => ({
        id: ingredient.id, metadata: 0, key: ingredient.itemKey, name: ingredient.displayName, itemName: ingredient.name,
        count: ingredient.count, stock: ingredient.stock, maxCraftable: 0, hasRecipe: false,
      })),
    });
  }
  if (path.match(/^\/api\/Inventory\/recipe-detail\//))       return apiOk(null);
  if (path.match(/^\/api\/Inventory\/storages\/[^/]+\/reservations$/)) return apiOk({ reservations: FAKE_RESERVATIONS });
  if (path.match(/^\/api\/Inventory\//) && method !== 'GET')  return apiOk(null);
  if (path.match(/^\/api\/Inventory\/storages/) && method === 'POST') return apiOk({ ...FAKE_STORAGES[0], id: 'new-storage-001', name: 'New Storage' });

  if (path === '/api/ItemEntry' && method === 'GET')          return apiOk(FAKE_ITEM_ENTRIES);
  if (path.match(/^\/api\/ItemEntry\/[^/]+$/) && method === 'GET') {
    const id = path.split('/').pop();
    return apiOk(FAKE_ITEM_ENTRIES.find(x => x.id === id) ?? FAKE_ITEM_ENTRIES[0]);
  }
  if (path.match(/^\/api\/ItemEntry/) && method !== 'GET')    return apiOk(null);

  if (path === '/api/MinecraftConfig/All')                    return apiOk(FAKE_DROP_CONFIGS);
  if (path.match(/^\/api\/MinecraftConfig\//))                return apiOk(null);

  if (path === '/api/JobEntry/All')                           return apiOk({ entries: FAKE_JOB_ENTRIES });
  if (path.match(/^\/api\/JobEntry(\/\d{4}-\d{2}-\d{2}|\?)/)) {
    const base = FAKE_JOB_ENTRIES[0];
    const history: any[] = [];
    for (let i = 0; i < 45; i++) {
      const d = new Date(Date.now() - i * 86400000);
      const jitter = (Math.sin(i * 0.7) + 1) * 0.5;
      const prices: Record<number, number> = {};
      Object.entries(base.pricesToStacks).forEach(([p, s]) => {
        const np = Math.max(1, Math.round(parseInt(p) + jitter * 8 - 4));
        prices[np] = Math.max(1, Math.round((s as number) * (0.8 + jitter * 0.4)));
      });
      history.push({ ...base, createdAt: d.toISOString(), pricesToStacks: prices });
    }
    return apiOk({ entries: history });
  }
  if (path.match(/^\/api\/JobEntry\//))                       return apiOk({ entries: [] });

  if (path === '/api/admin/proxies' && method === 'GET')      return apiOk({ proxies: FAKE_PROXIES });
  if (path === '/api/admin/proxies/external' && method === 'GET') return apiOk({ proxies: FAKE_EXTERNAL_PROXIES });
  if (path === '/api/admin/proxies/bots' && method === 'GET') return apiOk({ bots: FAKE_PROXY_BOTS });
  if (path === '/api/admin/proxies/pools' && method === 'GET') return apiOk({ pools: FAKE_PROXY_POOLS });
  if (path.match(/^\/api\/admin\/proxies\/pools\/[^/]+\/sync$/) && method === 'POST')
    return apiOk({ total: 2, inserted: 0, updated: 2, reactivated: 0, deactivated: 0 });
  if (path.match(/^\/api\/admin\/proxies\/pools\/[^/]+\/test$/) && method === 'POST') {
    const failing = path.includes(POOL_WEBSHARE_RESIDENTIAL);
    return apiOk(failing
      ? { ok: false, endpoint: 'http://p.webshare.io:80', username: 'preview-session-a', exitAddress: null, elapsedMs: 2140, error: 'The remote server returned 407 Proxy Authentication Required.' }
      : { ok: true, endpoint: 'socks5://brd.superproxy.io:22228', username: 'brd-customer-hl_00000000-zone-datacenter_zone-ip-203.0.113.7', exitAddress: '203.0.113.7', elapsedMs: 412, error: null });
  }
  if (path.match(/^\/api\/admin\/proxies\/[^/]+\/health/) && method === 'GET') {
    const isSlow = path.includes('ext-de-002');
    return apiOk({ buckets: fakeHealthBuckets(isSlow ? 320 : 20) });
  }
  if (path === '/api/admin/proxies/direct' && method === 'GET') return apiOk({ botsUsingCount: 2 });
  if (path.match(/^\/api\/admin\/proxies\/bots\/[^/]+\/reassign$/) && method === 'POST') {
    const botId = path.split('/')[5];
    const bot = FAKE_PROXY_BOTS.find(b => b.botId === botId);
    const occupied = new Set(FAKE_PROXY_BOTS.map(b => b.externalProxyId));
    const free = FAKE_EXTERNAL_PROXIES.find(e => e.poolId === bot?.poolId && !occupied.has(e.id));
    if (!bot || !free) return apiError('externalProxy', 'Every usable proxy in this pool already has a bot on it. Add entries to the pool to free one up.');
    bot.externalProxyId = free.id;
    bot.externalIdentity = free.host;
    return apiOk({ botId, externalProxyId: free.id, externalProxyName: free.name });
  }
  if (path === '/api/botregistry/admin/all' && method === 'GET') return apiOk([
    { id: '00000000-0000-0000-0000-000000000001', minecraftUuid: 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa', minecraftUsername: 'AlphaBot', ownerUserId: 'u1', ownerUsername: 'demo', proxyId: null, proxyName: null, state: 'Playing' },
    { id: '00000000-0000-0000-0000-000000000002', minecraftUuid: 'bbbbbbbb-bbbb-bbbb-bbbb-bbbbbbbbbbbb', minecraftUsername: 'BetaBot', ownerUserId: 'u1', ownerUsername: 'demo', proxyId: 'p1', proxyName: 'EU-1', state: 'Offline' },
  ]);
  if (path === '/api/admin/tenants' && method === 'GET')      return apiOk(FAKE_ADMIN_TENANTS);
  if (path.match(/^\/api\/admin\//) && method !== 'GET')      return apiOk(null);

  if (path === '/api/tenants' && method === 'GET')            return apiOk([FAKE_TENANT]);
  if (path.match(/^\/api\/tenants\/[^/]+\/members$/) && method === 'GET') return apiOk([FAKE_TENANT_MEMBERSHIP, FAKE_TENANT_MEMBERSHIP2]);
  if (path.match(/^\/api\/tenants\//) && method !== 'GET')    return apiOk(null);

  if (path === '/api/JarDownload/Versions')                   return rawOk(FAKE_VERSIONS);
  if (path === '/api/Changelog/Versions' && method === 'GET') return apiOk({ versions: FAKE_CHANGELOG });
  if (path.match(/^\/api\/Changelog\//) && method !== 'GET')  return apiOk(null);

  if (path === '/api/AppEventView')                           return apiOk(FAKE_APP_EVENTS);
  if (path === '/api/AppEventView/locations')                 return apiOk(['play.example-citybuild.de']);
  if (path.match(/^\/api\/AppEventView\/type\//))             return apiOk(FAKE_APP_EVENTS);

  if (path === '/api/BiomeObservation')                       return apiOk({ observations: [] });
  if (path === '/api/BiomeObservation/runs')                  return apiOk([]);
  if (path === '/api/VillageObservation')                     return apiOk({ observations: [] });
  if (path.match(/^\/api\/BiomeObservation\//) || path.match(/^\/api\/VillageObservation\//)) return apiOk(null);

  if (path === '/api/shopstock/bots' && method === 'GET')    return slowOk({ bots: FAKE_SHOP_STOCK_BOTS }, 300);
  if (path === '/api/shopstock/snapshot')                    return slowOk(FAKE_SHOP_STOCK_SNAPSHOT, 300);
  if (path === '/api/shopstock/watchlist' && method === 'GET') return slowOk(fakeShopStockWatchlist(query), 300);
  if (path === '/api/shopstock/watchlist' && method === 'POST') return apiOk(null);
  if (path.match(/^\/api\/shopstock\/watchlist\//) && method === 'DELETE') return apiOk({ deleted: true });
  if (path === '/api/shopstock/items')                       return apiOk({ items: FAKE_SHOP_STOCK_ITEMS });
  const shopStockBotMatch = path.match(/^\/api\/shopstock\/bots\/([^/]+)$/);
  if (shopStockBotMatch && method === 'GET') {
    const bot = FAKE_SHOP_STOCK_BOTS.find(b => b.id === shopStockBotMatch[1]);
    return bot ? apiOk({ ...bot, items: FAKE_SHOP_STOCK_STOCK_BY_BOT.get(bot.id) ?? [] }) : apiOk(null);
  }
  const shopStockCompareMatch = path.match(/^\/api\/shopstock\/compare\/([^/]+)$/);
  if (shopStockCompareMatch) return apiOk(fakeShopStockCompare(shopStockCompareMatch[1]));
  if (path.match(/^\/api\/shopstock\//))                     return apiOk(null);

  if (path === '/api/user/oauth/grants')                      return apiOk([]);
  if (path.match(/^\/api\/user\/oauth\//) && method !== 'GET') return apiOk(null);
  if (path === '/api/oauthclients' || path.match(/^\/api\/oauthclient/)) return apiOk([]);
  if (path === '/api/oauth/ms/callback')                     return apiError('code', 'AUTH_FAIL_NO_MC_PROFILE');

  if (path === '/api/Command/ExecuteCommand' && method === 'POST') return apiOk({ status: 200 });
  if (path.match(/^\/api\/GrantedUsers\/ActivateConnectionCode/)) return apiOk(null);

  if (path.match(/^\/api\/TranslateLog/))                     return apiOk([]);

  // Unrecognised API call — return empty success so pages don't crash
  return apiOk(null);
}

let _installed = false;

export function installFakeBackend(): void {
  if (_installed) return;
  _installed = true;

  const realFetch = window.fetch.bind(window);

  window.fetch = async function previewFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const urlStr =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.href
          : (input as Request).url;

    if (urlStr.startsWith('/api/')) {
      let body: unknown;
      if (init?.body && typeof init.body === 'string') {
        try { body = JSON.parse(init.body); } catch { /* leave undefined */ }
      } else if (init?.body instanceof FormData) {
        body = Object.fromEntries([...init.body.entries()].map(([field, value]) =>
          [field, value instanceof File ? value.name : value]));
      }
      const method = (init?.method ?? 'GET').toUpperCase();
      const [pathOnly, queryStr] = urlStr.split('?');
      const query = new URLSearchParams(queryStr ?? '');
      const res = route(pathOnly, method, body, query);
      if (res !== null) return await res;
    }

    return realFetch(input, init);
  };
}

export function isPreviewMode(): boolean {
  try {
    const fromParam = new URLSearchParams(window.location.search).get('preview');
    if (fromParam === 'true' || fromParam === '1') {
      sessionStorage.setItem('preview', 'true');
      return true;
    }
    return sessionStorage.getItem('preview') === 'true';
  } catch {
    return false;
  }
}

/**
 * Call once at app start (before render). Activates the mock if ?preview=true.
 * Seeds localStorage so the tenant auto-selector picks up the fake tenant.
 */
export function setupPreviewMode(): void {
  if (!isPreviewMode()) return;

  installFakeBackend();

  document.addEventListener('DOMContentLoaded', () => {
    const banner = document.createElement('div');
    banner.textContent = '👁 PREVIEW MODE - fake data';
    banner.style.cssText = [
      'position:fixed', 'bottom:12px', 'right:12px', 'z-index:99999',
      'background:#7c4dff', 'color:#fff', 'padding:6px 12px',
      'border-radius:6px', 'font-size:12px', 'font-weight:600',
      'pointer-events:none', 'opacity:0.85',
    ].join(';');
    document.body.appendChild(banner);
  });
}
